'use client'

import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Loader2,
  ImagePlus,
  CheckCircle2,
  Film,
  Clapperboard,
  ChevronLeft,
  ChevronRight,
  Video,
  ArrowLeft,
  PenSquare,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import {
  useTaskFlowStore,
  type TaskFlowPhase,
  type TaskFlowRequest,
} from '@/lib/task-flow/taskFlowStore'
import { HyperFramesScenePanel } from '@/components/creator-studio/hyperframes/HyperFramesScenePanel'

type DirectionState = {
  contentGoal: 'viral' | 'product' | 'storytelling' | 'ugc' | 'auto' | ''
  publishTarget: 'shorts' | 'feed' | 'youtube' | 'auto' | ''
  durationMode:
    | 'auto'
    | 'short'
    | 'medium'
    | 'long'
    | 'documentary'
    | 'custom'
    | ''
  durationTargetSec?: number
  productionMode?: 'express' | 'cinematic_longform'
  visualOutputMode?: 'animated_scenes' | 'static_images' | 'mixed'
  useCharacter: 'yes' | 'no' | 'auto' | ''
  characterType:
    | 'expert'
    | 'creator'
    | 'founder'
    | 'customer'
    | 'narrator'
    | 'avatar'
    | 'none'
    | 'auto'
    | ''
  hasProductImage: 'yes' | 'no' | ''
}

type SceneItem = {
  sceneId: number | null
  sceneOrder: number
  title: string
  prompt: string
  negativePrompt?: string
  overlayText?: string
  durationSec?: number | string | null
  sceneType?: string
  speaker?: string
  dialogueText?: string
  imageUrl?: string | null
  status?: 'pending' | 'generating' | 'done'
  metadata?: any
}

const GEMINI_TTS_VOICE_OPTIONS = [
  {
    id: 'auto',
    label: 'Auto',
    description: 'Samantha elige la voz ideal usando el personaje, estilo y escena.',
    gender: 'auto',
  },
  {
    id: 'Kore',
    label: 'Kore',
    description: 'Firme, segura, autoridad femenina.',
    gender: 'female',
  },
  {
    id: 'Aoede',
    label: 'Aoede',
    description: 'Cálida, melódica, humana y cercana.',
    gender: 'female',
  },
  {
    id: 'Zephyr',
    label: 'Zephyr',
    description: 'Clara, brillante, limpia y moderna.',
    gender: 'female',
  },
  {
    id: 'Charon',
    label: 'Charon',
    description: 'Calma, profesional, narrador serio.',
    gender: 'male',
  },
  {
    id: 'Puck',
    label: 'Puck',
    description: 'Energético, joven, dinámico y social.',
    gender: 'male',
  },
] as const

type AvatarQuality = 'preview' | 'standard' | 'premium'

type SceneDetailDraft = {
  prompt: string
  negativePrompt: string
  overlayText: string
  dialogueText: string
  speaker: string
  audioMode: 'dialogue' | 'voiceover' | 'silent'
  expressionLevel: 'natural' | 'expressive'
  emotionTag: string
  voiceChoice: string
  voiceGenderPreference?: 'auto' | 'female' | 'male'
  performanceStyle?: 'auto' | 'natural' | 'cinematic' | 'ugc' | 'commercial' | 'mentor' | 'emotional'
  voicePace: 'auto' | 'slightly_faster' | 'faster' | 'slower'
  voiceIntensity: 'auto' | 'restrained' | 'medium' | 'high'
  voiceEmphasis: 'auto' | 'cinematic' | 'natural' | 'dramatic'
  lockVoiceSettings: boolean
  enhanceVoiceWithAI?: boolean
  languageCode?: string
  avatarQuality?: AvatarQuality
}

function getDurationOptions(publishTarget: DirectionState['publishTarget']) {
  if (publishTarget === 'shorts') {
    return [
      {
        id: 'short',
        label: 'Short viral',
        hint: '30–60 segundos',
        durationTargetSec: 60,
        productionMode: 'express',
        visualOutputMode: 'animated_scenes',
      },
      {
        id: 'medium',
        label: 'Short extendido',
        hint: '60–90 segundos',
        durationTargetSec: 90,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'auto',
        label: 'Que Samantha decida',
        hint: 'Optimizar según la idea',
        durationTargetSec: 60,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
    ]
  }

  if (publishTarget === 'youtube') {
    return [
      {
        id: 'short',
        label: 'Video corto',
        hint: '1–3 minutos',
        durationTargetSec: 180,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'medium',
        label: 'Video medio',
        hint: '4–8 minutos',
        durationTargetSec: 480,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
      {
        id: 'long',
        label: 'YouTube largo',
        hint: '8–12 minutos',
        durationTargetSec: 720,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
      {
        id: 'documentary',
        label: 'Mini documental',
        hint: '15–20 minutos',
        durationTargetSec: 1200,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
      {
        id: 'auto',
        label: 'Que Samantha decida',
        hint: 'Según objetivo y tema',
        durationTargetSec: 600,
        productionMode: 'cinematic_longform',
        visualOutputMode: 'static_images',
      },
    ]
  }

  if (publishTarget === 'feed') {
    return [
      {
        id: 'short',
        label: 'Post breve',
        hint: '30–60 segundos',
        durationTargetSec: 60,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'medium',
        label: 'Contenido educativo',
        hint: '1–3 minutos',
        durationTargetSec: 180,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
      {
        id: 'auto',
        label: 'Que Samantha decida',
        hint: 'Optimizar según plataforma',
        durationTargetSec: 90,
        productionMode: 'express',
        visualOutputMode: 'mixed',
      },
    ]
  }

  return [
    {
      id: 'auto',
      label: 'Que Samantha decida',
      hint: 'Recomendado',
      durationTargetSec: 60,
      productionMode: 'express',
      visualOutputMode: 'mixed',
    },
    {
      id: 'short',
      label: 'Corto',
      hint: '30–90 segundos',
      durationTargetSec: 60,
      productionMode: 'express',
      visualOutputMode: 'mixed',
    },
    {
      id: 'medium',
      label: 'Medio',
      hint: '2–5 minutos',
      durationTargetSec: 300,
      productionMode: 'express',
      visualOutputMode: 'mixed',
    },
    {
      id: 'long',
      label: 'Largo',
      hint: '8–12 minutos',
      durationTargetSec: 720,
      productionMode: 'cinematic_longform',
      visualOutputMode: 'static_images',
    },
  ]
}

function hasDiscoveryContext(payload: any, currentDiscovery?: any) {
  const discovery =
    currentDiscovery ||
    payload?.discovery ||
    payload?.creativeDirection?.discovery ||
    {}

  const topic = String(
    discovery?.topic ||
      payload?.topic ||
      payload?.niche ||
      payload?.idea ||
      payload?.creativeDirection?.topic ||
      payload?.creativeDirection?.niche ||
      ''
  ).trim()

  const audience = String(
    discovery?.audience ||
      payload?.audience ||
      payload?.creativeDirection?.audience ||
      ''
  ).trim()

  return Boolean(topic && audience)
}

function getGlobalVoiceSettingsFromResult(result: any, request: any) {
  return (
    result?.project_json?.voice_settings ||
    result?.project?.project_json?.voice_settings ||
    request?.payload?.project_json?.voice_settings ||
    null
  )
}

async function fetchSceneVideoStatus(generationId: number, sceneId: number) {
  const res = await fetch(
    `/api/creator-studio/video/scene/status?generationId=${generationId}&sceneId=${sceneId}`,
    {
      credentials: 'include',
      cache: 'no-store',
    }
  )

  const json = await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || 'No se pudo consultar el estado del video')
  }

  return json
}

export default function SynCreateContentFlow({
  request,
  phase,
}: {
  request: TaskFlowRequest
  phase: TaskFlowPhase
}) {
  const {
    setPhase,
    setResult,
    result,
    setError,
    resetTaskFlow,
  } = useTaskFlowStore()

  const [busy, setBusy] = useState(false)

  const [direction, setDirection] = useState<DirectionState>({
    contentGoal: '',
    publishTarget: '',
    durationMode: '',
    durationTargetSec: undefined,
    productionMode: undefined,
    visualOutputMode: undefined,
    useCharacter: '',
    characterType: '',
    hasProductImage: '',
  })

  const [discovery, setDiscovery] = useState({
    topic: '',
    objective: '',
    audience: '',
    desiredAction: '',
    mustInclude: '',
    mustAvoid: '',
  })

  const [regeneratingMotionSceneId, setRegeneratingMotionSceneId] = useState<number | null>(null)

  const [stepIndex, setStepIndex] = useState(0)

  const plan = result?.plan || null
  const generationId = result?.generationId || null
  const projectJson = result?.projectJson || null

  const projectId = Number(
    result?.projectId ||
      result?.creatorStudioProject?.project_id ||
      request.payload?.projectId ||
      request.payload?.creatorStudioProject?.project_id ||
      0
  )

  const projectAspectRatio =
    result?.project?.aspect_ratio ||
    result?.project?.aspectRatio ||
    result?.aspect_ratio ||
    result?.aspectRatio ||
    projectJson?.brief?.aspectRatio ||
    projectJson?.brief?.aspect_ratio ||
    request.payload?.aspectRatio ||
    request.payload?.aspect_ratio ||
    request.payload?.creatorStudioProject?.aspect_ratio ||
    request.payload?.creatorStudioProject?.aspectRatio ||
    '9:16'

  const isCreatorStudioVideoProject =
    request.payload?.source === 'creator_studio_project_video'

  const latestScriptGeneration =
    request.payload?.scriptGeneration ||
    request.payload?.creatorStudioProject?.latestByStep?.scripts ||
    request.payload?.creatorStudioProject?.generationsByStep?.scripts?.[0] ||
    request.payload?.creatorStudioProject?.generations?.find?.(
      (generation: any) =>
        generation?.tab === 'scripts' || generation?.project_step === 'scripts'
    ) ||
    null

  const creatorStudioMaterial = {
    idea:
      request.payload?.selectedIdea ||
      request.payload?.initialBrief?.selectedIdea ||
      null,

    hook:
      request.payload?.selectedHook ||
      request.payload?.initialBrief?.selectedHook ||
      null,

    script:
      request.payload?.script ||
      request.payload?.initialBrief?.script ||
      latestScriptGeneration?.result_json ||
      result?.script ||
      null,

    scriptGenerationId:
      request.payload?.scriptGenerationId ||
      request.payload?.sourceScriptGenerationId ||
      request.payload?.initialBrief?.scriptGenerationId ||
      request.payload?.initialBrief?.sourceScriptGenerationId ||
      request.payload?.scriptGeneration?.generation_id ||
      request.payload?.scriptGeneration?.generationId ||
      latestScriptGeneration?.generation_id ||
      latestScriptGeneration?.generationId ||
      request.payload?.script?.generation_id ||
      request.payload?.script?.generationId ||
      request.payload?.initialBrief?.script?.generation_id ||
      request.payload?.initialBrief?.script?.generationId ||
      result?.scriptGenerationId ||
      result?.sourceScriptGenerationId ||
      null,
  }

  const isUGC = direction.contentGoal === 'ugc'
  const isProductGoal = direction.contentGoal === 'product'
  const isStorytelling = direction.contentGoal === 'storytelling'
  const isViral = direction.contentGoal === 'viral'
  const isAuto = direction.contentGoal === 'auto'

  function normalizeHyperframesPlan(plan: any) {
    if (!plan) return null

    const normalizeText = (item: any) => {
      const visibleText = String(
        item?.visibleText ||
        item?.overlayText ||
        item?.headline ||
        ''
      ).trim()

      return {
        ...item,
        visibleText,
        overlayText: visibleText,
        headline: '',
      }
    }

    return {
      ...plan,
      clips: Array.isArray(plan.clips)
        ? plan.clips.map(normalizeText)
        : [],
      motionBlueprint: plan.motionBlueprint
        ? {
            ...plan.motionBlueprint,
            contentBlocks: Array.isArray(plan.motionBlueprint.contentBlocks)
              ? plan.motionBlueprint.contentBlocks.map(normalizeText)
              : [],
          }
        : plan.motionBlueprint,
    }
  }

  const [sceneProgress, setSceneProgress] = useState<any[]>([])

  const [viewMode, setViewMode] = useState<'grid' | 'scene_detail'>('grid')
  const [selectedSceneId, setSelectedSceneId] = useState<number | null>(null)
  const [sceneDraft, setSceneDraft] = useState<SceneDetailDraft | null>(null)
  const [savingSceneEdit, setSavingSceneEdit] = useState(false)

  const [isSavingSceneDraft, setIsSavingSceneDraft] = useState(false)
  const [animatingSceneId, setAnimatingSceneId] = useState<number | null>(null)

  const [generatingVoiceSceneId, setGeneratingVoiceSceneId] = useState<number | null>(null)
  const [voiceHistoryOpen, setVoiceHistoryOpen] = useState(false)
  const [voiceTakes, setVoiceTakes] = useState<any[]>([])
  const [loadingVoiceTakes, setLoadingVoiceTakes] = useState(false)
  const [selectingVoiceAssetId, setSelectingVoiceAssetId] = useState<number | null>(null)

  const [globalVoiceSettingsState, setGlobalVoiceSettingsState] = useState<any>(null)
  const globalVoiceSettings =
    globalVoiceSettingsState ||
    result?.project_json?.voice_settings ||
    result?.project?.project_json?.voice_settings ||
    request?.payload?.project_json?.voice_settings ||
    null

  const hasGlobalVoicePreset = Boolean(globalVoiceSettings?.locked)

  const [avatarMode, setAvatarMode] = useState<'choose' | 'clone' | 'ai'>('choose')
  const [portraitItems, setPortraitItems] = useState<any[]>([])
  const [portraitsLoading, setPortraitsLoading] = useState(false)
  const [selectedPortraitId, setSelectedPortraitId] = useState<number | null>(null)

  const allScenesReady =
    Array.isArray(sceneProgress) &&
    sceneProgress.length > 0 &&
    sceneProgress.every((scene: any) => {
      if (!sceneNeedsImageFrame(scene)) return true
      return !!scene.imageUrl
    })
  
  const scenesThatNeedVideo = Array.isArray(sceneProgress)
    ? sceneProgress.filter((scene: any) => sceneNeedsVideo(scene))
    : []

  const allSceneVideosReady =
    scenesThatNeedVideo.length > 0 &&
    scenesThatNeedVideo.every((scene: any) => !!scene.videoUrl)

  const shouldAskCharacter =
    !!direction.contentGoal &&
    !isUGC &&
    (isProductGoal || isStorytelling || isViral || isAuto)

  const totalSteps =
    4 + (shouldAskCharacter ? 1 : 0)

  const canContinueDirection = useMemo(() => {
    if (!direction.contentGoal) return false
    if (!direction.publishTarget) return false
    if (!direction.durationMode) return false
    if (!direction.hasProductImage) return false

    if (isUGC) return true

    if (!shouldAskCharacter) return true
    if (!direction.useCharacter) return false

    if (direction.useCharacter === 'yes' && !direction.characterType) return false
    if (direction.useCharacter === 'no' && direction.characterType !== 'none') return false
    if (direction.useCharacter === 'auto' && direction.characterType !== 'auto') return false

    return true
  }, [direction, isUGC, shouldAskCharacter])

  const totalShots = sceneProgress.reduce((sum: number, scene: any) => {
    const shots = scene?.metadata_json?.shots || scene?.metadata?.shots || []
    return sum + (Array.isArray(shots) ? shots.length : 0)
  }, 0)

  const hyperframesScenes = sceneProgress.filter((scene: any) => {
    const shots = scene?.metadata_json?.shots || scene?.metadata?.shots || []

    return (
      Array.isArray(shots) &&
      shots.length > 0 &&
      shots.every((shot: any) =>
        String(shot?.render_engine || '').trim() === 'hyperframes' &&
        !shot?.needs_extra_frame
      )
    )
  }).length

  const [hasPlanned, setHasPlanned] = useState(false)

  const snapshotId = useTaskFlowStore((s) => s.snapshotId)
  const setSnapshotId = useTaskFlowStore((s) => s.setSnapshotId)

  const [storyboardBusy, setStoryboardBusy] = useState(false)

  const hydratedSnapshotRef = useRef(false)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const latestSceneProgressRef = useRef<any[]>([])
  const regeneratingSceneIdsRef = useRef<Set<number>>(new Set())
  const DEBUG_FAKE_REGENERATE = false
  const DEBUG_FAKE_REGENERATE_DELAY_MS = 2200

  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [
    generateStoryboardImages,
    setGenerateStoryboardImages,
  ] = useState(
    request.payload
      ?.generateStoryboardImages !==
      false
  )

  const [
    allowHyperframesScenes,
    setAllowHyperframesScenes,
  ] = useState(
    request.payload
      ?.allowHyperframesScenes ===
      true
  )

  const [productionSteps, setProductionSteps] = useState([
    {
      id: 'shell',
      label: 'Preparando producción',
      description: 'Estamos creando el contenedor técnico del proyecto.',
      status: 'pending' as 'pending' | 'active' | 'done' | 'error',
    },
    {
      id: 'storyboard_job',
      label: 'Diseñando storyboard premium',
      description: 'Samantha está convirtiendo tu guion y biblias visuales en escenas finales.',
      status: 'pending' as 'pending' | 'active' | 'done' | 'error',
    },
    {
      id: 'scenes',
      label: 'Organizando escenas',
      description: 'Estamos preparando las escenas para revisión antes de generar imágenes.',
      status: 'pending' as 'pending' | 'active' | 'done' | 'error',
    },
    {
      id: 'review',
      label: 'Preparando revisión',
      description: 'Tu storyboard quedará listo para aprobar o ajustar.',
      status: 'pending' as 'pending' | 'active' | 'done' | 'error',
    },
  ])

  const [productionJobId, setProductionJobId] = useState<number | null>(null)
  const [productionJobStatus, setProductionJobStatus] = useState<string | null>(null)
  const [productionJobProgress, setProductionJobProgress] = useState(0)
  const [productionJobStep, setProductionJobStep] = useState<string | null>(null)

  function updateProductionStep(
    id: string,
    status: 'pending' | 'active' | 'done' | 'error'
  ) {
    setProductionSteps((prev) =>
      prev.map((step) =>
        step.id === id
          ? { ...step, status }
          : step
      )
    )
  }

  async function hydrateSceneVideoStatuses() {
    if (!generationId) return

    const currentScenes = latestSceneProgressRef.current || []

    if (!currentScenes.length) return

    const statuses = await Promise.all(
      currentScenes.map(async (scene: any) => {
        try {
          const status = await fetchSceneVideoStatus(
            Number(generationId),
            Number(scene.sceneId)
          )

          return {
            sceneId: Number(scene.sceneId),
            status,
          }
        } catch (err) {
          console.warn('[HYDRATE_SCENE_VIDEO_STATUS_FAILED]', scene.sceneId, err)
          return {
            sceneId: Number(scene.sceneId),
            status: null,
          }
        }
      })
    )

    const statusMap = new Map(
      statuses.map((item) => [Number(item.sceneId), item.status])
    )

    const nextScenes = currentScenes.map((scene: any) => {
      const videoStatus = statusMap.get(Number(scene.sceneId))

      if (!videoStatus) return scene

      if (videoStatus.status === 'ready') {
        return {
          ...scene,
          videoUrl: videoStatus.videoUrl,
          assetId: videoStatus.assetId || scene.assetId,
          status: 'video_done',
          videoStatus: 'ready',
          videoRequestId: videoStatus.requestId || scene.videoRequestId || null,
          videoModel: videoStatus.model || scene.videoModel || null,
          metadata: {
            ...(scene.metadata || {}),
            video: {
              ...((scene.metadata || {})?.video || {}),
              status: 'ready',
              video_url: videoStatus.videoUrl,
              request_id: videoStatus.requestId || null,
              model: videoStatus.model || null,
              asset_id: videoStatus.assetId || null,
            },
          },
        }
      }

      if (videoStatus.status === 'processing') {
        return {
          ...scene,
          status: 'video_processing',
          videoStatus: 'processing',
          videoRequestId: videoStatus.requestId || scene.videoRequestId || null,
          videoModel: videoStatus.model || scene.videoModel || null,
          metadata: {
            ...(scene.metadata || {}),
            video: {
              ...((scene.metadata || {})?.video || {}),
              status: 'processing',
              request_id: videoStatus.requestId || null,
              model: videoStatus.model || null,
            },
          },
        }
      }

      if (videoStatus.status === 'failed') {
        return {
          ...scene,
          status: 'video_failed',
          videoStatus: 'failed',
          videoError: videoStatus.error || 'La generación de video falló.',
          metadata: {
            ...(scene.metadata || {}),
            video: {
              ...((scene.metadata || {})?.video || {}),
              status: 'failed',
              error: videoStatus.error || null,
              request_id: videoStatus.requestId || null,
              model: videoStatus.model || null,
            },
          },
        }
      }

      return scene
    })

    latestSceneProgressRef.current = nextScenes
    setSceneProgress(nextScenes)
  }

  async function loadExistingAvatars() {
    try {
      setPortraitsLoading(true)

      const res = await fetch('/api/creator-studio/portrait/list?limit=12', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudieron cargar los avatares')
      }

      setPortraitItems(Array.isArray(json.items) ? json.items : [])
    } catch (err) {
      console.error('[SynCreateContentFlow] loadExistingAvatars error', err)
      setPortraitItems([])
    } finally {
      setPortraitsLoading(false)
    }
  }

  async function saveFlowSnapshot(nextPhase?: string, nextResult?: any) {
    try {
        const baseResult = (nextResult || result) || {}
        const prevMeta = baseResult?.__snapshotMeta || {}

        const hasDirectionValues =
        !!direction?.contentGoal ||
        !!direction?.useCharacter ||
        !!direction?.characterType ||
        !!direction?.hasProductImage ||
        !!direction?.publishTarget

        const safeResult = {
        ...baseResult,
        __snapshotMeta: {
            direction: hasDirectionValues
            ? direction
            : prevMeta.direction || baseResult?.creativeDirection || {},
            stepIndex:
            typeof stepIndex === 'number'
                ? stepIndex
                : typeof prevMeta.stepIndex === 'number'
                ? prevMeta.stepIndex
                : 0,
            sceneProgress:
            Array.isArray(sceneProgress) && sceneProgress.length > 0
                ? sceneProgress
                : Array.isArray(prevMeta.sceneProgress)
                ? prevMeta.sceneProgress
                : [],
        },
        }

        console.log('[saveFlowSnapshot] start', {
        snapshotId,
        nextPhase,
        sellerId: request.payload?.sellerId,
        generationId: safeResult?.generationId || null,
        safeResult,
        })

        const res = await fetch('/api/task-flows/create-content/snapshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
            snapshotId,
            sellerId: request.payload?.sellerId,
            generationId: safeResult?.generationId || null,
            phase: nextPhase || phase,
            snapshot: {
            phase: nextPhase || phase,
            request,
            result: safeResult,
            },
        }),
        })

        const json = await res.json().catch(() => null)
        console.log('[saveFlowSnapshot] response', { ok: res.ok, json })

        if (res.ok && json?.ok && json?.snapshotId) {
        setSnapshotId(Number(json.snapshotId))
        }
    } catch (err) {
        console.error('saveFlowSnapshot error', err)
    }
  }

  function normalizeScene(raw: any): SceneItem {
    return {
      sceneId: raw?.sceneId ?? raw?.scene_id ?? raw?.id ?? null,
      sceneOrder: Number(raw?.sceneOrder ?? raw?.scene_order ?? 0),
      title: String(raw?.title || 'Escena'),
      prompt: String(raw?.prompt || ''),
      negativePrompt: String(raw?.negativePrompt || raw?.negative_prompt || ''),
      overlayText: String(
        raw?.overlayText ||
          raw?.overlay_text ||
          raw?.metadata_json?.overlay_text ||
          ''
      ),
      durationSec: raw?.durationSec ?? raw?.duration_sec ?? null,
      sceneType: String(raw?.sceneType || raw?.scene_type || ''),
      speaker: String(raw?.audio?.speaker || raw?.speaker || ''),
      dialogueText: String(raw?.audio?.text || raw?.dialogueText || ''),
      imageUrl: raw?.imageUrl || raw?.image_url || null,
      status: raw?.status || (raw?.imageUrl || raw?.image_url ? 'done' : 'pending'),
      metadata: raw?.metadata || raw?.metadata_json || {},
    }
  }

  const selectedScene = useMemo(() => {
    if (!selectedSceneId) return null

    return sceneProgress.find((scene: any) => {
      return Number(scene.sceneId || scene.scene_id || 0) === Number(selectedSceneId)
    }) || null
  }, [sceneProgress, selectedSceneId])

  function openSceneDetail(scene: any) {
    const normalized = mapSceneToRuntime(scene)

    setSelectedSceneId(Number(normalized.sceneId))

    const normalizedAny = normalized as any

    setSceneDraft({
      prompt: normalizedAny.prompt || '',
      negativePrompt: normalizedAny.negativePrompt || '',
      overlayText: normalizedAny.overlayText || '',
      dialogueText: normalizedAny.dialogueText || '',
      speaker: normalizedAny.speaker || 'expert',
      audioMode: normalizedAny.audioMode || 'voiceover',
      expressionLevel: normalizedAny.expressionLevel || 'natural',
      emotionTag: normalizedAny.emotionTag || '',
      voiceChoice: normalizedAny.voiceChoice || 'auto',
      voiceGenderPreference: normalizedAny.voiceGenderPreference || 'auto',
      performanceStyle: normalizedAny.performanceStyle || 'auto',
      enhanceVoiceWithAI: normalizedAny.enhanceVoiceWithAI !== false,
      voicePace:
        normalizedAny?.voicePace ||
        normalizedAny?.voice_pace ||
        normalizedAny?.voice?.voicePace ||
        normalizedAny?.voice?.voice_pace ||
        'auto',

      voiceIntensity:
        normalizedAny?.voiceIntensity ||
        normalizedAny?.voice_intensity ||
        normalizedAny?.voice?.voiceIntensity ||
        normalizedAny?.voice?.voice_intensity ||
        'auto',

      voiceEmphasis:
        normalizedAny?.voiceEmphasis ||
        normalizedAny?.voice_emphasis ||
        normalizedAny?.voice?.voiceEmphasis ||
        normalizedAny?.voice?.voice_emphasis ||
        'auto',
      lockVoiceSettings: Boolean(
        normalizedAny.lockVoiceSettings ??
          normalizedAny.lock_voice_settings ??
          normalizedAny.metadata?.voice?.lockVoiceSettings ??
          normalizedAny.metadata?.voice?.lock_voice_settings ??
          normalizedAny.metadata_json?.voice?.lockVoiceSettings ??
          normalizedAny.metadata_json?.voice?.lock_voice_settings ??
          false
      ),
      languageCode: normalizedAny.languageCode || '',
      avatarQuality:
        normalizedAny.avatarQuality ||
        normalizedAny.avatar_quality ||
        normalizedAny.metadata?.video?.avatar_quality ||
        normalizedAny.metadata_json?.video?.avatar_quality ||
        'standard',
    })
    setViewMode('scene_detail')
  }

  function closeSceneDetail() {
    setViewMode('grid')
    setSelectedSceneId(null)
    setSceneDraft(null)
  }

  function buildFakeRegeneratedImageUrl(scene: any) {
    const current = String(scene?.imageUrl || '').trim()

    if (!current) return ''

    // Solo cambiamos la URL con un query param para forzar refresh visual
    // sin tocar backend ni guardar nada real
    const separator = current.includes('?') ? '&' : '?'
    return `${current}${separator}debug_regen=${Date.now()}`
  }

  async function simulateSceneRegeneration(scene: any) {
    await new Promise((resolve) =>
      setTimeout(resolve, DEBUG_FAKE_REGENERATE_DELAY_MS)
    )

    return {
      ok: true,
      imageUrl: buildFakeRegeneratedImageUrl(scene),
    }
  }

  const regenerateSingleScene = useCallback(async (scene: any) => {
    if (!sceneNeedsImageFrame(scene)) {
      alert('Esta escena usa HyperFrames y no necesita generar un frame con FAL.')
      return
    }

    const currentSceneId = Number(scene.sceneId)

    try {
      regeneratingSceneIdsRef.current.add(currentSceneId)

      let optimisticScenes: any[] = []

      setSceneProgress((prev) => {
        optimisticScenes = (prev || []).map((item: any) =>
          Number(item.sceneId) === currentSceneId
            ? mapSceneToRuntime(
                {
                  ...item,
                  status: 'generating',
                },
                item
              )
            : item
        )
        return optimisticScenes
      })

      latestSceneProgressRef.current = optimisticScenes

      let json: any = null

      if (DEBUG_FAKE_REGENERATE) {
        json = await simulateSceneRegeneration(scene)
      } else {
        const res = await fetch('/api/creator-studio/video/scene/regenerate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            generationId,
            sellerId: request.payload?.sellerId,
            customerId: request.payload?.userId,
            sceneId: currentSceneId,
            dryRun: false,
          }),
        })

        json = await res.json().catch(() => null)

        if (!res.ok || !json?.ok || !json?.imageUrl) {
          throw new Error(json?.error || 'No se pudo regenerar la escena')
        }
      }

      if (!json?.ok || !json?.imageUrl) {
        throw new Error('No se pudo regenerar la escena')
      }

      regeneratingSceneIdsRef.current.delete(currentSceneId)

      let nextScenes: any[] = []

      setSceneProgress((prev) => {
        nextScenes = (prev || []).map((item: any) =>
          Number(item.sceneId) === currentSceneId
            ? mapSceneToRuntime(
                {
                  ...item,
                  status: 'done',
                  imageUrl: json.imageUrl,
                },
                item
              )
            : item
        )
        return nextScenes
      })

      latestSceneProgressRef.current = nextScenes

      if (!DEBUG_FAKE_REGENERATE) {
        await saveFlowSnapshot(phase, {
          ...(result || {}),
          __snapshotMeta: {
            direction,
            stepIndex,
            sceneProgress: nextScenes,
          },
        })
      }
    } catch (err: any) {
      console.error(err)

      regeneratingSceneIdsRef.current.delete(currentSceneId)

      let revertedScenes: any[] = []

      setSceneProgress((prev) => {
        revertedScenes = (prev || []).map((item: any) =>
          Number(item.sceneId) === currentSceneId
            ? mapSceneToRuntime(
                {
                  ...item,
                  status: item.imageUrl ? 'done' : 'pending',
                },
                item
              )
            : item
        )
        return revertedScenes
      })

      latestSceneProgressRef.current = revertedScenes

      alert(err?.message || 'Error regenerando escena')
    }
  }, [
    generationId,
    request.payload?.sellerId,
    request.payload?.userId,
    phase,
    result,
    direction,
    stepIndex,
  ])

  async function handleRenderFinalVideo() {
    if (!generationId || !allSceneVideosReady) return

    try {
      setPhase('rendering_final_video')

      const res = await fetch('/api/creator-studio/video/render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo renderizar el video final')
      }

      const nextResult = {
        ...(result || {}),
        finalVideoUrl: json.finalVideoUrl,
      }

      setResult(nextResult)
      setPhase('result')

      await saveFlowSnapshot('result', nextResult)
    } catch (err: any) {
      console.error(err)
      alert(err?.message || 'Error creando video final')
      setPhase('generating_scenes')
    }
  }

  async function handleUploadSceneImage(file: File, sceneId: number) {
    if (!generationId || !sceneId || !file) return

    try {
      const sellerId = request.payload?.sellerId
      const customerId = request.payload?.userId

      const formData = new FormData()
      formData.append('generationId', String(generationId))
      formData.append('customerId', String(customerId || ''))
      formData.append('sellerId', String(sellerId || ''))
      formData.append('kind', 'scene')
      formData.append('sceneId', String(sceneId))
      formData.append('files', file)

      const uploadRes = await fetch('/api/creator-studio/video/project/assets/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      const uploadJson = await uploadRes.json().catch(() => null)

      if (!uploadRes.ok || !uploadJson?.ok) {
        throw new Error(uploadJson?.error || 'No se pudo subir la imagen')
      }

      const imageUrl =
        uploadJson.url ||
        uploadJson.asset?.url ||
        uploadJson.assets?.[0]?.url ||
        uploadJson.file?.url ||
        ''

      const assetId =
        uploadJson.assetId ||
        uploadJson.asset?.assetId ||
        uploadJson.asset?.asset_id ||
        uploadJson.assetIds?.[0] ||
        uploadJson.assets?.[0]?.assetId ||
        uploadJson.assets?.[0]?.asset_id ||
        null

      if (!imageUrl) {
        throw new Error('La imagen se subió, pero no regresó URL')
      }

      const updateRes = await fetch('/api/creator-studio/video/scene/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId,
          sellerId,
          customerId,
          assetId,
          image_url: imageUrl,
          imageUrl,
        }),
      })

      const updateJson = await updateRes.json().catch(() => null)

      if (!updateRes.ok || !updateJson?.ok) {
        throw new Error(updateJson?.error || 'No se pudo guardar la imagen en la escena')
      }

      let nextScenes: any[] = []

      setSceneProgress((prev) => {
        nextScenes = (prev || []).map((item: any) => {
          const currentSceneId = Number(item.sceneId || item.scene_id || 0)

          if (currentSceneId !== Number(sceneId)) return item

          return {
            ...item,
            imageUrl,
            image_url: imageUrl,
            thumbnailUrl: imageUrl,
            thumbnail_url: imageUrl,
            status:
              item.status === 'pending' || item.status === 'generating'
                ? 'done'
                : item.status,
            metadata: {
              ...(item.metadata || {}),
              manual_asset_id: assetId,
              uploaded_image_url: imageUrl,
              image_url: imageUrl,
              imageUrl,
              image_source: 'manual_upload',
            },
            metadata_json: {
              ...(item.metadata_json || item.metadata || {}),
              manual_asset_id: assetId,
              uploaded_image_url: imageUrl,
              image_url: imageUrl,
              imageUrl,
              image_source: 'manual_upload',
            },
          }
        })

        return nextScenes
      })

      latestSceneProgressRef.current = nextScenes

      await saveFlowSnapshot(phase, {
        ...(result || {}),
        __snapshotMeta: {
          direction,
          stepIndex,
          sceneProgress: nextScenes,
        },
      })
    } catch (err: any) {
      console.error('[handleUploadSceneImage]', err)
      alert(err?.message || 'Error subiendo imagen de escena')
      throw err
    }
  }

  async function handleSaveSceneEdits() {
    if (!generationId || !selectedSceneId || !sceneDraft) return

    try {
      setSavingSceneEdit(true)

      const res = await fetch('/api/creator-studio/video/scene/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId: selectedSceneId,
          sellerId: request.payload?.sellerId,
          customerId: request.payload?.userId,
          prompt: sceneDraft.prompt,
          negative_prompt: sceneDraft.negativePrompt,
          overlay_text: sceneDraft.overlayText,
          audio: {
            speaker: sceneDraft.speaker,
            text: sceneDraft.dialogueText,
            mode: sceneDraft.audioMode,
          },
          voice: {
            expression_level: sceneDraft.expressionLevel,
            emotion_tag: sceneDraft.emotionTag,
            selected_voice: sceneDraft.voiceChoice,
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo guardar la escena')
      }

      let nextScenes: any[] = []

      setSceneProgress((prev) => {
        nextScenes = (prev || []).map((item: any) =>
          Number(item.sceneId) === Number(selectedSceneId)
            ? {
                ...item,
                prompt: sceneDraft.prompt,
                negativePrompt: sceneDraft.negativePrompt,
                overlayText: sceneDraft.overlayText,
                speaker: sceneDraft.speaker,
                dialogueText: sceneDraft.dialogueText,
                audioMode: sceneDraft.audioMode,
                expressionLevel: sceneDraft.expressionLevel,
                emotionTag: sceneDraft.emotionTag,
                voiceChoice: sceneDraft.voiceChoice,
                metadata: {
                  ...(item.metadata || {}),
                  voice: {
                    ...((item.metadata || {})?.voice || {}),
                    expression_level: sceneDraft.expressionLevel,
                    emotion_tag: sceneDraft.emotionTag,
                    selected_voice: sceneDraft.voiceChoice,
                  },
                },
              }
            : item
        )
        return nextScenes
      })

      latestSceneProgressRef.current = nextScenes

      await saveFlowSnapshot(phase, {
        ...(result || {}),
        __snapshotMeta: {
          direction,
          stepIndex,
          sceneProgress: nextScenes,
        },
      })
      return true
    } catch (err: any) {
      console.error(err)
      alert(err?.message || 'Error guardando escena')
      return false
    } finally {
      setSavingSceneEdit(false)
    }
  }

  async function handleSaveAndRegenerateFromDetail() {
    if (!selectedScene) return

    const saved = await handleSaveSceneEdits()
    if (!saved) return

    const freshScene =
      (latestSceneProgressRef.current || []).find(
        (item: any) => Number(item.sceneId) === Number(selectedScene.sceneId)
      ) || {
        ...selectedScene,
        prompt: sceneDraft?.prompt || selectedScene.prompt,
        negativePrompt: sceneDraft?.negativePrompt || selectedScene.negativePrompt,
        overlayText: sceneDraft?.overlayText || selectedScene.overlayText,
        dialogueText: sceneDraft?.dialogueText || selectedScene.dialogueText,
        speaker: sceneDraft?.speaker || selectedScene.speaker,
        audioMode: sceneDraft?.audioMode || selectedScene.audioMode,
        expressionLevel: sceneDraft?.expressionLevel || selectedScene.expressionLevel,
        emotionTag: sceneDraft?.emotionTag || selectedScene.emotionTag,
        voiceChoice: sceneDraft?.voiceChoice || selectedScene.voiceChoice,
    }

    await regenerateSingleScene(freshScene)
  }

  async function handlePrepareHyperframesScene() {
    if (!selectedScene || !generationId) return

    const sceneId = Number(selectedScene.sceneId || selectedScene.scene_id || 0)

    if (!sceneId) {
      setError('No se encontró la escena para preparar HyperFrames.')
      return
    }

    setAnimatingSceneId(sceneId)

    try {
      const res = await fetch('/api/creator-studio/video/hyperframes/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo preparar HyperFrames.')
      }

      setSceneProgress((prev) =>
        prev.map((scene: any) => {
          const currentSceneId = Number(scene.sceneId || scene.scene_id || 0)

          if (currentSceneId !== sceneId) return scene

          return {
            ...scene,
            hyperframesPlan: normalizeHyperframesPlan(json.hyperframesPlan),
            metadata_json: {
              ...(scene.metadata_json || {}),
              hyperframes_plan: normalizeHyperframesPlan(json.hyperframesPlan),
              hyperframes_status: 'ready',
            },
            status: scene.status === 'generating' ? 'ready' : scene.status,
          }
        })
      )

      console.log('[HyperFrames prepare]', {
        sceneId,
        returnedSceneId: json?.hyperframesPlan?.sceneId,
        clips: json?.hyperframesPlan?.clips?.length,
        overlays: json?.hyperframesPlan?.overlays?.length,
      })
    } catch (err: any) {
      setError(err?.message || 'Error preparando HyperFrames.')
    } finally {
      setAnimatingSceneId(null)
    }
  }

  const projectVoiceContext = getSceneVoiceContext({
    project_json: projectJson,
  })

  const selectedSceneVoiceContext = selectedScene
    ? getSceneVoiceContext(selectedScene)
    : null

  const voiceContext =
    projectVoiceContext?.projectJson
      ? projectVoiceContext
      : selectedSceneVoiceContext

  const subjectGender = voiceContext?.subjectGender || 'unknown'

  const mustChooseVoiceGender =
    !!sceneDraft &&
    sceneDraft.audioMode !== 'silent' &&
    (sceneDraft.voiceChoice || 'auto') === 'auto' &&
    subjectGender === 'neutral'

  const resolvedLanguageCode =
    sceneDraft?.languageCode ||
    resolveDefaultLanguageCode(String(voiceContext?.language || ''))

  async function loadSceneVoiceHistory(sceneId?: number | string | null) {
    const effectiveSceneId = Number(sceneId || selectedScene?.sceneId || 0)

    if (!generationId || !effectiveSceneId) return

    try {
      setLoadingVoiceTakes(true)

      const res = await fetch(
        `/api/creator-studio/video/scene/voice/list?generationId=${generationId}&sceneId=${effectiveSceneId}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      )

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo cargar el historial de voces')
      }

      setVoiceTakes(Array.isArray(json.voices) ? json.voices : [])
    } catch (err) {
      console.error(err)
      setVoiceTakes([])
    } finally {
      setLoadingVoiceTakes(false)
    }
  }

  async function handleSelectSceneVoice(assetId: number) {
    if (!selectedScene?.sceneId || !generationId) return

    try {
      setSelectingVoiceAssetId(assetId)

      const res = await fetch('/api/creator-studio/video/scene/voice/select', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId: selectedScene.sceneId,
          assetId,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo seleccionar esta voz')
      }

      const nextScenes = (latestSceneProgressRef.current || []).map((item: any) =>
        Number(item.sceneId) === Number(selectedScene.sceneId)
          ? {
              ...item,
              voiceUrl: json.audioUrl,
              voiceStatus: 'ready',
              metadata: {
                ...(item.metadata || {}),
                voice: {
                  ...((item.metadata || {})?.voice || {}),
                  audio_url: json.audioUrl,
                  status: 'ready',
                  active_asset_id: json.assetId,

                  selected_voice: json.selectedVoice || null,
                  resolved_voice: json.resolvedVoice || json.voice || null,

                  performance_style: json.performanceStyle || 'auto',
                  voice_pace: json.voicePace || 'auto',
                  voice_intensity: json.voiceIntensity || 'auto',
                  voice_emphasis: json.voiceEmphasis || 'auto',
                  language_code: json.languageCode || null,
                },
              },
            }
          : item
      )

      latestSceneProgressRef.current = nextScenes
      setSceneProgress(nextScenes)

      setVoiceTakes((prev) =>
        prev.map((take) => ({
          ...take,
          active: Number(take.assetId) === Number(assetId),
        }))
      )

      await saveFlowSnapshot(phase, {
        ...(result || {}),
        __snapshotMeta: {
          direction,
          stepIndex,
          sceneProgress: nextScenes,
        },
      })
    } catch (err: any) {
      console.error(err)
      alert(err?.message || 'Error seleccionando voz')
    } finally {
      setSelectingVoiceAssetId(null)
    }
  }

  async function handleToggleGlobalVoicePreset(enabled: boolean) {
    if (!generationId || !selectedScene?.sceneId) return

    const activeVoiceAssetId =
      selectedScene?.metadata?.voice?.active_asset_id ||
      selectedScene?.metadata?.voice?.asset_id ||
      null

    if (enabled && !activeVoiceAssetId) {
      alert('Primero genera o selecciona una versión de voz para esta escena.')
      return
    }

    try {
      const res = await fetch('/api/creator-studio/video/scene/voice/preset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId: selectedScene.sceneId,
          sceneOrder: selectedScene.sceneOrder || selectedScene.scene_order || null,
          sceneTitle: selectedScene.title || null,
          assetId: activeVoiceAssetId,
          enabled,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo guardar el preset global de voz')
      }

      setGlobalVoiceSettingsState(json.voiceSettings || json.lockedVoicePreset || null)

      setSceneDraft((prev: any) =>
        prev
          ? {
              ...prev,
              lockVoiceSettings: json.locked === true,
            }
          : prev
      )

      await saveFlowSnapshot(phase, {
        ...(result || {}),
        project_json: {
          ...((result || {})?.project_json || {}),
          voice_settings: json.voiceSettings || json.lockedVoicePreset || null,
        },
        __snapshotMeta: {
          direction,
          stepIndex,
          sceneProgress: latestSceneProgressRef.current || sceneProgress,
        },
      })
    } catch (err: any) {
      console.error(err)
      alert(err?.message || 'Error guardando preset global de voz')
    }
  }

  async function handleGenerateSceneVoice() {
    if (!selectedScene?.sceneId || !generationId || !sceneDraft) return

    const voiceText = String(sceneDraft.dialogueText || '').trim()

    if (!voiceText) {
      alert('Escribe el texto de voz antes de generar el audio.')
      return
    }

    if (mustChooseVoiceGender && !sceneDraft.voiceGenderPreference) {
      alert('Selecciona si prefieres una voz femenina o masculina para este personaje.')
      return
    }

    try {
      setGeneratingVoiceSceneId(Number(selectedScene.sceneId))

      const res = await fetch('/api/creator-studio/video/scene/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId: selectedScene.sceneId,
          customerId: request.payload?.userId,
          sellerId: request.payload?.sellerId,

          audioMode: sceneDraft.audioMode,
          text: voiceText,

          // Nuevo flujo Gemini/FAL TTS
          voice: sceneDraft.voiceChoice || 'auto',
          voiceChoice: sceneDraft.voiceChoice || 'auto',
          voiceGenderPreference: sceneDraft.voiceGenderPreference || 'auto',
          performanceStyle: sceneDraft.performanceStyle || 'auto',

          voicePace: sceneDraft.voicePace || 'auto',
          voiceIntensity: sceneDraft.voiceIntensity || 'auto',
          voiceEmphasis: sceneDraft.voiceEmphasis || 'auto',
          lockVoiceSettings: sceneDraft.lockVoiceSettings === true,
          targetDurationSec:
            selectedScene?.durationSec ||
            selectedScene?.duration_sec ||
            null,

          enhanceVoiceWithAI: sceneDraft.enhanceVoiceWithAI !== false,
          languageCode: sceneDraft.languageCode || resolvedLanguageCode || '',

          // Legacy temporal
          speaker: sceneDraft.speaker,
          expressionLevel: sceneDraft.expressionLevel,
          emotionTag: sceneDraft.emotionTag,
        }),
      })

      const json = await res.json().catch(() => null)

      console.log('GENERATE VOICE RESPONSE >>>', json)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo generar la voz')
      }

      const nextScenes = (latestSceneProgressRef.current || []).map((item: any) =>
        Number(item.sceneId) === Number(selectedScene.sceneId)
          ? {
              ...item,
              voiceUrl: json.audioUrl,
              voiceStatus: 'ready',
              metadata: {
                ...(item.metadata || {}),
                voice: {
                  ...((item.metadata || {})?.voice || {}),
                  audio_url: json.audioUrl,
                  status: 'ready',

                  provider: json.provider || 'fal',
                  provider_model: json.model || 'fal-ai/gemini-3.1-flash-tts',

                  selected_voice: sceneDraft.voiceChoice || 'auto',
                  resolved_voice: json.voice || json.resolvedVoice || null,

                  voice_gender_preference: sceneDraft.voiceGenderPreference || null,
                  performance_style: sceneDraft.performanceStyle || 'auto',
                  enhance_voice_with_ai: sceneDraft.enhanceVoiceWithAI !== false,
                  language_code: sceneDraft.languageCode || resolvedLanguageCode || null,

                  voice_pace: sceneDraft.voicePace || 'auto',
                  voice_intensity: sceneDraft.voiceIntensity || 'auto',
                  voice_emphasis: sceneDraft.voiceEmphasis || 'auto',
                  target_duration_sec:
                    selectedScene?.durationSec ||
                    selectedScene?.duration_sec ||
                    null,

                  voice_settings_locked: json.voiceSettingsLocked === true,
                  locked_voice_preset_source_scene_id: json.lockedFromSceneId || null,
                  locked_voice_preset_source_scene_order: json.lockedFromSceneOrder || null,

                  enhanced_prompt: json.enhancedPrompt || null,
                  style_instructions: json.styleInstructions || null,

                  // Legacy temporal
                  expression_level: sceneDraft.expressionLevel,
                  emotion_tag: sceneDraft.emotionTag,
                },
              },
            }
          : item
      )

      latestSceneProgressRef.current = nextScenes
      setSceneProgress(nextScenes)

      const nextResult =
        json.voiceSettingsLocked && json.lockedVoicePreset
          ? {
              ...(result || {}),
              project_json: {
                ...((result || {})?.project_json || {}),
                voice_settings: json.lockedVoicePreset,
              },
            }
          : result

      if (voiceHistoryOpen) {
        await loadSceneVoiceHistory(selectedScene.sceneId)
      }

      await saveFlowSnapshot(phase, {
        ...(nextResult || result || {}),
        __snapshotMeta: {
          direction,
          stepIndex,
          sceneProgress: nextScenes,
        },
      })
    } catch (err: any) {
      console.error(err)
      alert(err?.message || 'Error generando voz')
    } finally {
      setGeneratingVoiceSceneId(null)
    }
  }

  async function handleRegenerateMotionWithDirection(direction: string) {
    if (!selectedScene || !generationId) return

    const sceneId = Number(selectedScene.sceneId || selectedScene.scene_id || 0)

    if (!sceneId) {
      setError('No se encontró la escena para ajustar la animación.')
      return
    }

    setRegeneratingMotionSceneId(sceneId)

    try {
      const prepareRes = await fetch('/api/creator-studio/video/hyperframes/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId,
          direction,
        }),
      })

      const prepareJson = await prepareRes.json().catch(() => null)

      if (!prepareRes.ok || !prepareJson?.ok) {
        throw new Error(prepareJson?.error || 'No se pudo ajustar la animación.')
      }

      const generateRes = await fetch('/api/creator-studio/video/hyperframes/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId,
        }),
      })

      const generateJson = await generateRes.json().catch(() => null)

      if (!generateRes.ok || !generateJson?.ok) {
        throw new Error(generateJson?.error || 'No se pudo generar la animación ajustada.')
      }

      const nextPlan =
        generateJson.hyperframesPlan ||
        {
          ...(prepareJson.hyperframesPlan || {}),
          status: 'generated',
          composition: generateJson.composition || null,
        }

      setSceneProgress((prev) =>
        prev.map((scene: any) => {
          const currentSceneId = Number(scene.sceneId || scene.scene_id || 0)

          if (currentSceneId !== sceneId) return scene

          const previousMetadata =
            scene.metadata_json ||
            scene.metadata ||
            {}

          return {
            ...scene,
            hyperframesPlan: nextPlan,
            metadata: {
              ...previousMetadata,
              hyperframes_plan: nextPlan,
              hyperframes_status: 'generated',
            },
            metadata_json: {
              ...previousMetadata,
              hyperframes_plan: nextPlan,
              hyperframes_status: 'generated',
            },
            status:
              scene.status === 'generating'
                ? 'animation_generated'
                : scene.status,
          }
        })
      )
    } catch (err: any) {
      console.error('[handleRegenerateMotionWithDirection]', err)
      setError(err?.message || 'Error ajustando la animación.')
    } finally {
      setRegeneratingMotionSceneId(null)
    }
  }

  async function handleGenerateHyperframesScene() {
    if (!selectedScene || !generationId) return

    const sceneId = Number(selectedScene.sceneId || selectedScene.scene_id || 0)

    if (!sceneId) {
      setError('No se encontró la escena para generar animación.')
      return
    }

    setAnimatingSceneId(sceneId)

    try {
      const res = await fetch('/api/creator-studio/video/hyperframes/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo generar la animación.')
      }

      setSceneProgress((prev) =>
        prev.map((scene: any) => {
          const currentSceneId = Number(scene.sceneId || scene.scene_id || 0)

          if (currentSceneId !== sceneId) return scene

          const previousMetadata =
            scene.metadata_json ||
            scene.metadata ||
            {}

          const nextPlan =
            json.hyperframesPlan ||
            {
              ...(previousMetadata?.hyperframes_plan || scene.hyperframesPlan || {}),
              status: 'generated',
              composition: json.composition || null,
            }

          return {
            ...scene,
            hyperframesPlan: nextPlan,
            metadata: {
              ...previousMetadata,
              hyperframes_plan: nextPlan,
              hyperframes_status: 'generated',
            },
            metadata_json: {
              ...previousMetadata,
              hyperframes_plan: nextPlan,
              hyperframes_status: 'generated',
            },
            status:
              scene.status === 'generating'
                ? 'hyperframes_generated'
                : scene.status,
          }
        })
      )
    } catch (err: any) {
      console.error('[handleGenerateHyperframesScene]', err)
      setError(err?.message || 'Error generando HyperFrames.')
    } finally {
      setAnimatingSceneId(null)
    }
  }

  function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async function refreshProjectScenesFromServer(targetSceneId?: number) {
    if (!generationId) return null

    const projectRes = await fetch(`/api/creator-studio/video/project/${generationId}`, {
      credentials: 'include',
      cache: 'no-store',
    })

    const projectFresh = await projectRes.json().catch(() => null)

    if (!projectRes.ok || !projectFresh?.ok) {
      throw new Error(projectFresh?.error || 'No se pudo refrescar el proyecto')
    }

    const freshScenes = Array.isArray(projectFresh.scenes)
      ? projectFresh.scenes.map((s: any) => mapSceneToRuntime(s))
      : []

    setSceneProgress(freshScenes)
    latestSceneProgressRef.current = freshScenes

    if (targetSceneId) {
      const freshSelected = freshScenes.find(
        (s: any) => Number(s.sceneId) === Number(targetSceneId)
      )

      if (freshSelected) {
        setSelectedSceneId(Number(freshSelected.sceneId))
      }

      return freshSelected || null
    }

    return null
  }

  async function handleAnimateScene(avatarQuality: AvatarQuality = 'standard') {
    if (!selectedScene?.sceneId || !generationId) return

    const activeSceneId = Number(selectedScene.sceneId)

    try {
      setAnimatingSceneId(activeSceneId)

      const res = await fetch('/api/creator-studio/video/scene/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          generationId,
          sceneId: activeSceneId,
          customerId: request.payload?.userId,
          sellerId: request.payload?.sellerId,
          aspectRatio: projectAspectRatio,
          avatarQuality,
        }),
      })

      const json = await res.json().catch(() => null)

      console.log('ANIMATE SCENE RESPONSE >>>', json)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo preparar la animación')
      }

      const nextScenes = (latestSceneProgressRef.current || []).map((item: any) =>
        Number(item.sceneId) === activeSceneId
          ? {
              ...item,
              status: 'video_processing',
              videoStatus: 'processing',
              videoRequestId: json.requestId || null,
              videoModel: json.model || null,
              avatarQuality: json.avatarQuality || avatarQuality,
              metadata: {
                ...(item.metadata || {}),
                video: {
                  ...((item.metadata || {})?.video || {}),
                  status: 'processing',
                  request_id: json.requestId || null,
                  model: json.model || null,
                  avatar_quality: json.avatarQuality || avatarQuality,
                  avatar_quality_label: json.avatarQualityLabel || null,
                  credits_charged: json.creditsCharged || null,
                  audio_url: json.audioUrl || null,
                  voice_asset_id: json.voiceAssetId || null,
                  image_asset_id: json.imageAssetId || null,
                },
              },
              metadata_json: {
                ...(item.metadata_json || item.metadata || {}),
                video: {
                  ...((item.metadata_json || item.metadata || {})?.video || {}),
                  status: 'processing',
                  request_id: json.requestId || null,
                  model: json.model || null,
                  avatar_quality: json.avatarQuality || avatarQuality,
                  avatar_quality_label: json.avatarQualityLabel || null,
                  credits_charged: json.creditsCharged || null,
                  audio_url: json.audioUrl || null,
                  voice_asset_id: json.voiceAssetId || null,
                  image_asset_id: json.imageAssetId || null,
                },
              },
            }
          : item
      )

      latestSceneProgressRef.current = nextScenes
      setSceneProgress(nextScenes)

      await saveFlowSnapshot(phase, {
        ...(result || {}),
        __snapshotMeta: {
          direction,
          stepIndex,
          sceneProgress: nextScenes,
        },
      })

      alert(
        'El video fue enviado a FAL y se está procesando. Cuando termine, el webhook actualizará la escena.'
      )
    } catch (err: any) {
      console.error(err)
      alert(err?.message || 'Error preparando escena')
    } finally {
      setAnimatingSceneId(null)
    }
  }

  async function continueWithSelectedAvatar(avatar: {
    mode: 'existing_avatar' | 'digital_clone' | 'ai_generated'
    imageUrl: string
    portraitId?: number | null
    promptBase?: string | null
    metadata?: any
  }) {
    const nextResult = {
      ...(result || {}),
      avatar: {
        mode: avatar.mode,
        imageUrl: avatar.imageUrl,
        portraitId: avatar.portraitId || null,
        promptBase: avatar.promptBase || '',
        metadata: avatar.metadata || null,
      },

      // compatibilidad con tu flow actual
      characterImageUrl: avatar.imageUrl,
      characterId: avatar.portraitId || null,
      characterPrompt: avatar.promptBase || '',
    }

    hydratedSnapshotRef.current = false
    setResult(nextResult)

    const nextPhase = 'generating_scenes'
    setPhase(nextPhase)

    await saveFlowSnapshot(nextPhase, nextResult)
  }

  async function handleUseExistingAvatar(item: any) {
    if (!item?.image_url) return

    setSelectedPortraitId(Number(item.portrait_id))

    await continueWithSelectedAvatar({
      mode: 'existing_avatar',
      imageUrl: String(item.image_url),
      portraitId: Number(item.portrait_id),
      promptBase: item.prompt_base || '',
      metadata: item.metadata_json || null,
    })
  }

  useEffect(() => {
    setVoiceHistoryOpen(false)
    setVoiceTakes([])
    setSelectingVoiceAssetId(null)
  }, [selectedSceneId])

  useEffect(() => {
    if (phase !== 'creating_character') return
    void loadExistingAvatars()
  }, [phase])

  useEffect(() => {
    if (phase !== 'analyzing' || hasPlanned) return

    setHasPlanned(true)

    let cancelled = false

    async function run() {
        try {
        const res = await fetch('/api/task-flows/create-content/plan', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sellerId: request.payload?.sellerId,
              customerId: request.payload?.userId,
              payload: {
                ...(request.payload || {}),
                creativeDirection: direction,
                discovery: {
                  ...(request.payload?.discovery || {}),
                  ...discovery,
                  source: request.payload?.source || 'creator_studio_direct',
                },
              },
            })
        })

        const json = await res.json()

        if (!res.ok || !json?.ok) {
            throw new Error(json?.error || 'No se pudo planear el contenido')
        }

        if (cancelled) return

        const nextResult = {
            plan: json.plan,
            generationId: null,
            projectJson: null,
            creativeDirection: direction,
        }

        setResult(nextResult)
        setPhase('proposed_plan')

        await saveFlowSnapshot('proposed_plan', nextResult)
        } catch (err: any) {
        if (cancelled) return
        setError(err?.message || 'Error planeando contenido')
        }
    }

    run()

    return () => {
        cancelled = true
    }
  }, [phase, request.payload, direction, setPhase, setResult, setError])

  const didHydrateVideoStatusesRef = useRef<string | null>(null)

  useEffect(() => {
    if (!generationId) return
    if (!sceneProgress.length) return

    const key = `${generationId}:${sceneProgress.map((s: any) => s.sceneId).join(',')}`

    if (didHydrateVideoStatusesRef.current === key) return

    didHydrateVideoStatusesRef.current = key

    hydrateSceneVideoStatuses().catch((err) => {
      console.warn('[HYDRATE_SCENE_VIDEO_STATUSES_ERROR]', err)
    })
  }, [generationId, sceneProgress.length])


  useEffect(() => {
    const meta = result?.__snapshotMeta
    if (!meta) return
    if (hydratedSnapshotRef.current) return

    const hasMetaDirection =
        !!meta?.direction?.contentGoal ||
        !!meta?.direction?.useCharacter ||
        !!meta?.direction?.characterType ||
        !!meta?.direction?.hasProductImage ||
        !!meta?.direction?.publishTarget

    if (hasMetaDirection) {
        setDirection(meta.direction)
    } else if (result?.creativeDirection) {
        setDirection(result.creativeDirection)
    }

    if (typeof meta.stepIndex === 'number') {
        setStepIndex(meta.stepIndex)
    }

    if (Array.isArray(meta.sceneProgress) && meta.sceneProgress.length > 0) {
        setSceneProgress(meta.sceneProgress)
    }

    hydratedSnapshotRef.current = true
  }, [result])

  useEffect(() => {
    latestSceneProgressRef.current = Array.isArray(sceneProgress) ? sceneProgress : []
  }, [sceneProgress])

  useEffect(() => {
    if (
        phase !== 'generating_scenes' &&
        phase !== 'generating_avatar' &&
        phase !== 'result'
    ) {
        return
    }

    if ((sceneProgress || []).length > 0) return

    const plannerScenes = projectJson?.planner?.scenes
    if (!Array.isArray(plannerScenes) || plannerScenes.length === 0) return

    setSceneProgress(
      plannerScenes.map((s: any) =>
        mapSceneToRuntime(
          {
            ...s,
            status: s.image_url ? 'done' : 'generating',
          },
          null
        )
      )
    )
  }, [phase, projectJson, sceneProgress])

  useEffect(() => {
    const shouldPoll =
        (phase === 'generating_scenes' || phase === 'generating_avatar') &&
        !!generationId

    if (!shouldPoll) {
        if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
        }
        return
    }

    const stop = pollProject(generationId)

    return () => {
        stop?.()
    }
  }, [phase, generationId])

  function pollProject(generationId: number) {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    let cancelled = false

    const stop = () => {
      cancelled = true
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }

    const tick = async () => {
      if (cancelled) return

      try {
        const res = await fetch(`/api/creator-studio/video/project/${generationId}`, {
          cache: 'no-store',
        })

        const json = await res.json().catch(() => null)
        if (!json?.ok) return

        const runtimeScenes = Array.isArray(json?.scenes) ? json.scenes : []
        const plannerScenes =
          Array.isArray(json?.project?.project_json?.planner?.scenes)
            ? json.project.project_json.planner.scenes
            : []

        const projectJson = json?.project?.project_json || {}
        const voiceSelections = projectJson?.voice_selections || {}
        if (projectJson?.voice_settings) {
          setGlobalVoiceSettingsState(projectJson.voice_settings)
        }

        if (!runtimeScenes.length && !plannerScenes.length) return

        const scenes = runtimeScenes.map((runtime: any) => {
          const plannerMatch =
            plannerScenes.find(
              (p: any) =>
                Number(p?.scene_order || p?.sceneOrder) ===
                Number(runtime?.sceneOrder || runtime?.scene_order)
            ) || null

          return {
            ...plannerMatch,
            ...runtime,
            negative_prompt:
              runtime?.negative_prompt ??
              plannerMatch?.negative_prompt ??
              '',
            duration_sec:
              runtime?.duration_sec ??
              runtime?.durationSec ??
              plannerMatch?.duration_sec ??
              plannerMatch?.durationSec ??
              null,
            audio:
              runtime?.audio ??
              plannerMatch?.audio ??
              null,
          }
        })

        const prev = latestSceneProgressRef.current || []

        const nextScenes = scenes.map((s: any) => {
          const prevByOrder = prev.find(
            (p: any) => Number(p.sceneOrder) === Number(s.scene_order)
          )

          const sceneId = Number(s.scene_id)
          const serverImageUrl = s.image_url || null
          const prevImageUrl = prevByOrder?.imageUrl || null
          const isLocallyRegenerating = regeneratingSceneIdsRef.current.has(sceneId)

          const resolvedImageUrl = serverImageUrl || prevImageUrl || null

          const activeVoice =
            voiceSelections[String(sceneId)] ||
            voiceSelections[sceneId] ||
            null

          const activeVoiceUrl =
            activeVoice?.audioUrl ||
            activeVoice?.fileUrl ||
            null

          return mapSceneToRuntime(
            {
              ...s,
              image_url: resolvedImageUrl,

              voiceUrl:
                activeVoiceUrl ||
                s.voiceUrl ||
                s.voice_url ||
                s.metadata?.voice?.audio_url ||
                null,

              voiceStatus:
                activeVoiceUrl ||
                s.voiceUrl ||
                s.voice_url ||
                s.metadata?.voice?.audio_url
                  ? 'ready'
                  : s.voiceStatus || s.voice_status || 'pending',

              metadata: {
                ...(s.metadata || {}),
                voice: {
                  ...((s.metadata || {})?.voice || {}),
                  ...(activeVoiceUrl
                    ? {
                        audio_url: activeVoiceUrl,
                        active_asset_id: activeVoice?.assetId || null,
                        status: 'ready',
                        selected_voice: activeVoice?.selectedVoice || null,
                        resolved_voice:
                          activeVoice?.resolvedVoice ||
                          activeVoice?.voice ||
                          null,
                        performance_style: activeVoice?.performanceStyle || 'auto',
                        voice_pace: activeVoice?.voicePace || 'auto',
                        voice_intensity: activeVoice?.voiceIntensity || 'auto',
                        voice_emphasis: activeVoice?.voiceEmphasis || 'auto',
                        language_code: activeVoice?.languageCode || null,
                      }
                    : {}),
                },
              },

              status: isLocallyRegenerating
                ? 'generating'
                : resolvedImageUrl
                ? 'done'
                : 'pending',
            },
            prevByOrder
          )
        })

        const changed =
          JSON.stringify(
            nextScenes.map((s: any) => ({
              sceneId: s.sceneId,
              status: s.status,
              imageUrl: s.imageUrl,
              voiceUrl: s.voiceUrl,
            }))
          ) !==
          JSON.stringify(
            prev.map((s: any) => ({
              sceneId: s.sceneId,
              status: s.status,
              imageUrl: s.imageUrl,
              voiceUrl: s.voiceUrl,
            }))
          )

        if (changed) {
          latestSceneProgressRef.current = nextScenes
          setSceneProgress(nextScenes)
        }

        const done =
          nextScenes.length > 0 &&
          nextScenes.every((s: any) => !!s.imageUrl) &&
          regeneratingSceneIdsRef.current.size === 0

        if (done) {
          stop()

          setSceneProgress((prevScenes) =>
            (prevScenes || []).map((item: any) =>
              mapSceneToRuntime(
                {
                  ...item,
                  status: item.imageUrl ? 'done' : 'pending',
                },
                item
              )
            )
          )
        }
      } catch (err) {
        console.error('pollProject error', err)
      }
    }

    tick()
    pollIntervalRef.current = setInterval(tick, 2000)

    return stop
  }

  function mergeGeneratedSceneImages(
    baseScenes: any[],
    generatedScenes: Array<{ sceneId: number; imageUrl: string }>
  ) {
    if (!Array.isArray(baseScenes) || baseScenes.length === 0) return baseScenes
    if (!Array.isArray(generatedScenes) || generatedScenes.length === 0) return baseScenes

    return baseScenes.map((scene: any, index: number) => {
      const bySceneId = generatedScenes.find(
        (item) => Number(item.sceneId) === Number(scene.sceneId)
      )

      const fallbackByIndex = generatedScenes[index]
      const matched = bySceneId || fallbackByIndex

      return mapSceneToRuntime(
        {
          ...scene,
          sceneId: scene.sceneId || matched?.sceneId || null,
          imageUrl: matched?.imageUrl || scene.imageUrl || null,
          status: matched?.imageUrl || scene.imageUrl ? 'done' : 'generating',
        },
        scene
      )
    })
  }

  function inferCreatorProjectType() {
    const goal = String(
      result?.creativeDirection?.contentGoal ||
        direction.contentGoal ||
        plan?.goal ||
        ''
    ).toLowerCase()

    const publishTarget = String(
      result?.creativeDirection?.publishTarget ||
        direction.publishTarget ||
        ''
    ).toLowerCase()

    if (goal === 'ugc') return 'ugc_avatar_video'
    if (goal === 'product') return 'product_ad'
    if (publishTarget === 'youtube') return 'youtube_video'

    return 'content_pipeline'
  }

  async function createCreatorStudioProjectFromPlan(
    mode: 'quick' | 'advanced',
    approvedPlan?: any,
    approvedDirection?: DirectionState
  ) {
    if (!plan) {
      throw new Error('No hay plan para crear el proyecto.')
    }

    const existingProjectId = Number(
      result?.projectId ||
        request.payload?.projectId ||
        request.payload?.creatorStudioProject?.project_id ||
        0
    )

    if (existingProjectId) {
      
      const finalPlan = approvedPlan || buildApprovedPlan(plan, approvedDirection || direction)
      const creativeDirection = approvedDirection || result?.creativeDirection || direction

      const res = await fetch(`/api/creator-studio/projects/${existingProjectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mode,
          status: 'active',
          currentStep: mode === 'quick' ? 'express' : 'ideas',
          title: finalPlan.title || 'Nuevo proyecto creativo',
          objective: finalPlan.objective || '',
          brief: {
            ...(finalPlan || {}),
            title: finalPlan.title || 'Nuevo proyecto creativo',
            objective: finalPlan.objective || '',
            hook: finalPlan.hook || '',
            cta: finalPlan.cta || '',
            productName: finalPlan.productName || '',
            audience: finalPlan.audience || '',
            language: finalPlan.language || 'es',
            aspectRatio: finalPlan.aspectRatio || '9:16',
            durationMode: finalPlan.durationMode || creativeDirection?.durationMode || 'auto',
            durationTargetSec: Number(finalPlan.durationTargetSec || 60),
            productionMode: finalPlan.productionMode || creativeDirection?.productionMode || 'express',
            visualOutputMode: finalPlan.visualOutputMode || creativeDirection?.visualOutputMode || 'mixed',
            goal: finalPlan.goal || creativeDirection?.contentGoal || 'viral',
            style: finalPlan.style || '',
            musicMood: finalPlan.musicMood || '',
            voiceStyle: finalPlan.voiceStyle || '',
          },
          creativeDirection,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo actualizar el proyecto existente.')
      }

      setResult({
        ...(result || {}),
        projectId: existingProjectId,
        creatorStudioProject: json.project || request.payload?.creatorStudioProject || null,
      })

      return existingProjectId
    }

    const creativeDirection = result?.creativeDirection || direction
    const projectType = inferCreatorProjectType()

    const res = await fetch('/api/creator-studio/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        sellerId: request.payload?.sellerId,
        type: projectType,
        mode,
        title: plan.title || 'Nuevo proyecto creativo',
        objective: plan.objective || '',
        currentStep: mode === 'quick' ? 'express' : 'ideas',
        brief: {
          ...(plan || {}),
          title: plan.title || 'Nuevo proyecto creativo',
          objective: plan.objective || '',
          hook: plan.hook || '',
          cta: plan.cta || '',
          productName: plan.productName || '',
          audience: plan.audience || '',
          language: plan.language || 'es',
          aspectRatio: plan.aspectRatio || '9:16',
          durationTargetSec: Number(plan.durationTargetSec || 20),
          goal: plan.goal || creativeDirection?.contentGoal || 'viral',
          style: plan.style || '',
          musicMood: plan.musicMood || '',
          voiceStyle: plan.voiceStyle || '',
        },
        creativeDirection,
        projectJson: result?.projectJson || null,
        skillAgentId: request.payload?.skillAgentId || request.payload?.skill_agent_id || null,
        skillAgentSlug: request.payload?.skillAgentSlug || request.payload?.skill_agent_slug || null,
        skillAgentName: request.payload?.skillName || request.payload?.skillAgentName || request.payload?.skill_agent_name || null,
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok || !json?.projectId) {
      throw new Error(json?.error || 'No se pudo crear el proyecto en Creator Studio.')
    }

    const projectId = Number(json.projectId)

    const nextResult = {
      ...(result || {}),
      projectId,
      creatorStudioProject: json.project || null,
    }

    setResult(nextResult)

    return projectId
  }

  async function pollPremiumStoryboardJob(jobId: number) {
    let attempts = 0

    while (true) {
      attempts += 1

      const res = await fetch(`/api/creator-studio/jobs/${jobId}`, {
        method: 'GET',
        credentials: 'include',
      })

      const data = await res.json().catch(() => null)

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || 'Error consultando el storyboard')
      }

      const job = data.job || {}

      setProductionJobStatus(job.status || null)
      setProductionJobProgress(Number(job.progress || 0))
      setProductionJobStep(job.step || null)

      if (job.status === 'completed') {
        const generation = data.generation || null

        if (!generation?.generation_id || !generation?.result_json) {
          throw new Error('El storyboard terminó, pero no devolvió una generación válida')
        }

        return generation
      }

      if (job.status === 'failed') {
        throw new Error(job.errorMessage || 'No se pudo generar el storyboard premium')
      }

      if (job.status === 'cancelled') {
        throw new Error('La generación del storyboard fue cancelada')
      }

      const delay =
        attempts < 10
          ? 2000
          : attempts < 30
          ? 5000
          : 10000

      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  async function handleApprove() {
    if (!plan || busy) return

    setBusy(true)
    setStoryboardBusy(true)
    let projectId: number | null = null

    try {
        const creativeDirection = result?.creativeDirection || direction
        const approvedPlan = buildApprovedPlan(plan, creativeDirection)

        console.log('[handleApprove] creatorStudioMaterial', creatorStudioMaterial)
        console.log('[handleApprove] request.payload', request.payload)
        console.log('[handleApprove] result', result)

        const sourceScriptGenerationId = Number(
          creatorStudioMaterial?.scriptGenerationId || 0
        )

        if (!sourceScriptGenerationId) {
          throw new Error('No se encontró el ID de generación del guion premium')
        }

        projectId = await createCreatorStudioProjectFromPlan('quick', approvedPlan, creativeDirection)

        updateProductionStep('shell', 'active')
        
        const res = await fetch('/api/task-flows/create-content/project-json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
              sellerId: request.payload?.sellerId,
              customerId: request.payload?.userId,
              projectId,
              mode: 'production_shell_only',
              sourceScriptGenerationId,
              payload: {
                ...(request.payload || {}),
                projectId,
                durationMode: approvedPlan.durationMode,
                durationTargetSec: approvedPlan.durationTargetSec,
                productionMode: approvedPlan.productionMode,
                visualOutputMode: approvedPlan.visualOutputMode,
                sourceScriptGenerationId,
                scriptPackage: creatorStudioMaterial?.script,
                creativeDirection,
                productImageUrl: result?.productImageUrl || null,
                allowHyperframesScenes,
                forceRealScenes: !allowHyperframesScenes,
                disallowMotionGraphicsScenes: !allowHyperframesScenes,
                discovery: {
                  ...(request.payload?.discovery || {}),
                  ...discovery,
                  source: request.payload?.source || 'creator_studio_direct',
                },
              },
              plan: approvedPlan,
          }),
        })

        const json = await res.json()

        if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo crear el proyecto')
        }

        updateProductionStep('shell', 'done')
        updateProductionStep('storyboard_job', 'active')

        const storyboardJobRes = await fetch('/api/creator-studio/jobs/storyboard/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            inputs: {
              projectId,
              scriptGenerationId: sourceScriptGenerationId,
              productionGenerationId: json.generationId,

              allowHyperframesScenes,
              forceRealScenes: !allowHyperframesScenes,
              disallowMotionGraphicsScenes: !allowHyperframesScenes,
            },
          }),
        })

        const storyboardJobJson = await storyboardJobRes.json().catch(() => null)

        if (!storyboardJobRes.ok || !storyboardJobJson?.ok) {
          throw new Error(storyboardJobJson?.error || 'No se pudo crear el job de storyboard')
        }

        const storyboardJobId = Number(storyboardJobJson.jobId || 0)

        if (!storyboardJobId) {
          throw new Error('No se recibió jobId del storyboard')
        }

        setProductionJobId(storyboardJobId)
        setProductionJobStatus(storyboardJobJson.status || 'queued')
        setProductionJobProgress(Number(storyboardJobJson.progress || 0))
        setProductionJobStep(storyboardJobJson.step || 'Storyboard en cola')

        const premiumStoryboardGeneration = await pollPremiumStoryboardJob(storyboardJobId)

        updateProductionStep('storyboard_job', 'done')
        updateProductionStep('scenes', 'active')

        const premiumScenes =
          premiumStoryboardGeneration?.result_json?.scenes ||
          premiumStoryboardGeneration?.resultJson?.scenes ||
          []

        const scenes = Array.isArray(premiumScenes) && premiumScenes.length > 0
          ? premiumScenes
          : json?.projectJson?.planner?.scenes || []
        const goal = String(json?.projectJson?.brief?.goal || approvedPlan?.goal || '').toLowerCase()
        const needsAvatar = goal === 'ugc'
        const needsProductUpload =
        (result?.creativeDirection?.hasProductImage || direction.hasProductImage) === 'yes'

        const nextResult = {
          ...(result || {}),
          plan: approvedPlan,
          projectId,
          generationId: json.generationId,
          projectJson: json.projectJson,
          premiumStoryboardGeneration,
          preparedStoryboardPlan: premiumStoryboardGeneration?.result_json || null,
          sourceScriptGenerationId,
        }

        hydratedSnapshotRef.current = false

        setResult(nextResult)

        setSceneProgress(
          scenes.map((s: any) =>
            mapSceneToRuntime(
              {
                ...s,
                status: 'pending',
                image_url: null,
              },
              null
            )
          )
        )

        const nextPhase = needsProductUpload
            ? 'product_input'
            : needsAvatar
            ? 'creating_character'
            : 'generating_scenes'

        setPhase(nextPhase)

        await saveFlowSnapshot(nextPhase, nextResult)

        if (!needsProductUpload && !needsAvatar) {
            const storyboardRes = await fetch('/api/creator-studio/video/storyboard/generate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                generationId: json.generationId,
                customerId: request.payload?.userId,
                sellerId: request.payload?.sellerId,
                brief: {
                  ...json.projectJson?.brief,
                  initialImage: null,
                },
                preparedPlan: premiumStoryboardGeneration?.result_json,
                skipFal: !generateStoryboardImages,
                allowHyperframesScenes,
                forceRealScenes: !allowHyperframesScenes,
                disallowMotionGraphicsScenes: !allowHyperframesScenes,
                notifications: {
                  channels: ['dashboard'],
                  requireApproval: true,
                },
              }),
            })

            const storyboardJson = await storyboardRes.json().catch(() => null)

            if (!storyboardRes.ok || !storyboardJson?.ok) {
              throw new Error(storyboardJson?.error || 'Error preparando storyboard')
            }

            updateProductionStep('scenes', 'done')
            updateProductionStep('review', 'done')

            if (Array.isArray(storyboardJson?.scenes) && storyboardJson.scenes.length > 0) {
                setSceneProgress((prev) =>
                    mergeGeneratedSceneImages(prev, storyboardJson.scenes)
                )
            }
        }

    } catch (err: any) {
        console.error('[handleApprove] error', err)

        setError(
          err?.message ||
          err?.error ||
          'Error creando proyecto'
        )

        setPhase('error')
    } finally {
        setBusy(false)
        setStoryboardBusy(false)
    }
  }

  async function handleContinueInCreatorStudio() {
    if (!plan || busy) return

    setBusy(true)

    try {
      const creativeDirection = result?.creativeDirection || direction
      const approvedPlan = buildApprovedPlan(plan, creativeDirection)

      const projectId = await createCreatorStudioProjectFromPlan(
        'advanced',
        approvedPlan,
        creativeDirection
      )

      const nextResult = {
        ...(result || {}),
        plan: approvedPlan,
        projectId,
        creativeDirection,
      }

      setResult(nextResult)

      await saveFlowSnapshot('completed', nextResult)

      const params = new URLSearchParams()
      params.set('projectId', String(projectId))
      params.set('tab', 'ideas')
      params.set('autoGenerate', 'true')
      params.set('fromFlow', 'create_content')

      window.location.href = `/creator-studio?${params.toString()}`
    } catch (err: any) {
      setError(err?.message || 'Error abriendo Creator Studio')
      setPhase('error')
    } finally {
      setBusy(false)
    }
  }

  function handleRegenerate() {
    if (busy) return
    hydratedSnapshotRef.current = false
    setHasPlanned(false)
    setResult(null)
    setPhase('analyzing')
  }

  function nextStep() {
    if (stepIndex === 0 && !direction.contentGoal) return
    if (stepIndex === 1 && !direction.publishTarget) return
    if (stepIndex === 2 && !direction.durationMode) return
    if (stepIndex === 3 && !direction.hasProductImage) return

    if (stepIndex === 4 && shouldAskCharacter) {
      if (!direction.useCharacter) return
      if (direction.useCharacter === 'yes' && !direction.characterType) return
      if (direction.useCharacter === 'no' && direction.characterType !== 'none') return
      if (direction.useCharacter === 'auto' && direction.characterType !== 'auto') return
    }

    setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1))
  }

  function prevStep() {
    setStepIndex((prev) => Math.max(prev - 1, 0))
  }

  function ProductionProgressCard({
    productionSteps,
    productionJobStatus,
    productionJobProgress,
    productionJobStep,
  }: {
    productionSteps: Array<{
      id: string
      label: string
      description: string
      status: 'pending' | 'active' | 'done' | 'error'
    }>
    productionJobStatus: string | null
    productionJobProgress: number
    productionJobStep: string | null
  }) {
    return (
      <div className="space-y-5">
        <div className="rounded-3xl border border-primary/20 bg-card/80 p-5 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                Producción en proceso
              </p>

              <h3 className="mt-2 text-xl font-bold text-foreground">
                Estamos preparando tu storyboard premium
              </h3>

              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Estamos convirtiendo tu guion, estructura narrativa y biblias visuales en escenas listas para revisión.
              </p>
            </div>

            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          </div>

          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>{productionJobStep || 'Preparando producción...'}</span>
              <span>{Math.max(0, Math.min(100, productionJobProgress || 0))}%</span>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-primary/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary via-blue-500 to-violet-500 transition-all duration-700"
                style={{
                  width: `${Math.max(5, Math.min(100, productionJobProgress || 0))}%`,
                }}
              />
            </div>
          </div>

          <div className="space-y-3">
            {productionSteps.map((step) => (
              <div
                key={step.id}
                className="flex gap-3 rounded-2xl border border-border bg-background/70 p-3"
              >
                <div
                  className={[
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px]',
                    step.status === 'done'
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : '',
                    step.status === 'active'
                      ? 'border-primary bg-primary/10 text-primary'
                      : '',
                    step.status === 'pending'
                      ? 'border-border bg-muted text-muted-foreground'
                      : '',
                    step.status === 'error'
                      ? 'border-destructive bg-destructive/10 text-destructive'
                      : '',
                  ].join(' ')}
                >
                  {step.status === 'done' ? (
                    '✓'
                  ) : step.status === 'active' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    '•'
                  )}
                </div>

                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {step.label}
                  </div>
                  <div className="mt-0.5 text-xs leading-5 text-muted-foreground">
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {productionJobStatus === 'queued' && (
            <div className="mt-4 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-3 text-xs leading-5 text-amber-700 dark:text-amber-300">
              Estamos teniendo una alta demanda de solicitudes. Tu storyboard está en cola y aparecerá automáticamente cuando esté listo.
            </div>
          )}
        </div>
      </div>
    )
  }

  function buildApprovedPlan(basePlan: any, creativeDirection: DirectionState) {
    const durationTargetSec =
      Number(creativeDirection.durationTargetSec || basePlan?.durationTargetSec || 60)

    const productionMode =
      creativeDirection.productionMode ||
      (durationTargetSec >= 300 ? 'cinematic_longform' : 'express')

    const visualOutputMode =
      creativeDirection.visualOutputMode ||
      (durationTargetSec >= 300 ? 'static_images' : 'mixed')

    return {
      ...(basePlan || {}),
      durationMode: creativeDirection.durationMode || basePlan?.durationMode || 'auto',
      durationTargetSec,
      productionMode,
      visualOutputMode,
    }
  }

  function startPlanning() {
    if (!canContinueDirection) return

    hydratedSnapshotRef.current = false

    setResult({
      ...(result || {}),
      creativeDirection: direction,
    })

    setHasPlanned(false)

    if (!hasDiscoveryContext(request.payload, discovery)) {
      setPhase('discovery')
      return
    }

    setPhase('analyzing')
  }

  const needsAvatar = projectJson?.brief?.goal === 'ugc'

  /*
  PARA PRUEBAS
  */

  /*
  useEffect(() => {
  const debugGenerationId = 38

  if (!result?.generationId) {
    setResult({
      ...(result || {}),
      generationId: debugGenerationId,
      projectJson: {
        brief: { goal: 'product', durationTargetSec: 20 },
        planner: { scenes: [] },
      },
    })

    setPhase('creating_character')
  }
}, [])
*/

  const handleCloseFlow = useCallback(async () => {
    try {
        if (snapshotId) {
        await fetch('/api/task-flows/create-content/abandon', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ snapshotId }),
        })
        }
    } catch (err) {
        console.error('abandon snapshot error', err)
    } finally {
        setShowCloseConfirm(false)
        resetTaskFlow()
    }
  }, [snapshotId, resetTaskFlow])

  const selectedSceneIndex = useMemo(() => {
    if (!selectedSceneId) return -1
    return sceneProgress.findIndex((s: any) => Number(s.sceneId) === Number(selectedSceneId))
  }, [sceneProgress, selectedSceneId])

  function normalizeText(value: any) {
    return String(value || '').replace(/\r\n/g, '\n').trim()
  }

  useEffect(() => {
    if (!selectedScene) return

    setSceneDraft({
      prompt: String(selectedScene.prompt || ''),
      negativePrompt: String(selectedScene.negativePrompt || ''),
      overlayText: String(selectedScene.overlayText || ''),
      dialogueText: String(selectedScene.dialogueText || ''),
      speaker: String(selectedScene.speaker || 'expert'),

      audioMode: (selectedScene.audioMode || 'voiceover') as
        | 'dialogue'
        | 'voiceover'
        | 'silent',

      // Legacy fields: los dejamos por compatibilidad, aunque ya no se muestren igual en UI.
      expressionLevel: (selectedScene.expressionLevel || 'natural') as
        | 'natural'
        | 'expressive',
      emotionTag: String(selectedScene.emotionTag || ''),

      // Nuevo flujo Gemini/FAL TTS
      voiceChoice: String(
        selectedScene.voiceChoice ||
          selectedScene.voice_choice ||
          selectedScene.metadata?.voice?.selected_voice ||
          selectedScene.metadata?.voice?.resolved_voice ||
          'auto'
      ),

      voiceGenderPreference:
        (selectedScene.voiceGenderPreference ||
          selectedScene.voice_gender_preference ||
          selectedScene.metadata?.voice?.voice_gender_preference ||
          'auto') as 'auto' | 'female' | 'male',

      performanceStyle: String(
        selectedScene.performanceStyle ||
          selectedScene.performance_style ||
          selectedScene.metadata?.voice?.performance_style ||
          'auto'
      ) as
        | 'auto'
        | 'natural'
        | 'cinematic'
        | 'ugc'
        | 'commercial'
        | 'mentor'
        | 'emotional',

      voicePace: String(
        selectedScene.voicePace ||
          selectedScene.voice_pace ||
          selectedScene.metadata?.voice?.voicePace ||
          selectedScene.metadata?.voice?.voice_pace ||
          'auto'
      ) as 'auto' | 'slightly_faster' | 'faster' | 'slower',

      voiceIntensity: String(
        selectedScene.voiceIntensity ||
          selectedScene.voice_intensity ||
          selectedScene.metadata?.voice?.voiceIntensity ||
          selectedScene.metadata?.voice?.voice_intensity ||
          'auto'
      ) as 'auto' | 'restrained' | 'medium' | 'high',

      voiceEmphasis: String(
        selectedScene.voiceEmphasis ||
          selectedScene.voice_emphasis ||
          selectedScene.metadata?.voice?.voiceEmphasis ||
          selectedScene.metadata?.voice?.voice_emphasis ||
          'auto'
      ) as 'auto' | 'natural' | 'cinematic' | 'dramatic',

      lockVoiceSettings:
        Boolean(
          selectedScene.lockVoiceSettings ??
            selectedScene.lock_voice_settings ??
            selectedScene.metadata?.voice?.lockVoiceSettings ??
            selectedScene.metadata?.voice?.lock_voice_settings ??
            false
        ),

      enhanceVoiceWithAI:
        selectedScene.enhanceVoiceWithAI ??
        selectedScene.enhance_voice_with_ai ??
        selectedScene.metadata?.voice?.enhance_voice_with_ai ??
        true,

      languageCode: String(
        selectedScene.languageCode ||
          selectedScene.language_code ||
          selectedScene.metadata?.voice?.language_code ||
          ''
      ),
    })
  }, [selectedSceneId, selectedScene])

  const hasScenePromptChanges = useMemo(() => {
    if (!selectedScene || !sceneDraft) return false

    return (
      normalizeText(sceneDraft.prompt) !== normalizeText(selectedScene.prompt) ||
      normalizeText(sceneDraft.negativePrompt) !== normalizeText(selectedScene.negativePrompt)
    )
  }, [sceneDraft, selectedScene])

  function goToPrevScene() {
    if (selectedSceneIndex <= 0) return
    const prevScene = sceneProgress[selectedSceneIndex - 1]
    if (prevScene?.sceneId) setSelectedSceneId(Number(prevScene.sceneId))
  }

  function goToNextScene() {
    if (selectedSceneIndex < 0 || selectedSceneIndex >= sceneProgress.length - 1) return
    const nextScene = sceneProgress[selectedSceneIndex + 1]
    if (nextScene?.sceneId) setSelectedSceneId(Number(nextScene.sceneId))
  }

  /**
   * Opción rápida.
   * Si quieres el número exacto, mejor devuélvelo desde backend.
   */
  function estimateSceneRegenCredits(scene: any) {
    const meta = scene?.metadata || scene?.metadata_json || {}
    const usesRefs = !!meta?.use_person_reference || !!meta?.use_product_reference
    return usesRefs ? 12 : 8
  }

  const selectedSceneNeedsImage = selectedScene ? sceneNeedsImageFrame(selectedScene) : false

  const canGenerateOrRegenerateFrame =
    !!selectedScene &&
    selectedSceneNeedsImage &&
    (!selectedScene.imageUrl || hasScenePromptChanges)

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-none border border-border bg-background text-foreground md:rounded-[32px]">
      <div className="shrink-0 border-b border-border/70 px-6 py-5">
        <div className="text-[11px] uppercase tracking-[0.28em] text-foreground/60">
          {phase === 'creating_character' ? 'Avatar' : 'Samantha Content Runtime'}
        </div>
        <div className="mt-2 text-2xl font-semibold">
          {phase === 'creating_character'
            ? 'Elige el avatar para tu video'
            : request.title}
        </div>
        {request.subtitle ? (
          <div className="mt-2 text-sm text-muted-foreground">
            {phase === 'creating_character'
              ? 'Puedes usar un avatar existente, crear tu clon digital o generar uno nuevo con IA usando el brief, audiencia y estilo de este proyecto.'
              : request.subtitle}
          </div>
        ) : null}
      </div>

      <div 
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 pb-10"
        style={{ WebkitOverflowScrolling: 'touch' }}>
        {phase === 'creative_direction' && (
            <div className="flex min-h-[70vh] flex-col">
                <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-foreground/60">
                    Creative Direction
                    </div>
                    <div className="font-mono text-[11px] text-primary/60">
                    {stepIndex + 1}/{totalSteps}
                    </div>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-cyan-500/10">
                    <div
                    className="h-full rounded-full bg-gradient-to-r from-primary via-blue-500 to-violet-500 transition-all duration-300"
                    style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
                    />
                </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-1">
                {stepIndex === 0 && (
                    <FlowStepCard
                    eyebrow="Objetivo"
                    title="¿Qué tipo de pieza quieres crear?"
                    subtitle="Samantha puede optimizar el contenido para viralidad, producto, storytelling, UGC o elegir la mejor dirección por ti."
                    >
                    <OptionsGrid
                        options={[
                        { id: 'viral', label: 'Viral', hint: 'Impacto y scroll-stop' },
                        { id: 'product', label: 'Producto', hint: 'Vender y explicar mecanismo' },
                        { id: 'storytelling', label: 'Storytelling', hint: 'Narrativa y transformación' },
                        { id: 'ugc', label: 'UGC', hint: 'Más humano y cercano' },
                        { id: 'auto', label: 'Que Samantha elija', hint: 'Menos fricción, más inteligencia' },
                        ]}
                        value={direction.contentGoal}
                        onChange={(value) =>
                        setDirection((prev) => ({
                            ...prev,
                            contentGoal: value as any,
                            useCharacter:
                            value === 'ugc'
                                ? 'yes'
                                : value === 'auto'
                                ? 'auto'
                                : '',
                            characterType:
                            value === 'ugc'
                                ? 'expert'
                                : value === 'auto'
                                ? 'auto'
                                : '',
                        }))
                        }
                    />
                    </FlowStepCard>
                )}

                {stepIndex === 1 && (
                <FlowStepCard
                    eyebrow="Plataforma"
                    title="¿Dónde publicarás primero esta pieza?"
                    subtitle="Samantha usará esto para optimizar automáticamente el formato visual del contenido."
                >
                    <OptionsGrid
                    options={[
                        { id: 'shorts', label: 'Reels / TikTok / Shorts', hint: 'Vertical y social-first' },
                        { id: 'feed', label: 'Feed', hint: 'Instagram / Facebook feed' },
                        { id: 'youtube', label: 'YouTube / Web', hint: 'Horizontal y más cinematográfico' },
                        { id: 'auto', label: 'Que Samantha decida', hint: 'Usar la mejor opción según el objetivo' },
                    ]}
                    value={direction.publishTarget}
                    onChange={(value) =>
                      setDirection((prev) => ({
                        ...prev,
                        publishTarget: value as any,
                        durationMode: '',
                        durationTargetSec: undefined,
                        productionMode: undefined,
                        visualOutputMode: undefined,
                      }))
                    }
                    />
                </FlowStepCard>
                )}

                {stepIndex === 2 && (
                  <FlowStepCard
                    eyebrow="Duración"
                    title="¿Qué duración tendrá esta pieza?"
                    subtitle="Samantha usará esto para decidir si conviene un video express, una pieza media o una producción cinematográfica larga."
                  >
                    <OptionsGrid
                      options={getDurationOptions(direction.publishTarget).map((option) => ({
                        id: option.id,
                        label: option.label,
                        hint: option.hint,
                      }))}
                      value={direction.durationMode}
                      onChange={(value) => {
                        const selected = getDurationOptions(direction.publishTarget).find(
                          (option) => option.id === value
                        )

                        setDirection((prev) => ({
                          ...prev,
                          durationMode: value as any,
                          durationTargetSec: selected?.durationTargetSec || 60,
                          productionMode: selected?.productionMode as any,
                          visualOutputMode: selected?.visualOutputMode as any,
                        }))
                      }}
                    />
                  </FlowStepCard>
                )}

                {stepIndex === 3 && (
                    <FlowStepCard
                    eyebrow="Producto"
                    title="¿Quieres incluir una imagen real de producto?"
                    subtitle="Si la tienes, el siguiente paso será usarla como referencia visual para mantener branding, forma y presencia real en las escenas."
                    >
                    <OptionsGrid
                        options={[
                        { id: 'yes', label: 'Sí, ya la tengo', hint: 'Usar referencia real' },
                        { id: 'no', label: 'Aún no', hint: 'Seguir sin imagen por ahora' },
                        ]}
                        value={direction.hasProductImage}
                        onChange={(value) =>
                        setDirection((prev) => ({ ...prev, hasProductImage: value as any }))
                        }
                    />
                    </FlowStepCard>
                )}

                {stepIndex === 4 && shouldAskCharacter && (
                    <FlowStepCard
                    eyebrow="Personaje"
                    title="¿Cómo quieres manejar la presencia humana?"
                    subtitle="Solo te pregunto esto porque puede cambiar el lenguaje visual, la cámara y la estructura de las escenas."
                    >
                    <div className="space-y-5">
                        <OptionsRow
                        options={[
                            { id: 'yes', label: 'Usar personaje' },
                            { id: 'no', label: 'Solo producto / visuales' },
                            { id: 'auto', label: 'Que Samantha recomiende' },
                        ]}
                        value={direction.useCharacter}
                        onChange={(value) =>
                            setDirection((prev) => ({
                            ...prev,
                            useCharacter: value as any,
                            characterType:
                                value === 'no'
                                ? 'none'
                                : value === 'auto'
                                ? 'auto'
                                : '',
                            }))
                        }
                        />

                        {direction.useCharacter === 'yes' && (
                        <OptionsGrid
                          options={[
                            { id: 'expert', label: 'Experto / guía', hint: 'Autoridad y claridad' },
                            { id: 'creator', label: 'Creador / storyteller', hint: 'Más personal y narrativo' },
                            { id: 'founder', label: 'Fundador / marca personal', hint: 'Visión, negocio y confianza' },
                            { id: 'customer', label: 'Cliente / testimonio', hint: 'Prueba social y transformación' },
                            { id: 'narrator', label: 'Narrador', hint: 'Voz conductora y presencia editorial' },
                            { id: 'avatar', label: 'Avatar IA', hint: 'Personaje digital consistente' },
                          ]}
                          value={direction.characterType}
                          onChange={(value) =>
                            setDirection((prev) => ({ ...prev, characterType: value as any }))
                          }
                        />
                        )}
                    </div>
                    </FlowStepCard>
                )}
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border/70 pt-4">
                <button
                    type="button"
                    onClick={prevStep}
                    disabled={stepIndex === 0}
                    className="rounded-2xl border border-primary/20 bg-primary/10 px-5 py-3 text-sm text-primary disabled:opacity-35"
                >
                    Atrás
                </button>

                {stepIndex < totalSteps - 1 ? (
                    <button
                    type="button"
                    onClick={nextStep}
                    disabled={
                    (stepIndex === 0 && !direction.contentGoal) ||
                    (stepIndex === 1 && !direction.publishTarget) ||
                    (stepIndex === 2 && !direction.durationMode) ||
                    (stepIndex === 3 && !direction.hasProductImage) ||
                    (stepIndex === 4 &&
                        shouldAskCharacter &&
                        (!direction.useCharacter ||
                        (direction.useCharacter === 'yes' && !direction.characterType) ||
                        (direction.useCharacter === 'no' && direction.characterType !== 'none') ||
                        (direction.useCharacter === 'auto' && direction.characterType !== 'auto')))
                    }
                    className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40"
                    >
                    Continuar
                    </button>
                ) : (
                    <button
                    type="button"
                    onClick={startPlanning}
                    disabled={!canContinueDirection}
                    className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40"
                    >
                    Continuar con Samantha
                    </button>
                )}
                </div>
            </div>
        )}

        {phase === 'discovery' && (
          <div className="space-y-5">
            <div>
              <div className="text-sm font-semibold text-primary">
                Brief rápido
              </div>
              <h2 className="mt-2 text-2xl font-semibold text-foreground">
                ¿De qué quieres que trate este contenido?
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Dame estos datos para evitar ideas genéricas y crear un plan con intención real.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setPhase('niche-finder')}
              className="group relative overflow-hidden rounded-3xl border border-primary/20 bg-primary/5 p-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-primary/10 hover:shadow-lg hover:shadow-primary/10"
            >
              <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-primary/20 blur-2xl transition-all duration-500 group-hover:scale-125" />

              <div className="relative flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                  <Sparkles className="h-5 w-5 animate-pulse" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      Descubrir oportunidad viral
                    </p>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                      Recomendado
                    </span>
                  </div>

                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    Analiza qué está funcionando ahora en tu nicho antes de crear ideas, hooks y guiones.
                  </p>

                  <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-primary">
                    <TrendingUp className="h-4 w-4" />
                    Usar Niche / Idea Finder
                  </div>
                </div>
              </div>
            </button>

            <div className="grid gap-4">
              <label className="space-y-2">
                <span className="text-sm font-medium text-foreground">
                  Tema, nicho o idea principal
                </span>
                <textarea
                  value={discovery.topic}
                  onChange={(e) =>
                    setDiscovery((prev) => ({ ...prev, topic: e.target.value }))
                  }
                  placeholder="Ej. cómo crear videos y monetizar en redes sociales con un Avatar Digital"
                  className="min-h-[96px] w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-medium text-foreground">
                  Objetivo principal
                </span>
                <select
                  value={discovery.objective}
                  onChange={(e) =>
                    setDiscovery((prev) => ({ ...prev, objective: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                >
                  <option value="">Selecciona un objetivo</option>
                  <option value="vistas">Vistas</option>
                  <option value="leads">Leads</option>
                  <option value="ventas">Ventas</option>
                  <option value="autoridad">Autoridad</option>
                  <option value="comunidad">Comunidad</option>
                </select>
              </label>

              <label className="space-y-2">
                <span className="text-sm font-medium text-foreground">
                  Audiencia específica
                </span>
                <textarea
                  value={discovery.audience}
                  onChange={(e) =>
                    setDiscovery((prev) => ({ ...prev, audience: e.target.value }))
                  }
                  placeholder="Ej. jóvenes emprendedores, creadores de contenido y personas que quieren aprender IA"
                  className="min-h-[80px] w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-medium text-foreground">
                  ¿Qué quieres que hagan al final?
                </span>
                <input
                  value={discovery.desiredAction}
                  onChange={(e) =>
                    setDiscovery((prev) => ({ ...prev, desiredAction: e.target.value }))
                  }
                  placeholder="Ej. seguirme, comentar CLON, registrarse, comprar, pedir más info"
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
                />
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPhase('product')}
                className="rounded-2xl border border-border px-5 py-3 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                Atrás
              </button>

              <button
                type="button"
                disabled={!discovery.topic.trim() || !discovery.audience.trim()}
                onClick={() => setPhase('analyzing')}
                className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40"
              >
                Generar dirección
              </button>
            </div>
          </div>
        )}

        {phase === 'analyzing' && (
        <div className="flex h-[60vh] flex-col items-center justify-center text-center">
            
            <div className="relative mb-6">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />

            <div className="absolute inset-0 blur-xl opacity-40">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
            </div>

            <div className="text-lg font-semibold">
            Samantha está construyendo tu proyecto...
            </div>

            <div className="mt-2 text-sm text-muted-foreground max-w-md">
            Analizando producto, contexto, intención y estructura narrativa para generar algo que realmente funcione.
            </div>
        </div>
        )}

        {phase === 'proposed_plan' && plan && (
          <div className="space-y-5">
            <div className="rounded-3xl border border-border/70 bg-primary/[0.04] p-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-foreground/60">
                Approved direction preview
              </div>

              <div className="mt-3 text-2xl font-semibold">{plan.title}</div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Info label="Goal" value={result?.creativeDirection?.contentGoal || '—'} />
                <Info label="Objective" value={plan.objective} />
                <Info label="Hook" value={plan.hook} />
                <Info label="Audience" value={plan.audience} />
                <Info label="Product" value={plan.productName} />
                <Info label="Voice Style" value={plan.voiceStyle} />
                <Info label="Platform" value={result?.creativeDirection?.publishTarget || '—'} />
              </div>

              <div className="mt-5 text-sm leading-relaxed text-muted-foreground">
                {plan.reasoning}
              </div>
            </div>

            {isCreatorStudioVideoProject ? (
              busy ? (
                <ProductionProgressCard
                  productionSteps={productionSteps}
                  productionJobStatus={productionJobStatus}
                  productionJobProgress={productionJobProgress}
                  productionJobStep={productionJobStep}
                />
              ) : (
                <div className="space-y-5">
                  <div className="rounded-3xl border border-border bg-card/80 p-5 shadow-sm">
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                        Producción lista
                      </p>

                      <h3 className="text-xl font-bold text-foreground">
                        Vamos a convertir tu guion en un plan de producción
                      </h3>

                      <p className="text-sm leading-6 text-muted-foreground">
                        Ya utilizamos tu idea, hook y guion generados en Creator Studio para
                        preparar la producción. El siguiente paso es crear o seleccionar el
                        talento/personaje, definir assets y generar las escenas.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-border bg-card/70 p-4">
                    <p className="mb-3 text-sm font-semibold text-foreground">
                      Utilizaremos este material generado
                    </p>

                    <div className="space-y-2">
                      <details className="group rounded-2xl border border-border bg-background/70 p-3">
                        <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground">
                          <span>Idea seleccionada</span>
                          <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-500">
                            Lista
                          </span>
                        </summary>

                        <div className="mt-3 text-sm leading-6 text-muted-foreground">
                          <p className="font-medium text-foreground">
                            {creatorStudioMaterial.idea?.title || 'Idea del proyecto'}
                          </p>
                          {creatorStudioMaterial.idea?.description ? (
                            <p className="mt-1">{creatorStudioMaterial.idea.description}</p>
                          ) : null}
                        </div>
                      </details>

                      <details className="group rounded-2xl border border-border bg-background/70 p-3">
                        <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground">
                          <span>Hook seleccionado</span>
                          <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-500">
                            Listo
                          </span>
                        </summary>

                        <div className="mt-3 text-sm leading-6 text-muted-foreground">
                          {creatorStudioMaterial.hook?.text ||
                            creatorStudioMaterial.hook?.hook ||
                            plan?.hook ||
                            'Hook del proyecto'}
                        </div>
                      </details>

                      <details className="group rounded-2xl border border-border bg-background/70 p-3">
                        <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground">
                          <span>Guion generado</span>
                          <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-500">
                            Listo
                          </span>
                        </summary>

                        <div className="mt-3 max-h-56 overflow-y-auto whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                          {creatorStudioMaterial.script?.script ||
                            plan?.script ||
                            'Guion listo para producción.'}
                        </div>
                      </details>
                    </div>
                  </div>

                  <div className="mb-4 space-y-3">
                    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-border/70 bg-muted/30 p-4">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Generar imágenes del storyboard
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          Si está activo, se generarán imágenes con IA para las escenas. Si lo apagas,
                          solo se preparará el storyboard sin generar imágenes.
                        </p>
                      </div>

                      <input
                        type="checkbox"
                        checked={generateStoryboardImages}
                        onChange={(event) => setGenerateStoryboardImages(event.target.checked)}
                        disabled={busy}
                        className="mt-1 h-5 w-5 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                      />
                    </label>

                    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-border/70 bg-muted/30 p-4">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Permitir escenas con HyperFrames
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          Si lo apagas, el planner evitará escenas de motion graphics y tratará todas las escenas como frames reales generables.
                        </p>
                      </div>

                      <input
                        type="checkbox"
                        checked={allowHyperframesScenes}
                        onChange={(event) => setAllowHyperframesScenes(event.target.checked)}
                        disabled={busy}
                        className="mt-1 h-5 w-5 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={() => void handleApprove()}
                    disabled={busy}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy ? 'Preparando producción...' : 'Continuar producción'}
                  </button>
                </div>
              )
            ) : (
              <>
                <div className="space-y-4">
                  <div className="rounded-3xl border border-primary/20 bg-primary/10 p-5">
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
                        <Sparkles className="h-5 w-5" />
                      </div>

                      <div>
                        <div className="text-base font-semibold text-foreground">
                          Ya tengo la dirección creativa del proyecto.
                        </div>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Ahora puedo avanzar de dos formas: elegir por ti la mejor ruta o mostrarte varias opciones para que tú decidas.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={busy}
                      className="group rounded-3xl border border-primary/40 bg-primary p-5 text-left text-primary-foreground shadow-lg shadow-cyan-400/10 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-slate-950/10">
                          {busy ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5" />
                          )}
                        </div>

                        <span className="rounded-full bg-slate-950/10 px-3 py-1 text-xs font-bold">
                          Express
                        </span>
                      </div>

                      <div className="text-lg font-bold">
                        Samantha elige lo mejor
                      </div>

                      <p className="mt-2 text-sm leading-6 text-white/75">
                        Dejo que Samantha elija la idea, el hook, el guion y la estructura para avanzar más rápido.
                      </p>

                      <div className="mt-4 text-sm font-bold">
                        {busy ? 'Creando proyecto…' : 'Continuar con Samantha'}
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={handleContinueInCreatorStudio}
                      disabled={busy}
                      className="group rounded-3xl border border-primary/20 bg-background p-5 text-left shadow-lg shadow-black/10 transition-all hover:-translate-y-0.5 hover:border-primary/40 disabled:opacity-50"
                    >
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary">
                          {busy ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <PenSquare className="h-5 w-5" />
                          )}
                        </div>

                        <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                          Más control
                        </span>
                      </div>

                      <div className="text-lg font-bold text-foreground">
                        Quiero generar varias opciones
                      </div>

                      <p className="mt-2 text-sm leading-6 text-foreground/65">
                        Samantha generará ideas, hooks y guiones para que elijas las mejores versiones dentro de Creator Studio.
                      </p>

                      <div className="mt-4 text-sm font-bold text-primary">
                        Abrir Creator Studio
                      </div>
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleRegenerate}
                    disabled={busy}
                    className="rounded-2xl border border-primary/20 bg-primary/10 px-5 py-3 text-sm text-primary disabled:opacity-50"
                  >
                    Regenerar enfoque
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {phase === 'product_input' && generationId && (
            <ProductInputStep
                request={request}
                generationId={generationId}
                onContinue={async (fileUrl) => {
                    const productImageUrl =
                      typeof window !== 'undefined'
                        ? new URL(fileUrl, window.location.origin).toString()
                        : fileUrl

                    const nextResult = {
                      ...(result || {}),
                      productImageUrl,
                      preparedStoryboardPlan:
                        result?.preparedStoryboardPlan ||
                        result?.premiumStoryboardGeneration?.result_json ||
                        null,
                    }

                    setResult(nextResult)

                    const needsAvatar = projectJson?.brief?.goal === 'ugc'
                    const nextPhase = needsAvatar ? 'creating_character' : 'generating_scenes'

                    setPhase(nextPhase)

                    await saveFlowSnapshot(nextPhase, nextResult)

                    if (needsAvatar) {
                      return
                    }

                    const preparedPlan =
                      result?.preparedStoryboardPlan ||
                      result?.premiumStoryboardGeneration?.result_json ||
                      projectJson?.premium_storyboard ||
                      null

                    const storyboardRes = await fetch('/api/creator-studio/video/storyboard/generate', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        generationId,
                        customerId: request.payload?.userId,
                        sellerId: request.payload?.sellerId,
                        brief: {
                          ...projectJson?.brief,
                          initialImage: productImageUrl,
                          productImageUrl,
                        },
                        preparedPlan,
                        premiumStoryboardPlan: preparedPlan,
                        sourcePremiumStoryboardGenerationId:
                          result?.premiumStoryboardGeneration?.generation_id ||
                          result?.premiumStoryboardGeneration?.generationId ||
                          null,
                        skipFal: !generateStoryboardImages,
                        allowHyperframesScenes,
                        forceRealScenes: !allowHyperframesScenes,
                        disallowMotionGraphicsScenes: !allowHyperframesScenes,
                        notifications: {
                          channels: ['dashboard'],
                          requireApproval: true,
                        },
                      }),
                    })

                    const storyboardJson = await storyboardRes.json().catch(() => null)

                    if (!storyboardRes.ok || !storyboardJson?.ok) {
                      throw new Error(storyboardJson?.error || 'Error generando storyboard')
                    }

                    if (Array.isArray(storyboardJson?.scenes) && storyboardJson.scenes.length > 0) {
                      setSceneProgress((prev) =>
                          mergeGeneratedSceneImages(prev, storyboardJson.scenes)
                      )
                    }
                  }
                }
                onBack={() => setPhase('proposed_plan')}
            />
        )}

        {phase === 'creating_character' && generationId && projectJson && (
          <CharacterCreationStep
            plan={projectJson}
            request={request}
            generationId={generationId}
            onDone={async (avatarImageUrlRaw: string) => {
              const avatarImageUrl =
                typeof window !== 'undefined'
                  ? new URL(avatarImageUrlRaw, window.location.origin).toString()
                  : avatarImageUrlRaw

              const nextResult = {
                ...(result || {}),
                avatar: {
                  ...(result?.avatar || {}),
                  imageUrl: avatarImageUrl,
                },

                // Compatibilidad con el flow actual
                characterImageUrl: avatarImageUrl,
              }

              setResult(nextResult)
              setPhase('generating_scenes')

              await saveFlowSnapshot('generating_scenes', nextResult)

              const sourceScript = String(
                result?.script?.script ||
                  request.payload?.script?.script ||
                  request.payload?.initialBrief?.script?.script ||
                  request.payload?.plan?.script ||
                  result?.plan?.script ||
                  ''
              ).trim()

              const scriptTitle = String(
                result?.script?.title ||
                  request.payload?.script?.title ||
                  result?.plan?.title ||
                  projectJson?.brief?.title ||
                  ''
              ).trim()

              const scriptDurationEstimate = String(
                result?.script?.duration_estimate ||
                  request.payload?.script?.duration_estimate ||
                  ''
              ).trim()

              const selectedIdea =
                request.payload?.selectedIdea ||
                request.payload?.initialBrief?.selectedIdea ||
                result?.selectedIdea ||
                null

              const selectedHook =
                request.payload?.selectedHook ||
                request.payload?.initialBrief?.selectedHook ||
                result?.selectedHook ||
                null

              const storyboardRes = await fetch('/api/creator-studio/video/storyboard/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  generationId,
                  customerId: request.payload?.userId,
                  sellerId: request.payload?.sellerId,
                  brief: {
                    ...projectJson?.brief,

                    sourceScript,
                    strictScriptMode: !!sourceScript,
                    scriptTitle,
                    scriptDurationEstimate,

                    selectedIdea,
                    selectedHook,

                    initialImage: avatarImageUrl,
                    avatarImageUrl,
                    characterImageUrl: avatarImageUrl,

                    productImageUrl: result?.productImageUrl || null,
                  },
                  script: sourceScript
                    ? {
                        title: scriptTitle,
                        script: sourceScript,
                        duration_estimate: scriptDurationEstimate,
                      }
                    : null,
                  strictScriptMode: !!sourceScript,
                  selectedIdea,
                  selectedHook,
                  skipFal: !generateStoryboardImages,
                  allowHyperframesScenes,
                  forceRealScenes: !allowHyperframesScenes,
                  disallowMotionGraphicsScenes: !allowHyperframesScenes,
                  notifications: {
                    channels: ['dashboard'],
                    requireApproval: true,
                  },
                }),
              })

              const storyboardJson = await storyboardRes.json().catch(() => null)

              if (!storyboardRes.ok || !storyboardJson?.ok) {
                throw new Error(storyboardJson?.error || 'Error generando storyboard')
              }

              if (Array.isArray(storyboardJson?.scenes) && storyboardJson.scenes.length > 0) {
                setSceneProgress((prev) =>
                  mergeGeneratedSceneImages(prev, storyboardJson.scenes)
                )
              }
            }}
          />
        )}

        {phase === 'generating_avatar' && (
          <GenerationRuntime
            title="Proyecto listo"
            subtitle={`Duración: ${projectJson?.brief?.durationTargetSec || 20} segundos • Escenas: ${sceneProgress.length || projectJson?.planner?.scenes?.length || 0}${totalShots ? ` • Shots: ${totalShots}` : ''}${hyperframesScenes ? ` • HyperFrames: ${hyperframesScenes}` : ''}`}
            steps={[
              { label: 'Proyecto creado', status: 'done' },
              { label: 'Generando avatar', status: 'active' },
              { label: 'Generando escenas', status: 'pending' },
              { label: 'Compilando proyecto', status: 'pending' },
            ]}
            scenes={sceneProgress}
            onRegenerateScene={regenerateSingleScene}
            onOpenScene={openSceneDetail}
            allScenesReady={allScenesReady}
            generationId={generationId}
            projectId={projectId}
            routeFallback={request.routeFallback ?? undefined}
            onCloseFlow={handleCloseFlow}
            allSceneVideosReady={allSceneVideosReady}
            onRenderFinalVideo={handleRenderFinalVideo}
            animatingSceneId={animatingSceneId}
          />
        )}

        {phase === 'generating_scenes' && (
          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div
                key="scene-grid"
                className="min-h-0"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                <GenerationRuntime
                  title={allScenesReady ? 'Escenas listas para revisión' : 'Proyecto listo'}
                  subtitle={`Duración: ${projectJson?.brief?.durationTargetSec || 20} segundos • Escenas: ${sceneProgress.length || projectJson?.planner?.scenes?.length || 0}`}
                  steps={[
                    { label: 'Proyecto creado', status: 'done' },
                    { label: 'Avatar listo', status: needsAvatar ? 'done' : 'pending' },
                    { label: 'Generando escenas', status: allScenesReady ? 'done' : 'active' },
                    { label: 'Revisión visual', status: allScenesReady ? 'active' : 'pending' },
                  ]}
                  scenes={sceneProgress}
                  onRegenerateScene={regenerateSingleScene}
                  onOpenScene={openSceneDetail}
                  allScenesReady={allScenesReady}
                  generationId={generationId}
                  projectId={projectId}
                  routeFallback={request.routeFallback ?? undefined}
                  onCloseFlow={handleCloseFlow}
                  allSceneVideosReady={allSceneVideosReady}
                  onRenderFinalVideo={handleRenderFinalVideo}
                />
              </motion.div>
            ) : selectedScene && sceneDraft ? (
              <motion.div
                key="scene-detail"
                className="min-h-0"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                <SceneDetailView
                  scene={selectedScene}
                  draft={sceneDraft}
                  setDraft={setSceneDraft}
                  onBack={closeSceneDetail}
                  onSave={handleSaveSceneEdits}
                  onSaveAndRegenerate={handleSaveAndRegenerateFromDetail}
                  onAnimate={() => handleAnimateScene(sceneDraft.avatarQuality || 'standard')}
                  onPrepareHyperframes={handlePrepareHyperframesScene}
                  onGenerateHyperframes={handleGenerateHyperframesScene}
                  onPrevScene={goToPrevScene}
                  onNextScene={goToNextScene}
                  projectAspectRatio={projectAspectRatio}
                  canGoPrev={selectedSceneIndex > 0}
                  canGoNext={selectedSceneIndex >= 0 && selectedSceneIndex < sceneProgress.length - 1}
                  currentSceneIndex={selectedSceneIndex}
                  totalScenes={sceneProgress.length}
                  saving={savingSceneEdit}
                  animating={animatingSceneId === Number(selectedScene.sceneId)}
                  canRegenerate={canGenerateOrRegenerateFrame}
                  regenCredits={estimateSceneRegenCredits(selectedScene)}
                  voiceHistoryOpen={voiceHistoryOpen}
                  setVoiceHistoryOpen={setVoiceHistoryOpen}
                  voiceTakes={voiceTakes}
                  loadingVoiceTakes={loadingVoiceTakes}
                  selectingVoiceAssetId={selectingVoiceAssetId}
                  loadSceneVoiceHistory={loadSceneVoiceHistory}
                  onSelectSceneVoice={handleSelectSceneVoice}
                  generatingVoice={generatingVoiceSceneId === Number(selectedScene.sceneId)}
                  onGenerateVoice={handleGenerateSceneVoice}
                  globalVoiceSettings={globalVoiceSettings}
                  hasGlobalVoicePreset={hasGlobalVoicePreset}
                  onToggleGlobalVoicePreset={handleToggleGlobalVoicePreset}
                  sceneVoiceUrl={selectedScene?.voiceUrl || null}
                  regeneratingMotion={regeneratingMotionSceneId === Number(selectedScene.sceneId)}
                  onRegenerateMotionWithDirection={handleRegenerateMotionWithDirection}
                  onUploadSceneImage={handleUploadSceneImage}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        )}

        {phase === 'rendering_final_video' && (
          <div className="flex h-[60vh] flex-col items-center justify-center text-center">
            <div className="relative mb-6">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="absolute inset-0 blur-xl opacity-40">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            </div>

            <div className="text-lg font-semibold">
              Samantha está compilando tu video final...
            </div>

            <div className="mt-2 max-w-md text-sm text-muted-foreground">
              Uniendo escenas, mezclando audio y preparando la versión final.
            </div>
          </div>
        )}

        {phase === 'result' && (
          <div className="space-y-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-foreground/60">
              Final Video
            </div>

            <div className="text-3xl font-semibold text-foreground">
              Tu video está listo
            </div>

            <div className="overflow-hidden rounded-[32px] border border-border/70 bg-card">
              <div className="relative aspect-[9/16] w-full bg-primary/10">
                {result?.finalVideoUrl ? (
                  <video
                    src={result.finalVideoUrl}
                    className="h-full w-full object-cover"
                    controls
                    playsInline
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-cyan-100/45">
                    No se encontró el video final
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {result?.finalVideoUrl ? (
                <a
                  href={result.finalVideoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                >
                  Abrir video
                </a>
              ) : null}

              <button
                type="button"
                onClick={handleCloseFlow}
                className="rounded-2xl border border-primary/20 bg-primary/10 px-5 py-3 text-sm text-primary"
              >
                Cerrar flow
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="rounded-3xl border border-rose-400/15 bg-rose-400/[0.05] p-6">
            <div className="text-lg font-semibold text-rose-200">
              Algo falló al crear el proyecto
            </div>
            <div className="mt-3 text-sm text-rose-100/70">
              Revisa la consola o vuelve a intentar con otro enfoque.
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setResult(null)
                  setPhase('creative_direction')
                }}
                className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
              >
                Intentar de nuevo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function getSceneMotionInfo(scene: any) {
  const metadata = scene?.metadata_json || scene?.metadata || {}
  const shots = Array.isArray(metadata?.shots) ? metadata.shots : []
  const summary = metadata?.render_summary || {}

  const hasMotionOverlay =
    Boolean(summary?.has_motion_overlay) ||
    shots.some(
      (shot: any) =>
        shot?.render_stack?.motion?.engine === 'hyperframes_overlay'
    )

  const hasFullMotion =
    Boolean(summary?.has_full_motion_graphics) ||
    shots.some(
      (shot: any) =>
        shot?.render_stack?.motion?.engine === 'hyperframes_full'
    )

  return {
    hasMotion: hasMotionOverlay || hasFullMotion,
    hasMotionOverlay,
    hasFullMotion,
    motionMode:
      hasMotionOverlay && hasFullMotion
        ? 'mixed'
        : hasMotionOverlay
          ? 'overlay'
          : hasFullMotion
            ? 'full'
            : 'none',
  }
}

function mapSceneToRuntime(raw: any, prev?: any) {
  const metadata = raw?.metadata_json || raw?.metadata || prev?.metadata || {}
  const voiceMeta = metadata?.voice || {}
  const motionInfo = getSceneMotionInfo(raw)

  return {
    sceneId: raw?.sceneId ?? raw?.scene_id ?? raw?.id ?? prev?.sceneId ?? null,
    sceneOrder: Number(raw?.sceneOrder ?? raw?.scene_order ?? prev?.sceneOrder ?? 0),
    title: String(raw?.title || prev?.title || 'Escena'),
    prompt: String(raw?.prompt || prev?.prompt || ''),
    negativePrompt: String(
      raw?.negativePrompt ??
      raw?.negative_prompt ??
      prev?.negativePrompt ??
      ''
    ),
    overlayText: String(
      raw?.overlayText ??
      raw?.overlay_text ??
      metadata?.overlay_text ??
      prev?.overlayText ??
      ''
    ),
    dialogueText: String(
      raw?.dialogueText ??
      raw?.audio?.text ??
      raw?.raw_scene_json?.audio?.text ??
      prev?.dialogueText ??
      ''
    ),
    speaker: String(
      raw?.speaker ??
      raw?.audio?.speaker ??
      raw?.raw_scene_json?.audio?.speaker ??
      prev?.speaker ??
      ''
    ),
    audioMode:
      raw?.audio?.mode ??
      raw?.audioMode ??
      prev?.audioMode ??
      'voiceover',
    expressionLevel:
      metadata?.voice?.expression_level ??
      prev?.expressionLevel ??
      'natural',
    emotionTag:
      metadata?.voice?.emotion_tag ??
      prev?.emotionTag ??
      '',
    voiceChoice:
      raw?.voiceChoice ??
      metadata?.voice?.selected_voice ??
      prev?.voiceChoice ??
      'auto',
    durationSec:
      raw?.durationSec ??
      raw?.duration_sec ??
      prev?.durationSec ??
      null,
    sceneType: String(
      raw?.sceneType ??
      raw?.scene_type ??
      prev?.sceneType ??
      ''
    ),
    motionInfo,
    canPrepareMotion: motionInfo.hasMotion,
    motionMode: motionInfo.motionMode,
    metadata,
    metadata_json: metadata,
    hyperframesPlan:
      metadata?.hyperframes_plan ||
      raw?.hyperframesPlan ||
      prev?.hyperframesPlan ||
      null,
    voiceStatus:
      raw?.voiceStatus ??
      voiceMeta?.status ??
      prev?.voiceStatus ??
      'idle',
    voiceUrl:
      raw?.voiceUrl ??
      voiceMeta?.audio_url ??
      prev?.voiceUrl ??
      null,
    status:
      raw?.status ||
      prev?.status ||
      ((raw?.imageUrl || raw?.image_url) ? 'done' : 'pending'),
    imageUrl: raw?.imageUrl || raw?.image_url || prev?.imageUrl || null,
    videoUrl: raw?.videoUrl || raw?.video_url || prev?.videoUrl || null,
  }
}

const GenerationRuntime = React.memo(function GenerationRuntime({
  title,
  subtitle,
  steps,
  scenes,
  onRegenerateScene,
  onOpenScene,
  allScenesReady,
  allSceneVideosReady,
  generationId,
  projectId,
  routeFallback,
  onCloseFlow,
  onRenderFinalVideo,
  animatingSceneId,
}: {
  title: string
  subtitle: string
  steps: Array<{ label: string; status: 'done' | 'active' | 'pending' }>
  scenes: any[]
  onRegenerateScene: (scene: any) => void
  onOpenScene: (scene: any) => void
  allScenesReady: boolean
  allSceneVideosReady: boolean
  generationId?: number | null
  projectId?: number | null
  routeFallback?: string
  onCloseFlow: () => void
  onRenderFinalVideo: () => void
  animatingSceneId?: number | null
}) {

  const trackRef = React.useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  function getSceneMetadata(scene: any) {
    return scene?.metadata_json || scene?.metadata || {}
  }

  function getSceneShots(scene: any) {
    const metadata = getSceneMetadata(scene)
    const shots =
      metadata?.shots ||
      scene?.shots

    return Array.isArray(shots) ? shots : []
  }

  function isHyperframesOnlyScene(scene: any) {
    const shots = getSceneShots(scene)

    if (!shots.length) return false

    return shots.every((shot: any) => {
      return (
        String(shot?.render_engine || '').trim() === 'hyperframes' &&
        Boolean(shot?.needs_extra_frame) === false
      )
    })
  }

  function getSceneProductionLabel(scene: any) {
    const shots = getSceneShots(scene)

    if (isHyperframesOnlyScene(scene)) return 'HyperFrames'

    if (
      shots.some((shot: any) =>
        ['avatar_talk', 'avatar_frame', 'person_frame'].includes(
          String(shot?.execution_type || '').trim()
        )
      )
    ) {
      return 'Avatar'
    }

    if (
      shots.some((shot: any) =>
        ['product_showcase', 'product_demo', 'product_frame'].includes(
          String(shot?.execution_type || '').trim()
        )
      )
    ) {
      return 'Producto'
    }

    if (shots.length > 1) return 'Compuesta'

    return 'Frame'
  }

  const updateScrollState = React.useCallback(() => {
    const el = trackRef.current
    if (!el) return

    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 8)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 8)
  }, [])

  const scrollByCard = React.useCallback((dir: 'left' | 'right') => {
    const el = trackRef.current
    if (!el) return

    const firstCard = el.querySelector('[data-scene-card="1"]') as HTMLElement | null
    const cardWidth = firstCard?.offsetWidth || 220
    const gap = 16
    const amount = cardWidth + gap

    el.scrollBy({
      left: dir === 'right' ? amount : -amount,
      behavior: 'smooth',
    })
  }, [])

  function buildVideoEditorUrl() {
    const params = new URLSearchParams()

    const resolvedProjectId = Number(projectId || 0)
    const resolvedGenerationId = Number(generationId || 0)

    if (resolvedProjectId) {
      params.set('projectId', String(resolvedProjectId))
    }

    if (resolvedGenerationId) {
      params.set('videoGenerationId', String(resolvedGenerationId))
    }

    params.set('tab', 'export')

    return `/creator-studio?${params.toString()}`
  }

  React.useEffect(() => {
    updateScrollState()
    const el = trackRef.current
    if (!el) return

    const onScroll = () => updateScrollState()
    el.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', updateScrollState)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [updateScrollState, scenes])

  return (
    <div className="grid gap-6 md:grid-cols-[260px_1fr]">
      <div className="space-y-4">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-3">
            <div
              className={
                s.status === 'active'
                  ? 'h-2.5 w-2.5 rounded-full bg-primary animate-pulse'
                  : s.status === 'done'
                  ? 'h-2.5 w-2.5 rounded-full bg-primary'
                  : 'h-2.5 w-2.5 rounded-full bg-primary/20'
              }
            />
            <div className="text-sm text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="min-w-0">
        <div className="text-2xl font-semibold">{title}</div>
        <div className="mt-2 text-sm text-muted-foreground">{subtitle}</div>

        {!allScenesReady && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-border/70 bg-primary/[0.04] px-4 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
            Generando storyboard visual...
          </div>
        )}

        <div className="mt-6 text-sm text-primary/70">
          {allScenesReady
            ? 'Tus escenas ya están listas. Puedes revisar, editar o regenerar antes de animarlas.'
            : 'Ahora generaré las imágenes…'}
        </div>

        <div className="relative mt-4 w-full overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-10 bg-gradient-to-r from-background to-transparent md:block" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-10 bg-gradient-to-l from-background to-transparent md:block" />

          <button
            type="button"
            onClick={() => scrollByCard('left')}
            disabled={!canScrollLeft}
            className="absolute left-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-primary/20 bg-background/88 text-cyan-100 backdrop-blur md:flex disabled:opacity-30"
            aria-label="Escena anterior"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={() => scrollByCard('right')}
            disabled={!canScrollRight}
            className="absolute right-2 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-primary/20 bg-background/88 text-cyan-100 backdrop-blur md:flex disabled:opacity-30"
            aria-label="Siguiente escena"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div
            ref={trackRef}
            className="
              flex gap-4 overflow-x-auto overflow-y-hidden pb-6 pt-1
              snap-x snap-mandatory
              scroll-smooth
              [-ms-overflow-style:none]
              [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden
              touch-pan-x
              overscroll-x-contain
            "
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {scenes.map((scene: any, i: number) => {
              const sceneVideoProcessing =
                scene?.status === 'video_processing' ||
                scene?.videoStatus === 'processing' ||
                scene?.metadata?.video?.status === 'processing' ||
                scene?.metadata_json?.video?.status === 'processing'

              const isGenerating =
                scene?.status === 'generating' ||
                sceneVideoProcessing ||
                animatingSceneId === Number(scene.sceneId)
                
              const shots = getSceneShots(scene)
              const hyperframesOnly = isHyperframesOnlyScene(scene)
              const motionInfo = getSceneMotionInfo(scene)
              const productionLabel = getSceneProductionLabel(scene)

              return (
                <div
                  key={scene.sceneId || scene.sceneOrder || i}
                  data-scene-card="1"
                  className="min-w-[220px] max-w-[220px] shrink-0 snap-start rounded-2xl border border-border/70 bg-background p-3"
                >

                  <button
                    type="button"
                    onClick={() => onOpenScene(scene)}
                    className="relative block h-40 w-full overflow-hidden rounded-xl bg-primary/10 text-left"
                  >
                    {scene.videoUrl ? (
                      <video
                        key={scene.videoUrl}
                        src={scene.videoUrl}
                        className="h-full w-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    ) : scene.imageUrl ? (
                      <img
                        src={scene.imageUrl}
                        alt={scene.title || `Scene ${scene.sceneOrder || i + 1}`}
                        className="h-full w-full object-cover"
                      />
                    ) : hyperframesOnly ? (
                      <div className="flex h-full w-full items-center justify-center bg-primary/5 p-4 text-center">
                        <div>
                          <div className="text-xs font-semibold text-primary">
                            HyperFrames
                          </div>
                          <div className="mt-1 text-[11px] leading-4 text-muted-foreground">
                            Animación, overlays y motion graphics
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full w-full bg-primary/5" />
                    )}

                    <div className="absolute left-2 top-2 z-10 flex gap-2">
                      {scene.videoUrl ? (
                        <div className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-400/55 px-1 py-1 text-[10px] uppercase tracking-[0.16em] text-emerald-100">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </div>
                      ) : scene.imageUrl ? (
                        <div className="hidden inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/12 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                          Imagen lista
                        </div>
                      ) : null}
                    </div>

                    {isGenerating && !hyperframesOnly && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/45 backdrop-blur-[1px]">
                        <div className="h-9 w-9 rounded-full border-2 border-primary/20 border-t-cyan-300 animate-spin" />
                      </div>
                    )}
                  </button>

                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-muted-foreground">
                      {scene.title || `Escena ${scene.sceneOrder || i + 1}`}
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                        {productionLabel}
                      </span>

                      {shots.length > 0 && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                          {shots.length} {shots.length === 1 ? 'shot' : 'shots'}
                        </span>
                      )}

                      {hyperframesOnly && (
                        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-500">
                          sin frame extra
                        </span>
                      )}

                      {motionInfo.hasMotionOverlay && (
                        <span className="rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] font-medium text-cyan-500">
                          overlay animado
                        </span>
                      )}

                      {motionInfo.hasFullMotion && (
                        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-500">
                          animación completa
                        </span>
                      )}
                      
                    </div>

                    {shots.length > 0 && (
                      <div className="rounded-xl border border-border/60 bg-muted/30 p-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                          Shot 1 · {shots[0]?.duration_sec || '—'}s
                        </div>
                        <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-foreground/80">
                          {shots[0]?.visual_goal || shots[0]?.overlay_text || 'Plan de animación'}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onOpenScene(scene)}
                        className="inline-flex items-center gap-1 rounded-xl border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] text-primary"
                      >
                        <PenSquare className="h-3.5 w-3.5" />
                        Detalles
                      </button>

                      <button
                        type="button"
                        onClick={() => onRegenerateScene(scene)}
                        disabled={scene.status === 'generating'}
                        className="rounded-xl border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] text-primary disabled:opacity-40"
                      >
                        {scene.status === 'generating' ? 'Generando...' : 'Volver a generar'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {generationId ? (
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={buildVideoEditorUrl()}
              className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
            >
              <Film className="h-4 w-4" />
              {allScenesReady ? 'Abrir editor de video' : 'Revisar avance en editor'}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  )
})

function sceneNeedsVideo(scene: any) {
  const sceneType = String(scene?.sceneType || scene?.scene_type || '').trim().toLowerCase()
  const visualType = String(
    scene?.metadata?.visual_type ||
      scene?.metadata_json?.visual_type ||
      scene?.visualType ||
      scene?.visual_type ||
      sceneType ||
      ''
  ).trim().toLowerCase()

  const generationPriority = String(
    scene?.metadata?.generation_priority ||
      scene?.metadata_json?.generation_priority ||
      ''
  ).trim().toLowerCase()

  const skipFalThumbnail = Boolean(
    scene?.metadata?.skip_fal_thumbnail ||
      scene?.metadata_json?.skip_fal_thumbnail
  )

  const hasImage = Boolean(
    scene?.imageUrl ||
      scene?.image_url ||
      scene?.thumbnailUrl ||
      scene?.thumbnail_url
  )

  const hasGeneratedFrame =
    hasImage &&
    !['motion_graphics', 'text_card', 'screen_recording'].includes(visualType)

  const shots = getSceneShots(scene)

  const hasAvatarVideoShot = shots.some((shot: any) =>
    ['avatar_talk', 'talking_avatar', 'avatar_video'].includes(
      String(shot?.execution_type || '').trim().toLowerCase()
    )
  )

  const hasAnimatableShot = shots.some((shot: any) => {
    const executionType = String(shot?.execution_type || '').trim().toLowerCase()
    const renderEngine = String(
      shot?.render_engine ||
        shot?.render_stack?.motion?.engine ||
        shot?.render_stack?.base?.engine ||
        ''
    ).trim().toLowerCase()

    return (
      [
        'product_showcase',
        'person_frame',
        'image_frame',
        'ai_image',
        'b_roll',
        'establishing_shot',
        'cinematic_frame',
      ].includes(executionType) ||
      [
        'ken_burns_or_editorial_motion',
        'image_to_video',
        'fal_video',
        'veo',
        'runway',
        'kling',
      ].includes(renderEngine)
    )
  })

  if (hasAvatarVideoShot) return true

  if (hasGeneratedFrame) return true

  if (
    hasAnimatableShot &&
    !skipFalThumbnail &&
    generationPriority !== 'motion_graphics'
  ) {
    return true
  }

  return false
}

function sceneNeedsImageFrame(scene: any) {
  return !isHyperframesOnlyScene(scene)
}

function getSceneShots(scene: any) {
  const shots =
    scene?.metadata_json?.shots ||
    scene?.metadata?.shots ||
    scene?.shots

  return Array.isArray(shots) ? shots : []
}

function isHyperframesOnlyScene(scene: any) {
  const shots = getSceneShots(scene)

  if (!shots.length) return false

  return shots.every((shot: any) => {
    return (
      String(shot?.render_engine || '').trim() === 'hyperframes' &&
      Boolean(shot?.needs_extra_frame) === false
    )
  })
}

function getSceneProductionLabel(scene: any) {
  const shots = getSceneShots(scene)

  if (isHyperframesOnlyScene(scene)) return 'Animación HyperFrames'

  if (
    shots.some((shot: any) =>
      ['avatar_talk', 'avatar_frame', 'person_frame'].includes(
        String(shot?.execution_type || '').trim()
      )
    )
  ) {
    return 'Avatar + frame base'
  }

  if (
    shots.some((shot: any) =>
      ['product_showcase', 'product_demo', 'product_frame'].includes(
        String(shot?.execution_type || '').trim()
      )
    )
  ) {
    return 'Producto + frame base'
  }

  if (shots.length > 1) return 'Escena compuesta'

  return 'Frame generado'
}

function getShotExecutionLabel(value: any) {
  const type = String(value || '').trim()

  const labels: Record<string, string> = {
    avatar_talk: 'Avatar hablando',
    avatar_frame: 'Avatar',
    person_frame: 'Persona',
    screen_ui: 'Interfaz / UI',
    motion_graphics: 'Motion graphics',
    text_overlay: 'Texto animado',
    broll_frame: 'B-roll',
    product_showcase: 'Producto',
    scene_frame: 'Frame de escena',
  }

  return labels[type] || type || 'Shot'
}

function getRenderEngineLabel(value: any) {
  const engine = String(value || '').trim()

  const labels: Record<string, string> = {
    existing_scene_frame: 'Frame base',
    hyperframes: 'HyperFrames',
    fal_image_frame: 'Frame IA',
    avatar_frame: 'Avatar frame',
  }

  return labels[engine] || engine || 'Motor'
}

function normalizeGender(value: any): 'male' | 'female' | 'neutral' | 'unknown' {
  const gender = String(value || '').toLowerCase().trim()

  if (['male', 'man', 'masculino', 'hombre'].includes(gender)) return 'male'
  if (['female', 'woman', 'femenino', 'mujer'].includes(gender)) return 'female'
  if (['neutral', 'nonbinary', 'non-binary', 'genero neutro', 'género neutro'].includes(gender)) {
    return 'neutral'
  }

  return 'unknown'
}

function getProjectJsonFromScene(scene: any) {
  return (
    scene?.project_json ||
    scene?.projectJson ||
    scene?.project?.project_json ||
    scene?.project?.projectJson ||
    scene?.metadata_json?.project_json ||
    scene?.metadata?.project_json ||
    null
  )
}

function getSceneVoiceContext(scene: any) {
  const projectJson = getProjectJsonFromScene(scene)

  const brief = projectJson?.brief || {}
  const subjectBible = projectJson?.subject_bible || {}
  const primarySubject = subjectBible?.primary_subject || {}

  const subjectGender = normalizeGender(primarySubject?.gender)

  return {
    projectJson,
    brief,
    subjectBible,
    primarySubject,
    subjectGender,
    voiceStyle: String(brief?.voiceStyle || '').trim(),
    language: String(brief?.language || '').trim(),
    styleFamily: String(projectJson?.visual_bible?.style_family || brief?.style || '').trim(),
    performanceStyle: String(primarySubject?.performance_style || '').trim(),
    castingStyle: String(primarySubject?.casting_style || '').trim(),
  }
}

function resolveDefaultLanguageCode(language: string) {
  const lang = String(language || '').toLowerCase()

  if (lang === 'es' || lang.includes('spanish') || lang.includes('español')) {
    return 'Spanish (Mexico)'
  }

  if (lang === 'en' || lang.includes('english') || lang.includes('inglés')) {
    return 'English (US)'
  }

  return ''
}

type SceneProductionStepId = 'image' | 'voice' | 'graphics' | 'video' | 'done'

type SceneProductionState = {
  imageNeeded: boolean
  imageReady: boolean
  voiceNeeded: boolean
  voiceReady: boolean
  graphicsNeeded: boolean
  graphicsReady: boolean
  graphicsPrepared: boolean
  videoNeeded: boolean
  videoReady: boolean
  videoProcessing: boolean
  nextStep: SceneProductionStepId
}

const AVATAR_QUALITY_OPTIONS = [
  {
    id: 'preview',
    label: 'Preview',
    sub: 'Rápido',
  },
  {
    id: 'standard',
    label: 'Standard',
    sub: 'Recomendado',
  },
  {
    id: 'premium',
    label: 'Premium',
    sub: 'Final',
  },
] as const

const VOICE_GENDER_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'female', label: 'Femenina' },
  { id: 'male', label: 'Masculina' },
] as const

const PERFORMANCE_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'ugc', label: 'UGC' },
  { id: 'cinematic', label: 'Cine' },
  { id: 'commercial', label: 'Ad' },
  { id: 'mentor', label: 'Mentor' },
  { id: 'emotional', label: 'Emocional' },
] as const

function cnLite(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}

function getScenePreviewUrl(scene: any) {
  return (
    scene?.videoUrl ||
    scene?.video_url ||
    scene?.imageUrl ||
    scene?.image_url ||
    scene?.thumbnailUrl ||
    scene?.thumbnail_url ||
    scene?.metadata?.image_url ||
    scene?.metadata_json?.image_url ||
    ''
  )
}

function getSceneVoiceUrl(scene: any, sceneVoiceUrl?: string | null) {
  return (
    sceneVoiceUrl ||
    scene?.voiceUrl ||
    scene?.voice_url ||
    scene?.metadata?.voice?.audio_url ||
    scene?.metadata_json?.voice?.audio_url ||
    ''
  )
}

function getProductionState(input: {
  scene: any
  draft: SceneDetailDraft
  sceneVoiceUrl?: string | null
  canPrepareMotion: boolean
  hyperframesReady: boolean
  hasGeneratedMotion: boolean
}) {
  const { scene, draft, sceneVoiceUrl, canPrepareMotion, hyperframesReady, hasGeneratedMotion } =
    input

  const imageNeeded = sceneNeedsImageFrame(scene)
  const videoNeeded = sceneNeedsVideo(scene)

  const imageReady = Boolean(
    scene?.imageUrl ||
      scene?.image_url ||
      scene?.imageAssetId ||
      scene?.image_asset_id ||
      scene?.thumbnailUrl ||
      scene?.thumbnail_url
  )

  const voiceNeeded = draft.audioMode !== 'silent'
  const voiceReady = !voiceNeeded || Boolean(getSceneVoiceUrl(scene, sceneVoiceUrl))

  const graphicsNeeded = Boolean(
    scene?.graphicsPrompt ||
    scene?.graphics?.length ||
    scene?.visualPrompt ||
    scene?.imagePrompt
  )

  const canEditGraphics = true
  const graphicsPrepared = !graphicsNeeded || Boolean(hyperframesReady)
  const graphicsReady = !graphicsNeeded || Boolean(hasGeneratedMotion)

  const videoReady = Boolean(scene?.videoUrl || scene?.video_url)

  const videoProcessing = Boolean(
    scene?.status === 'video_processing' ||
      scene?.videoStatus === 'processing' ||
      scene?.metadata?.video?.status === 'processing' ||
      scene?.metadata_json?.video?.status === 'processing'
  )

  let nextStep: SceneProductionStepId = 'done'

  if (imageNeeded && !imageReady) nextStep = 'image'
  else if (voiceNeeded && !voiceReady) nextStep = 'voice'
  else if (graphicsNeeded && !graphicsReady) nextStep = 'graphics'
  else if (videoNeeded && !videoReady) nextStep = 'video'

  return {
    imageNeeded,
    imageReady,
    voiceNeeded,
    voiceReady,
    graphicsNeeded,
    graphicsReady,
    graphicsPrepared,
    videoNeeded,
    videoReady,
    videoProcessing,
    nextStep,
  } satisfies SceneProductionState
}

function AiPulseOrb({ active }: { active?: boolean }) {
  return (
    <div className="relative flex h-10 w-10 items-center justify-center">
      {active && (
        <>
          <div className="absolute h-10 w-10 animate-ping rounded-full bg-primary/20" />
          <div className="absolute h-8 w-8 animate-pulse rounded-full bg-cyan-400/20 blur-sm" />
        </>
      )}
      <div className="relative flex h-9 w-9 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary shadow-sm">
        {active ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
      </div>
    </div>
  )
}

function StepDot({
  done,
  active,
  processing,
}: {
  done?: boolean
  active?: boolean
  processing?: boolean
}) {
  if (processing) {
    return (
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )
  }

  if (done) {
    return (
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500"
      >
        <CheckCircle2 className="h-4 w-4" />
      </motion.div>
    )
  }

  return (
    <div
      className={cnLite(
        'flex h-7 w-7 items-center justify-center rounded-full border text-[11px]',
        active
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground'
      )}
    >
      •
    </div>
  )
}

function MinimalSegmentedControl<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: readonly { id: T; label: string; sub?: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div className="grid gap-1 rounded-2xl border border-border/70 bg-muted/30 p-1 md:grid-cols-3">
      {options.map((option) => {
        const active = value === option.id

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={cnLite(
              'rounded-xl px-3 py-2 text-left transition',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <div className="text-xs font-semibold">{option.label}</div>
            {option.sub ? <div className="mt-0.5 text-[10px] opacity-70">{option.sub}</div> : null}
          </button>
        )
      })}
    </div>
  )
}

function SceneMiniMeta({
  scene,
  state,
  productionLabel,
  shotsCount,
}: {
  scene: any
  state: SceneProductionState
  productionLabel: string
  shotsCount: number
}) {
  const duration = Number(scene?.durationSec || scene?.duration_sec || 0)

  return (
    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
      <span className="rounded-full border border-border/70 bg-background/70 px-2.5 py-1">
        {productionLabel || 'Producción IA'}
      </span>

      {duration ? (
        <span className="rounded-full border border-border/70 bg-background/70 px-2.5 py-1">
          {duration}s
        </span>
      ) : null}

      <span className="rounded-full border border-border/70 bg-background/70 px-2.5 py-1">
        {shotsCount || 1} shot{shotsCount === 1 ? '' : 's'}
      </span>

      <span
        className={cnLite(
          'rounded-full border px-2.5 py-1',
          state.voiceReady
            ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-600'
            : 'border-amber-500/20 bg-amber-500/10 text-amber-600'
        )}
      >
        {state.voiceReady ? 'Voz lista' : 'Voz pendiente'}
      </span>
    </div>
  )
}

function normalizeAspectRatio(value?: any) {
  const raw = String(value || '').trim()

  if (!raw) return '9 / 16'

  // Soporta "9:16", "16:9", "1:1"
  if (/^\d+(\.\d+)?:\d+(\.\d+)?$/.test(raw)) {
    return raw.replace(':', ' / ')
  }

  // Soporta "9 / 16"
  if (/^\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?$/.test(raw)) {
    return raw
  }

  return '9 / 16'
}

function getPreviewMaxWidth(aspectRatio?: any) {
  const raw = String(aspectRatio || '').trim()

  if (raw === '16:9' || raw === '16 / 9') return 'max-w-[720px]'
  if (raw === '1:1' || raw === '1 / 1') return 'max-w-[520px]'
  if (raw === '4:5' || raw === '4 / 5') return 'max-w-[520px]'
  if (raw === '9:16' || raw === '9 / 16') return 'max-w-[430px]'

  return 'max-w-[520px]'
}

function ScenePreviewPanel({
  scene,
  state,
  aspectRatio,
  productionLabel,
  shotsCount,
  hyperframesPlan,
  hyperframesReady,
  canPrepareMotion,
  animating,
  regeneratingMotion,
  onGenerateHyperframes,
  onRegenerateMotionWithDirection,
  onUploadImage,
}: {
  scene: any
  state: SceneProductionState
  aspectRatio?: string
  productionLabel: string
  shotsCount: number
  hyperframesPlan: any
  hyperframesReady: boolean
  canPrepareMotion: boolean
  animating: boolean
  regeneratingMotion: boolean
  onGenerateHyperframes: () => void
  onRegenerateMotionWithDirection: (direction: string) => void
  onUploadImage?: (file: File, sceneId: number) => Promise<void>
}) {
  const videoUrl = scene?.videoUrl || scene?.video_url || ''
  const imageUrl =
    scene?.imageUrl ||
    scene?.image_url ||
    scene?.thumbnailUrl ||
    scene?.thumbnail_url ||
    ''

  const previewAspectRatio = normalizeAspectRatio(aspectRatio)
  const previewMaxWidth = getPreviewMaxWidth(aspectRatio)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  const sceneId = Number(scene?.sceneId || scene?.scene_id || 0)

  const canUploadImage = Boolean(
    onUploadImage &&
      sceneId &&
      !videoUrl &&
      !imageUrl &&
      !uploadingImage
  )

  async function handlePickSceneImage(file?: File) {
    if (!file || !onUploadImage || !sceneId || uploadingImage) return

    setUploadingImage(true)

    try {
      await onUploadImage(file, sceneId)
    } finally {
      setUploadingImage(false)

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const isRenderingFalVideo = Boolean(state.videoProcessing)
  const isPreparingMotionGraphics = Boolean(animating && !state.videoProcessing)

  return (
    <div className="flex min-h-0 flex-col">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
            Escena {scene?.sceneOrder || scene?.scene_order || ''}
          </div>

          <div className="mt-1 text-xl font-semibold tracking-tight text-foreground">
            {scene?.title || 'Escena generada'}
          </div>
        </div>

        {state.videoProcessing ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Procesando
          </div>
        ) : state.videoReady ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-600">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Video listo
          </div>
        ) : hyperframesReady ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-xs font-medium text-cyan-500">
            <Sparkles className="h-3.5 w-3.5" />
            Motion listo
          </div>
        ) : null}
      </div>

      <div className="w-full">
        <div
          className={cnLite(
            'mx-auto w-full overflow-hidden rounded-[32px] border border-border bg-card shadow-sm',
            previewMaxWidth
          )}
        >
          <div
            className="group relative w-full overflow-hidden bg-primary/10"
            style={{ aspectRatio: previewAspectRatio }}
          >
            {videoUrl ? (
              <video
                key={videoUrl}
                src={videoUrl}
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                controls
              />
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={scene?.title || 'Escena'}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : hyperframesReady ? (
              <HyperFramesScenePanel
                key={`motion-full-${
                  hyperframesPlan?.compositionId || scene?.sceneId || scene?.scene_id
                }`}
                plan={hyperframesPlan}
                generating={animating}
                regenerating={regeneratingMotion}
                onGenerate={onGenerateHyperframes}
                onRegenerateWithDirection={onRegenerateMotionWithDirection}
              />
            ) : canPrepareMotion ? (
              <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-primary/5 p-8 text-center">
                <div className="max-w-[260px]">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10 text-cyan-300">
                    <Sparkles className="h-6 w-6" />
                  </div>

                  <div className="text-lg font-semibold text-foreground">
                    Animación visual pendiente
                  </div>

                  <div className="mt-2 text-sm leading-6 text-muted-foreground">
                    Samantha puede preparar overlays, textos y motion graphics para esta escena.
                  </div>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-primary/5 p-8 text-center">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Frame pendiente
                  </div>

                  <div className="mt-1 text-xs text-muted-foreground">
                    Esta escena necesita una imagen base para continuar.
                  </div>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0]
                void handlePickSceneImage(file)
              }}
            />

            {canUploadImage ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-2xl border border-border/70 bg-background/90 px-4 py-2 text-xs font-semibold text-foreground opacity-0 shadow-lg backdrop-blur transition hover:bg-background group-hover:opacity-100"
              >
                <ImagePlus className="h-4 w-4" />
                Subir imagen
              </button>
            ) : uploadingImage ? (
              <div className="absolute bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-2xl border border-border/70 bg-background/90 px-4 py-2 text-xs font-semibold text-foreground shadow-lg backdrop-blur">
                <Loader2 className="h-4 w-4 animate-spin" />
                Subiendo
              </div>
            ) : null}

            {(videoUrl || imageUrl) && hyperframesReady ? (
              <div className="absolute inset-0 z-20">
                <HyperFramesScenePanel
                  key={`motion-overlay-${
                    hyperframesPlan?.compositionId || scene?.sceneId || scene?.scene_id
                  }`}
                  plan={hyperframesPlan}
                  generating={animating}
                  regenerating={regeneratingMotion}
                  onGenerate={onGenerateHyperframes}
                  onRegenerateWithDirection={onRegenerateMotionWithDirection}
                  overlayMode
                />
              </div>
            ) : null}

            <div className="pointer-events-none absolute left-3 top-3 z-40 flex flex-wrap gap-2">
              {videoUrl ? (
                <div className="rounded-full border border-violet-400/25 bg-violet-400/15 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-violet-100 backdrop-blur">
                  Video
                </div>
              ) : null}

              {hyperframesReady ? (
                <div className="rounded-full border border-cyan-400/25 bg-cyan-400/15 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-cyan-100 backdrop-blur">
                  Motion
                </div>
              ) : null}

              {state.voiceReady ? (
                <div className="rounded-full border border-emerald-400/25 bg-emerald-400/15 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-emerald-100 backdrop-blur">
                  Voz
                </div>
              ) : null}
            </div>

            {(isRenderingFalVideo || isPreparingMotionGraphics) && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/55 backdrop-blur-[1px]">
                <div className="flex flex-col items-center gap-3 rounded-3xl border border-border/70 bg-background/80 px-5 py-4 shadow-sm">
                  <AiPulseOrb active />

                  <div className="text-sm font-medium">
                    {isRenderingFalVideo
                      ? 'Samantha está renderizando'
                      : 'Samantha está preparando motion graphics'}
                  </div>

                  <div className="max-w-[220px] text-center text-xs leading-5 text-muted-foreground">
                    {isRenderingFalVideo
                      ? 'El video se actualizará automáticamente cuando FAL termine.'
                      : 'Está organizando textos, timing y elementos animados para esta escena.'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <SceneMiniMeta
          scene={scene}
          state={state}
          productionLabel={productionLabel}
          shotsCount={shotsCount}
        />
      </div>
    </div>
  )
}

function ProductionStepRow({
  title,
  description,
  done,
  active,
  processing,
  children,
}: {
  title: string
  description?: string
  done?: boolean
  active?: boolean
  processing?: boolean
  children?: React.ReactNode
}) {
  return (
    <motion.div
      layout
      className={cnLite(
        'rounded-3xl border p-4 transition',
        active
          ? 'border-primary/25 bg-primary/[0.045]'
          : done
          ? 'border-emerald-500/15 bg-emerald-500/[0.035]'
          : 'border-border/70 bg-background/60'
      )}
    >
      <div className="flex items-start gap-3">
        <StepDot done={done} active={active} processing={processing} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {done ? <div className="text-[11px] font-medium text-emerald-600">Listo</div> : null}
          </div>

          {description ? (
            <div className="mt-1 text-xs leading-5 text-muted-foreground">{description}</div>
          ) : null}

          {children ? <div className="mt-4">{children}</div> : null}
        </div>
      </div>
    </motion.div>
  )
}

function VoiceSetupPanel({
  draft,
  setDraft,
  voiceContext,
  resolvedLanguageCode,
  globalVoiceSettings,
  hasGlobalVoicePreset,
  isGlobalVoiceSourceScene,
  canSaveVoiceAsGlobalPreset,
  onToggleGlobalVoicePreset,
}: {
  draft: SceneDetailDraft
  setDraft: React.Dispatch<React.SetStateAction<SceneDetailDraft | null>>
  voiceContext: any
  resolvedLanguageCode: string

  globalVoiceSettings?: any
  hasGlobalVoicePreset?: boolean
  isGlobalVoiceSourceScene?: boolean
  canSaveVoiceAsGlobalPreset?: boolean
  onToggleGlobalVoicePreset: (enabled: boolean) => Promise<void>
}) {
  const voiceChoice = String(draft.voiceChoice || 'auto') as any
  const gender = (draft.voiceGenderPreference || 'auto') as 'auto' | 'female' | 'male'
  const performance = (draft.performanceStyle || 'auto') as any
  const voicePresetSwitchOn = Boolean(hasGlobalVoicePreset && isGlobalVoiceSourceScene)

  return (
    <div className="space-y-3">
      <MinimalSegmentedControl
        value={gender}
        options={VOICE_GENDER_OPTIONS}
        onChange={(value) =>
          setDraft((prev) => (prev ? { ...prev, voiceGenderPreference: value } : prev))
        }
      />

      <MinimalSegmentedControl
        value={performance}
        options={PERFORMANCE_OPTIONS}
        onChange={(value) =>
          setDraft((prev) =>
            prev
              ? {
                  ...prev,
                  performanceStyle: value as SceneDetailDraft['performanceStyle'],
                }
              : prev
          )
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Ritmo
          </label>
          <select
            value={draft.voicePace || 'auto'}
            onChange={(e) =>
              setDraft((prev: any) => ({
                ...prev,
                voicePace: e.target.value,
              }))
            }
            className="h-9 w-full rounded-lg border border-border bg-background px-3 text-xs"
          >
            <option value="auto">Auto</option>
            <option value="slightly_faster">Un poco más rápido</option>
            <option value="faster">Rápido</option>
            <option value="slower">Más lento</option>
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Intensidad
          </label>
          <select
            value={draft.voiceIntensity || 'auto'}
            onChange={(e) =>
              setDraft((prev: any) => ({
                ...prev,
                voiceIntensity: e.target.value,
              }))
            }
            className="h-9 w-full rounded-lg border border-border bg-background px-3 text-xs"
          >
            <option value="auto">Auto</option>
            <option value="restrained">Controlada</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Énfasis
          </label>
          <select
            value={draft.voiceEmphasis || 'auto'}
            onChange={(e) =>
              setDraft((prev: any) => ({
                ...prev,
                voiceEmphasis: e.target.value,
              }))
            }
            className="h-9 w-full rounded-lg border border-border bg-background px-3 text-xs"
          >
            <option value="auto">Auto</option>
            <option value="natural">Natural</option>
            <option value="cinematic">Cinemático</option>
            <option value="dramatic">Dramático</option>
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-muted/20 p-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-foreground">
              Preset global de voz
            </div>

            {hasGlobalVoicePreset ? (
              <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                {isGlobalVoiceSourceScene ? (
                  <>
                    Esta escena es la referencia global de voz.
                  </>
                ) : (
                  <>
                    Se usará la voz guardada desde la escena{' '}
                    {globalVoiceSettings?.sourceSceneOrder ||
                      globalVoiceSettings?.sourceSceneId ||
                      'seleccionada'}
                    .
                  </>
                )}
              </div>
            ) : (
              <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                Cuando una voz te guste, puedes guardarla como referencia para el resto de las escenas.
              </div>
            )}

            {hasGlobalVoicePreset ? (
              <div className="mt-2 text-[10px] text-muted-foreground">
                Voz: {globalVoiceSettings?.resolvedVoice || globalVoiceSettings?.voice || 'auto'} ·
                Ritmo: {globalVoiceSettings?.voicePace || 'auto'} ·
                Intensidad: {globalVoiceSettings?.voiceIntensity || 'auto'} ·
                Énfasis: {globalVoiceSettings?.voiceEmphasis || 'auto'}
              </div>
            ) : null}

            {!canSaveVoiceAsGlobalPreset ? (
              <div className="mt-2 text-[10px] text-muted-foreground">
                Genera primero una voz para poder guardarla como preset global.
              </div>
            ) : null}
          </div>

          <button
            type="button"
            disabled={!canSaveVoiceAsGlobalPreset}
            onClick={() => {
              const next = !hasGlobalVoicePreset || !isGlobalVoiceSourceScene
              onToggleGlobalVoicePreset(next)
            }}
            className={[
              'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-40',
              voicePresetSwitchOn
                ? 'border-foreground bg-foreground'
                : 'border-border bg-background',
            ].join(' ')}
            aria-pressed={voicePresetSwitchOn}
          >
            <span
              className={[
                'inline-block h-5 w-5 transform rounded-full bg-background shadow transition',
                voicePresetSwitchOn ? 'translate-x-5' : 'translate-x-0.5',
              ].join(' ')}
            />
          </button>
        </div>

        {voicePresetSwitchOn ? (
          <div className="mt-3 rounded-xl bg-background/70 p-2 text-[11px] text-muted-foreground">
            Esta escena está guardada como preset global de voz.
          </div>
        ) : null}
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        <select
          value={voiceChoice}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, voiceChoice: e.target.value } : prev))
          }
          className="h-10 rounded-2xl border border-border/70 bg-background px-3 text-xs text-foreground outline-none"
        >
          {GEMINI_TTS_VOICE_OPTIONS.map((voice) => (
            <option key={voice.id} value={voice.id}>
              {voice.label}
            </option>
          ))}
        </select>

        <select
          value={draft.languageCode || resolvedLanguageCode || ''}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, languageCode: e.target.value } : prev))
          }
          className="h-10 rounded-2xl border border-border/70 bg-background px-3 text-xs text-foreground outline-none"
        >
          <option value="">Auto detectar</option>
          <option value="Spanish (Mexico)">Español México</option>
          <option value="Spanish (Latin America)">Español Latinoamérica</option>
          <option value="Spanish (Spain)">Español España</option>
          <option value="English (US)">English US</option>
          <option value="English (UK)">English UK</option>
        </select>
      </div>

      <label className="flex items-start gap-2 rounded-2xl border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={draft.enhanceVoiceWithAI !== false}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, enhanceVoiceWithAI: e.target.checked } : prev
            )
          }
          className="mt-0.5"
        />
        <span>Samantha puede agregar pausas e intención natural sin cambiar el mensaje.</span>
      </label>

      <button
        type="button"
        onClick={() =>
          setDraft((prev: any) => ({
            ...prev,
            lockVoiceSettings: !prev.lockVoiceSettings,
          }))
        }
        className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
      >
        {draft.lockVoiceSettings
          ? 'No guardar como preset global'
          : 'Usar esta voz para todo'}
      </button>

      {draft.lockVoiceSettings ? (
        <p className="text-xs text-muted-foreground">
          Al generar esta voz, se guardará como preset global para las siguientes escenas.
        </p>
      ) : null}

      {voiceContext?.voiceStyle ? (
        <div className="rounded-2xl bg-muted/40 px-3 py-2 text-[11px] leading-5 text-muted-foreground">
          Estilo detectado: <span className="text-foreground">{voiceContext.voiceStyle}</span>
        </div>
      ) : null}
    </div>
  )
}

function RenderQualityPanel({
  draft,
  setDraft,
}: {
  draft: SceneDetailDraft
  setDraft: React.Dispatch<React.SetStateAction<SceneDetailDraft | null>>
}) {
  return (
    <MinimalSegmentedControl
      value={(draft.avatarQuality || 'standard') as any}
      options={AVATAR_QUALITY_OPTIONS as any}
      onChange={(value) =>
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                avatarQuality: value as any,
              }
            : prev
        )
      }
    />
  )
}

function SceneProductionChecklist({
  scene,
  draft,
  setDraft,
  state,
  voiceContext,
  resolvedLanguageCode,
  generatingVoice,
  animating,
  regeneratingMotion,
  sceneVoiceUrl,

  voiceHistoryOpen,
  setVoiceHistoryOpen,
  voiceTakes,
  loadingVoiceTakes,
  selectingVoiceAssetId,
  loadSceneVoiceHistory,
  onSelectSceneVoice,

  globalVoiceSettings,
  hasGlobalVoicePreset,
  onToggleGlobalVoicePreset,

  onGenerateVoice,
  onPrepareHyperframes,
  onGenerateHyperframes,
  onAnimate,
}: {
  scene: any
  draft: SceneDetailDraft
  setDraft: React.Dispatch<React.SetStateAction<SceneDetailDraft | null>>
  state: SceneProductionState
  voiceContext: any
  resolvedLanguageCode: string
  generatingVoice: boolean
  animating: boolean
  regeneratingMotion: boolean
  sceneVoiceUrl?: string | null
  voiceHistoryOpen: boolean
  setVoiceHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>
  voiceTakes: any[]
  loadingVoiceTakes: boolean
  selectingVoiceAssetId: number | null
  loadSceneVoiceHistory: (sceneId?: number | string | null) => Promise<void>
  onSelectSceneVoice: (assetId: number) => Promise<void>
  onGenerateVoice: () => void
  globalVoiceSettings?: any
  hasGlobalVoicePreset?: boolean
  onToggleGlobalVoicePreset: (enabled: boolean) => Promise<void>
  onPrepareHyperframes: () => void
  onGenerateHyperframes: () => void
  onAnimate: (avatarQuality?: AvatarQuality) => void
}) {
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [videoOpen, setVideoOpen] = useState(false)

  const audioUrl = getSceneVoiceUrl(scene, sceneVoiceUrl)

  const graphicsActionLabel = state.graphicsPrepared
    ? 'Generar gráficos'
    : 'Preparar gráficos'

  const handleGraphics = () => {
    if (state.graphicsPrepared) onGenerateHyperframes()
    else onPrepareHyperframes()
  }

  const currentSceneOrder = Number(scene?.sceneOrder || scene?.scene_order || 0)
  const presetSourceSceneOrder = Number(
    globalVoiceSettings?.sourceSceneOrder || 0
  )

  const isGlobalVoiceSourceScene =
    hasGlobalVoicePreset &&
    presetSourceSceneOrder > 0 &&
    Number(presetSourceSceneOrder) === Number(currentSceneOrder)

  const canSaveVoiceAsGlobalPreset = Boolean(
    state.voiceReady || sceneVoiceUrl || scene?.voiceUrl || scene?.metadata?.voice?.audio_url
  )

  const graphicsProcessing = animating || regeneratingMotion

  const showImageCard = state.imageNeeded
  const showVoiceCard = state.voiceNeeded
  const showGraphicsCard = Boolean(scene)
  const showVideoCard = state.videoNeeded

  return (
    <div className="space-y-3">
      <div className="mb-5 flex items-start gap-3">
        <AiPulseOrb active={generatingVoice || animating || state.videoProcessing || regeneratingMotion} />
        <div>
          <div className="text-lg font-semibold tracking-tight text-foreground">
            Samantha está construyendo esta escena
          </div>
          <div className="mt-1 text-sm leading-6 text-muted-foreground">
            Te mostraré solo el siguiente paso. Puedes ajustar detalles cuando haga falta.
          </div>
        </div>
      </div>

      {showImageCard ? (
        <ProductionStepRow
          title="Imagen"
          description={
            state.imageReady
              ? 'La base visual de la escena ya está preparada.'
              : 'Esta escena necesita un frame base generado.'
          }
          done={state.imageReady}
          active={state.nextStep === 'image'}
        />
      ) : null}

      {showVoiceCard ? (
        <ProductionStepRow
          title="Voz"
          description={
            state.voiceReady
              ? 'La escena ya tiene audio aprobado.'
              : 'Generemos una voz natural usando el estilo del personaje.'
          }
          done={state.voiceReady}
          active={state.nextStep === 'voice'}
          processing={generatingVoice}
        >
          {hasGlobalVoicePreset ? (
            <div className="mb-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300">
              {isGlobalVoiceSourceScene ? (
                <>
                  Esta escena es la referencia global de voz.
                </>
              ) : (
                <>
                  Se utilizará la voz guardada desde la escena{' '}
                  {globalVoiceSettings?.sourceSceneOrder ||
                    globalVoiceSettings?.sourceSceneId ||
                    'seleccionada'}
                  {' '}al generar esta voz.
                </>
              )}
              <div className="mt-1 text-[11px] opacity-80">
                Voz: {globalVoiceSettings?.resolvedVoice || globalVoiceSettings?.voice || 'auto'} ·
                Ritmo: {globalVoiceSettings?.voicePace || 'auto'} ·
                Intensidad: {globalVoiceSettings?.voiceIntensity || 'auto'} ·
                Énfasis: {globalVoiceSettings?.voiceEmphasis || 'auto'}
              </div>
            </div>
          ) : null}

          {state.voiceReady ? (
            <div className="space-y-3">
              {audioUrl ? <audio controls src={audioUrl} className="w-full" /> : null}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setVoiceOpen((v) => !v)}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                >
                  Ajustar voz
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setVoiceHistoryOpen((v) => {
                      const next = !v
                      if (!v) loadSceneVoiceHistory(scene?.sceneId)
                      return next
                    })
                  }}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                >
                  Versiones
                </button>
                <button
                  type="button"
                  onClick={onGenerateVoice}
                  disabled={generatingVoice || !draft.dialogueText.trim()}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
                >
                  Regenerar
                </button>
              </div>
              <AnimatePresence>
                {voiceOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <VoiceSetupPanel
                      draft={draft}
                      setDraft={setDraft}
                      voiceContext={voiceContext}
                      resolvedLanguageCode={resolvedLanguageCode}
                      globalVoiceSettings={globalVoiceSettings}
                      hasGlobalVoicePreset={hasGlobalVoicePreset}
                      isGlobalVoiceSourceScene={isGlobalVoiceSourceScene}
                      canSaveVoiceAsGlobalPreset={canSaveVoiceAsGlobalPreset}
                      onToggleGlobalVoicePreset={onToggleGlobalVoicePreset}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {voiceHistoryOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xs font-semibold text-foreground">
                            Versiones de voz
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            Escucha y selecciona cuál audio queda activo para esta escena.
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => loadSceneVoiceHistory(scene?.sceneId)}
                          disabled={loadingVoiceTakes}
                          className="rounded-full border border-border px-3 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
                        >
                          {loadingVoiceTakes ? 'Cargando…' : 'Actualizar'}
                        </button>
                      </div>

                      {loadingVoiceTakes ? (
                        <div className="flex items-center gap-2 rounded-xl bg-background/70 p-3 text-xs text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cargando versiones…
                        </div>
                      ) : voiceTakes.length ? (
                        <div className="space-y-2">
                          {voiceTakes.map((take, index) => (
                            <div
                              key={take.assetId}
                              className="rounded-xl border border-border bg-background p-3"
                            >
                              <div className="mb-2 flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-xs font-semibold text-foreground">
                                    {take.active ? 'Voz activa' : `Versión ${voiceTakes.length - index}`}
                                  </div>
                                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                                    {take.resolvedVoice || take.voice || 'Voz auto'} ·{' '}
                                    {take.voicePace || 'auto'} ·{' '}
                                    {take.voiceIntensity || 'auto'} ·{' '}
                                    {take.voiceEmphasis || 'auto'}
                                  </div>
                                  {take.dateAdded ? (
                                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                                      {new Date(take.dateAdded).toLocaleString()}
                                    </div>
                                  ) : null}
                                </div>

                                {take.active ? (
                                  <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                                    Activa
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => onSelectSceneVoice(Number(take.assetId))}
                                    disabled={selectingVoiceAssetId === Number(take.assetId)}
                                    className="rounded-full bg-foreground px-3 py-1.5 text-[11px] font-semibold text-background hover:opacity-90 disabled:opacity-40"
                                  >
                                    {selectingVoiceAssetId === Number(take.assetId)
                                      ? 'Seleccionando…'
                                      : 'Usar esta'}
                                  </button>
                                )}
                              </div>

                              {take.audioUrl ? (
                                <audio controls src={take.audioUrl} className="w-full" />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-xl bg-background/70 p-3 text-xs text-muted-foreground">
                          Todavía no hay versiones de voz generadas para esta escena.
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : state.nextStep === 'voice' ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={onGenerateVoice}
                disabled={generatingVoice || !draft.dialogueText.trim()}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-foreground px-5 text-sm font-semibold text-background shadow-sm transition hover:opacity-90 disabled:opacity-40"
              >
                {generatingVoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {generatingVoice ? 'Generando voz…' : 'Generar voz'}
              </button>

              <button
                type="button"
                onClick={() => setVoiceOpen((v) => !v)}
                className="ml-2 rounded-full px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                {voiceOpen ? 'Ocultar ajustes' : 'Configurar'}
              </button>

              <AnimatePresence>
                {voiceOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <VoiceSetupPanel
                      draft={draft}
                      setDraft={setDraft}
                      voiceContext={voiceContext}
                      resolvedLanguageCode={resolvedLanguageCode}
                      globalVoiceSettings={globalVoiceSettings}
                      hasGlobalVoicePreset={hasGlobalVoicePreset}
                      isGlobalVoiceSourceScene={isGlobalVoiceSourceScene}
                      canSaveVoiceAsGlobalPreset={canSaveVoiceAsGlobalPreset}
                      onToggleGlobalVoicePreset={onToggleGlobalVoicePreset}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : null}
        </ProductionStepRow>
      ) : null}

      {showGraphicsCard ? (
        <ProductionStepRow
          title="Gráficos"
          description={
            state.graphicsReady
              ? 'Los elementos animados ya están listos.'
              : 'Samantha puede preparar textos y motion graphics para reforzar el mensaje.'
          }
          done={state.graphicsReady}
          active={state.nextStep === 'graphics'}
          processing={graphicsProcessing && state.nextStep === 'graphics'}
        >
          {!state.graphicsReady ? (
            <button
              type="button"
              onClick={handleGraphics}
              disabled={graphicsProcessing}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-foreground px-5 text-sm font-semibold text-background shadow-sm transition hover:opacity-90 disabled:opacity-40"
            >
              {graphicsProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {graphicsProcessing ? 'Trabajando…' : graphicsActionLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={onPrepareHyperframes}
              disabled={graphicsProcessing}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
            >
              Repreparar
            </button>
          )}
        </ProductionStepRow>
      ) : null}

      {showVideoCard ? (
        <ProductionStepRow
          title="Video"
          description={
            state.videoReady
              ? 'La escena ya tiene video generado.'
              : state.videoProcessing
              ? 'FAL está procesando el talking avatar. Samantha actualizará la escena cuando termine.'
              : 'Convierte la imagen y la voz en un talking avatar.'
          }
          done={state.videoReady}
          active={state.nextStep === 'video' || state.videoProcessing}
          processing={state.videoProcessing || animating}
        >
          {state.videoReady ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setVideoOpen((v) => !v)}
                className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                Cambiar calidad
              </button>
              <button
                type="button"
                onClick={() => onAnimate(draft.avatarQuality || 'standard')}
                disabled={animating || state.videoProcessing}
                className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                Regenerar
              </button>

              <AnimatePresence>
                {videoOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="w-full overflow-hidden pt-2"
                  >
                    <RenderQualityPanel draft={draft} setDraft={setDraft} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : state.nextStep === 'video' ? (
            <div className="space-y-3">
              <RenderQualityPanel draft={draft} setDraft={setDraft} />
              <button
                type="button"
                onClick={() => onAnimate(draft.avatarQuality || 'standard')}
                disabled={animating || state.videoProcessing}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-foreground px-5 text-sm font-semibold text-background shadow-sm transition hover:opacity-90 disabled:opacity-40"
              >
                {animating || state.videoProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Video className="h-4 w-4" />
                )}
                {state.videoProcessing ? 'Procesando…' : animating ? 'Enviando…' : 'Generar video'}
              </button>
            </div>
          ) : state.videoProcessing ? (
            <div className="rounded-2xl bg-muted/40 px-3 py-2 text-xs leading-5 text-muted-foreground">
              Puedes salir del detalle. Cuando el webhook termine, esta escena se actualizará.
            </div>
          ) : null}
        </ProductionStepRow>
      ) : null}
    </div>
  )
}

function SceneDetailView({
  scene,
  draft,
  setDraft,
  onBack,
  onSave,
  onSaveAndRegenerate,
  onAnimate,
  onPrepareHyperframes,
  onGenerateHyperframes,
  onPrevScene,
  onNextScene,
  projectAspectRatio,
  canGoPrev,
  canGoNext,
  currentSceneIndex,
  totalScenes,
  saving,
  animating,
  canRegenerate,
  regenCredits,
  generatingVoice,
  voiceHistoryOpen,
  setVoiceHistoryOpen,
  voiceTakes,
  loadingVoiceTakes,
  selectingVoiceAssetId,
  loadSceneVoiceHistory,
  onSelectSceneVoice,
  onGenerateVoice,
  globalVoiceSettings,
  hasGlobalVoicePreset,
  onToggleGlobalVoicePreset,
  sceneVoiceUrl,
  regeneratingMotion,
  onRegenerateMotionWithDirection,
  onUploadSceneImage,
}: {
  scene: any
  draft: SceneDetailDraft
  setDraft: React.Dispatch<React.SetStateAction<SceneDetailDraft | null>>
  onBack: () => void
  onSave: () => void
  onSaveAndRegenerate: () => void
  onAnimate: (avatarQuality?: AvatarQuality) => void
  onPrepareHyperframes: () => void
  onGenerateHyperframes: () => void
  onPrevScene: () => void
  onNextScene: () => void
  projectAspectRatio?: string
  canGoPrev: boolean
  canGoNext: boolean
  currentSceneIndex: number
  totalScenes: number
  saving: boolean
  animating: boolean
  canRegenerate: boolean
  regenCredits: number
  generatingVoice: boolean
  voiceHistoryOpen: boolean
  setVoiceHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>
  voiceTakes: any[]
  loadingVoiceTakes: boolean
  selectingVoiceAssetId: number | null
  loadSceneVoiceHistory: (sceneId?: number | string | null) => Promise<void>
  onSelectSceneVoice: (assetId: number) => Promise<void>
  onGenerateVoice: () => void
  globalVoiceSettings?: any
  hasGlobalVoicePreset?: boolean
  onToggleGlobalVoicePreset: (enabled: boolean) => Promise<void>
  sceneVoiceUrl?: string | null
  regeneratingMotion: boolean
  onRegenerateMotionWithDirection: (direction: string) => void
  onUploadSceneImage: (file: File, sceneId: number) => Promise<void>
}) {
  const [showAdvanced, setShowAdvanced] = useState(false)

  const shots = getSceneShots(scene)
  const motionInfo = scene?.motionInfo || getSceneMotionInfo(scene)
  const canPrepareMotion = Boolean(scene?.canPrepareMotion || motionInfo?.hasMotion)
  const productionLabel = getSceneProductionLabel(scene)
  const sceneMetadata = scene?.metadata_json || scene?.metadata || {}

  const hyperframesPlan =
    scene?.hyperframesPlan ||
    sceneMetadata?.hyperframes_plan ||
    null

  const hyperframesReady =
    !!hyperframesPlan ||
    sceneMetadata?.hyperframes_status === 'ready' ||
    sceneMetadata?.hyperframes_status === 'generated'

  const hasGeneratedMotion =
    !!hyperframesPlan?.composition ||
    sceneMetadata?.hyperframes_status === 'generated'

  const projectVoiceContext = getSceneVoiceContext(scene)
  const voiceContext = projectVoiceContext?.projectJson ? projectVoiceContext : null
  const resolvedLanguageCode =
    draft.languageCode ||
    resolveDefaultLanguageCode(String(voiceContext?.language || ''))

  const state = getProductionState({
    scene,
    draft,
    sceneVoiceUrl,
    canPrepareMotion,
    hyperframesReady,
    hasGeneratedMotion,
  })

  const processing =
    generatingVoice ||
    animating ||
    regeneratingMotion ||
    state.videoProcessing

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <div className="shrink-0 border-b border-border/70 px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-background px-3 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Escenas
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrevScene}
              disabled={!canGoPrev}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:bg-muted disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="min-w-[76px] text-center text-xs font-medium text-muted-foreground">
              {currentSceneIndex + 1} / {totalScenes}
            </div>

            <button
              type="button"
              onClick={onNextScene}
              disabled={!canGoNext}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:bg-muted disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto p-5 lg:grid-cols-[minmax(320px,0.92fr)_minmax(360px,0.75fr)]">
        <ScenePreviewPanel
          scene={scene}
          state={state}
          aspectRatio={projectAspectRatio}
          productionLabel={productionLabel}
          shotsCount={shots.length}
          hyperframesPlan={hyperframesPlan}
          hyperframesReady={hyperframesReady}
          canPrepareMotion={canPrepareMotion}
          animating={animating}
          regeneratingMotion={regeneratingMotion}
          onGenerateHyperframes={onGenerateHyperframes}
          onRegenerateMotionWithDirection={onRegenerateMotionWithDirection}
          onUploadImage={onUploadSceneImage}
        />

        <div className="min-h-0">
          <SceneProductionChecklist
            scene={scene}
            draft={draft}
            setDraft={setDraft}
            state={state}
            voiceContext={voiceContext}
            resolvedLanguageCode={resolvedLanguageCode}
            generatingVoice={generatingVoice}
            animating={animating}
            regeneratingMotion={regeneratingMotion}
            sceneVoiceUrl={sceneVoiceUrl}
            voiceHistoryOpen={voiceHistoryOpen}
            setVoiceHistoryOpen={setVoiceHistoryOpen}
            voiceTakes={voiceTakes}
            loadingVoiceTakes={loadingVoiceTakes}
            selectingVoiceAssetId={selectingVoiceAssetId}
            loadSceneVoiceHistory={loadSceneVoiceHistory}
            onSelectSceneVoice={onSelectSceneVoice}
            onGenerateVoice={onGenerateVoice}
            globalVoiceSettings={globalVoiceSettings}
            onToggleGlobalVoicePreset={onToggleGlobalVoicePreset}
            hasGlobalVoicePreset={hasGlobalVoicePreset}
            onPrepareHyperframes={onPrepareHyperframes}
            onGenerateHyperframes={onGenerateHyperframes}
            onAnimate={onAnimate}
          />

          <div className="mt-4 rounded-3xl border border-border/70 bg-background/60 p-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex w-full items-center justify-between text-left"
            >
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Detalles avanzados
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Prompts, texto en pantalla y regeneración manual.
                </div>
              </div>
              <div className="text-xs font-medium text-muted-foreground">
                {showAdvanced ? 'Ocultar' : 'Ver'}
              </div>
            </button>

            <AnimatePresence initial={false}>
              {showAdvanced && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-5 space-y-4">
                    <FieldBlock label="Texto de voz">
                      <textarea
                        value={draft.dialogueText}
                        onChange={(e) =>
                          setDraft((prev) =>
                            prev ? { ...prev, dialogueText: e.target.value } : prev
                          )
                        }
                        rows={4}
                        className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-primary/40"
                      />
                    </FieldBlock>

                    <FieldBlock label="Texto en pantalla">
                      <input
                        value={draft.overlayText}
                        onChange={(e) =>
                          setDraft((prev) =>
                            prev ? { ...prev, overlayText: e.target.value } : prev
                          )
                        }
                        className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-primary/40"
                      />
                    </FieldBlock>

                    <FieldBlock label="Prompt visual">
                      <textarea
                        value={draft.prompt}
                        onChange={(e) =>
                          setDraft((prev) => (prev ? { ...prev, prompt: e.target.value } : prev))
                        }
                        rows={5}
                        className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-primary/40"
                      />
                    </FieldBlock>

                    <FieldBlock label="Negative prompt">
                      <textarea
                        value={draft.negativePrompt}
                        onChange={(e) =>
                          setDraft((prev) =>
                            prev ? { ...prev, negativePrompt: e.target.value } : prev
                          )
                        }
                        rows={3}
                        className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none focus:border-primary/40"
                      />
                    </FieldBlock>

                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      <button
                        type="button"
                        onClick={onSave}
                        disabled={saving || processing}
                        className="rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
                      >
                        {saving ? 'Guardando…' : 'Guardar'}
                      </button>

                      <button
                        type="button"
                        onClick={onSaveAndRegenerate}
                        disabled={saving || processing || !canRegenerate}
                        className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        Regenerar frame
                      </button>

                      {state.graphicsNeeded ? (
                        <button
                          type="button"
                          onClick={() =>
                            onRegenerateMotionWithDirection(
                              String(
                                scene?.metadata_json?.motion_direction ||
                                  scene?.metadata?.motion_direction ||
                                  scene?.metadata_json?.motion_for_static_image ||
                                  scene?.metadata?.motion_for_static_image ||
                                  'Ajusta la animación respetando la dirección visual, ritmo, voz y continuidad de esta escena.'
                              )
                            )
                          }
                          disabled={processing}
                          className="rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-40"
                        >
                          Ajustar motion
                        </button>
                      ) : null}
                    </div>

                    {canRegenerate ? (
                      <div className="text-[11px] text-muted-foreground">
                        Regenerar frame usará aproximadamente {regenCredits} créditos.
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}

function FieldBlock({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  )
}

function SectionTitle({
  title,
  subtitle,
}: {
  title: string
  subtitle: string
}) {
  return (
    <div>
      <div className="text-2xl font-semibold">{title}</div>
      <div className="mt-2 text-sm text-muted-foreground">{subtitle}</div>
    </div>
  )
}

function CardBlock({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-3xl border border-border/70 bg-primary/[0.04] p-6">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div className="mt-4">{children}</div>
    </div>
  )
}

function OptionsRow({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: string; label: string }>
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map((option) => {
        const active = value === option.id

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={
              active
                ? 'rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground'
                : 'rounded-2xl border border-primary/20 bg-primary/10 px-4 py-2 text-sm text-primary'
            }
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border/70 px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 text-sm text-foreground">
        {value || '—'}
      </div>
    </div>
  )
}

function FlowStepCard({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
        {eyebrow}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-foreground">
        {title}
      </div>
      <div className="mt-3 max-w-2xl text-sm leading-relaxed text-cyan-100/58">
        {subtitle}
      </div>

      <div className="mt-6">{children}</div>
    </div>
  )
}

function OptionsGrid({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: string; label: string; hint?: string }>
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {options.map((option) => {
        const active = value === option.id

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={
              active
                ? 'rounded-[26px] border border-cyan-300/35 bg-cyan-300/14 p-5 text-left transition'
                : 'rounded-[26px] border border-border/70 bg-primary/[0.04] p-5 text-left transition hover:border-cyan-300/25 hover:bg-primary/[0.07]'
            }
          >
            <div className="text-lg font-semibold text-foreground">{option.label}</div>
            {option.hint ? (
              <div className="mt-2 text-sm leading-relaxed text-cyan-100/56">
                {option.hint}
              </div>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function ProductInputStep({
  onContinue,
  onBack,
  request,
  generationId,
}: {
  onContinue: (fileUrl: string) => void
  onBack: () => void
  request: TaskFlowRequest
  generationId: number
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return

    setFile(f)
    setPreview(URL.createObjectURL(f))
  }

  async function handleUpload() {
    if (!file) return

    setUploading(true)

    try {
        const sellerId = request?.payload?.sellerId
        const userId = request?.payload?.userId

        const formData = new FormData()
        formData.append('generationId', String(generationId))
        formData.append('customerId', String(userId))
        formData.append('sellerId', String(sellerId))
        formData.append('kind', 'product')
        formData.append('files', file)

        const res = await fetch('/api/creator-studio/video/project/assets/upload', {
        method: 'POST',
        body: formData,
        })

        const json = await res.json()

        if (!res.ok || !json?.ok) {
        throw new Error('Upload failed')
        }

        // ✅ SOLO PASAS URL
        onContinue(json.url)

    } catch (err) {
        console.error(err)
        alert('Error subiendo imagen')
    } finally {
        setUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Sube tu producto"
        subtitle="Samantha usará esta imagen como referencia visual real para todas las escenas."
      />

      {uploading && (
        <div className="mt-3 text-sm text-cyan-300">
            Subiendo imagen...
        </div>
      )}

      <div className="rounded-3xl border border-border/70 bg-primary/[0.04] p-6">
        <input type="file" accept="image/*" onChange={handleFile} />

        {preview && (
          <div className="mt-4">
            <img
              src={preview}
              className="h-40 w-auto rounded-xl object-cover"
            />
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="rounded-2xl border border-primary/20 px-4 py-2"
        >
          Atrás
        </button>

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="rounded-2xl bg-primary px-5 py-3 text-slate-900 disabled:opacity-40"
        >
          {uploading ? 'Subiendo…' : 'Continuar con Samantha'}
        </button>
      </div>
    </div>
  )
}

function CharacterCreationStep({
  plan,
  request,
  generationId,
  onDone,
}: {
  plan: any
  request: TaskFlowRequest
  generationId: number
  onDone: (imageUrl: string) => void | Promise<void>
}) {
  const [mode, setMode] = useState<'existing' | 'clone' | 'ai'>('existing')

  const [aiAvatarGender, setAiAvatarGender] =
    useState<'male' | 'female' | 'neutral'>('male')

  const [aiAvatarEthnicity, setAiAvatarEthnicity] =
    useState<'latino' | 'caucasian' | 'black' | 'asian' | 'free'>('latino')

  const [aiAvatarAgeRange, setAiAvatarAgeRange] =
    useState<'25-35' | '35-45' | '45-55'>('35-45')

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [image, setImage] = useState<string | null>(null)

  const [generatedAvatarIdentity, setGeneratedAvatarIdentity] = useState<any | null>(null)
  const [generatedAvatarPrompt, setGeneratedAvatarPrompt] = useState<string>('')

  const [portraitsLoading, setPortraitsLoading] = useState(false)
  const [portraits, setPortraits] = useState<any[]>([])
  const [selectedPortraitId, setSelectedPortraitId] = useState<number | null>(null)

  const cloneInputRef = useRef<HTMLInputElement | null>(null)

  const [cloneSourceImage, setCloneSourceImage] = useState<string | null>(null)
  const [cloneResult, setCloneResult] = useState<any | null>(null)
  const [cloneGenerating, setCloneGenerating] = useState(false)

  const [cloneStyle, setCloneStyle] =
    useState<'professional' | 'corporate' | 'premium'>('professional')

  const [clonePosePreset, setClonePosePreset] =
    useState<'neutral_standing' | 'arms_crossed' | 'relaxed_confident'>('neutral_standing')

  const [cloneClothingPreset, setCloneClothingPreset] =
    useState<'smart_casual' | 'business' | 'premium_black' | 'creator_modern'>('smart_casual')

  const [cloneUploading, setCloneUploading] = useState(false)
  const [cloneSourceImageUrl, setCloneSourceImageUrl] = useState<string | null>(null)
  const [cloneResultImageUrl, setCloneResultImageUrl] = useState<string | null>(null)
  

  const subject = plan?.subject_bible?.primary_subject || {}
  const creativeDirection = request.payload?.creativeDirection || {}
  const characterType =
    creativeDirection?.characterType ||
    plan?.subject_bible?.reason ||
    'expert'

  useEffect(() => {
    void loadExistingAvatars()
  }, [])

  async function loadExistingAvatars() {
    setPortraitsLoading(true)

    try {
      const res = await fetch('/api/creator-studio/portrait/list?limit=12', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudieron cargar los avatares')
      }

      const items = Array.isArray(json.items) ? json.items : []
      setPortraits(items)

      if (items.length === 0) {
        setMode('clone')
      }
    } catch (err) {
      console.error('[CharacterCreationStep] loadExistingAvatars error', err)
      setPortraits([])
    } finally {
      setPortraitsLoading(false)
    }
  }

  async function saveAvatarAsCharacter(params: {
    imageUrl: string
    mode: 'existing_avatar' | 'digital_clone' | 'ai_generated'
    portraitId?: number | null
    promptBase?: string | null
    metadata?: any
  }) {
    const res = await fetch('/api/creator-studio/character/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        generationId,
        sellerId: request.payload?.sellerId,
        customerId: request.payload?.userId,
        imageUrl: params.imageUrl,
        subject: plan.subject_bible,
        saveAsPortrait: params.mode === 'ai_generated',
        portrait: {
          promptBase:
            params.promptBase ||
            'Avatar generado con IA desde el brief del proyecto',
          metadata: {
            avatarMode: params.mode,
            characterType,
            projectId: request.payload?.projectId || null,
            generationId,
            source: 'syn_create_content_flow',
            ...(params.metadata || {}),
          },
        },
        metadata: {
          mode: params.mode,
          source:
            params.mode === 'existing_avatar'
              ? 'portrait'
              : params.mode === 'digital_clone'
                ? 'portrait_express'
                : 'ai_avatar',
          portraitId: params.portraitId || null,
          promptBase: params.promptBase || '',
          characterType,
          ...(params.metadata || {}),
        },
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || 'No se pudo guardar el avatar')
    }

    return json
  }

  async function continueWithAvatar(params: {
    imageUrl: string
    mode: 'existing_avatar' | 'digital_clone' | 'ai_generated'
    portraitId?: number | null
    promptBase?: string | null
    metadata?: any
  }) {
    setSaving(true)

    try {
      await saveAvatarAsCharacter(params)
      await onDone(params.imageUrl)
    } catch (err) {
      console.error(err)
      alert('Error preparando el avatar')
    } finally {
      setSaving(false)
    }
  }

  async function useExistingAvatar(item: any) {
    if (!item?.image_url) return

    setSelectedPortraitId(Number(item.portrait_id))

    await continueWithAvatar({
      mode: 'existing_avatar',
      imageUrl: String(item.image_url),
      portraitId: Number(item.portrait_id),
      promptBase: item.prompt_base || '',
      metadata: {
        portraitMetadata: item.metadata_json || null,
      },
    })
  }

  function handleCloneFileUpload(file: File) {
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten imágenes')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Máximo 10MB')
      return
    }

    const reader = new FileReader()

    reader.onload = () => {
      setCloneSourceImage(reader.result as string)
      setCloneResult(null)
    }

    reader.readAsDataURL(file)
  }

  async function generateDigitalClone() {
    if (!cloneSourceImage) {
      alert('Primero sube una foto para crear tu clon digital.')
      return
    }

    setCloneGenerating(true)

    try {
      const res = await fetch('/api/creator-studio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tab: 'portrait',
          inputs: {
            imageUrl: cloneSourceImage,
            style: cloneStyle,
            posePreset: clonePosePreset,
            clothingPreset: cloneClothingPreset,
            makePrimary: 0,
            source: 'task_flow_avatar_clone',
            userId: request.payload?.userId,
            sellerId: request.payload?.sellerId,
            projectId: request.payload?.projectId || null,
            generationId,
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo crear el clon digital')
      }

      const portrait = json?.generation?.portrait || null
      const resultJson = json?.generation?.result_json || null

      const imageUrl =
        portrait?.image_url ||
        resultJson?.image_url ||
        null

      if (!imageUrl) {
        throw new Error('No se recibió imagen del clon digital')
      }

      setCloneResult({
        portrait_id: Number(portrait?.portrait_id || 0) || null,
        image_url: imageUrl,
        source_image_url: portrait?.source_image_url || cloneSourceImage,
        prompt_base: resultJson?.prompt_base || '',
        metadata_json: resultJson || null,
      })

      await loadExistingAvatars()
    } catch (err) {
      console.error(err)
      alert('Error creando el clon digital')
    } finally {
      setCloneGenerating(false)
    }
  }

  async function useDigitalClone() {
    if (!cloneResult?.image_url) return

    await continueWithAvatar({
      mode: 'digital_clone',
      imageUrl: String(cloneResult.image_url),
      portraitId: cloneResult.portrait_id || null,
      promptBase: cloneResult.prompt_base || `Clon digital estilo ${cloneStyle}`,
      metadata: {
        cloneStyle,
        posePreset: clonePosePreset,
        clothingPreset: cloneClothingPreset,
        sourceImageUrl: cloneResult.source_image_url || null,
        portraitMetadata: cloneResult.metadata_json || null,
      },
    })
  }

  async function generateCharacter() {
    setLoading(true)
    setGeneratedAvatarIdentity(null)
    setGeneratedAvatarPrompt('')

    try {
      const res = await fetch('/api/ai/avatar/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan,
          customerId: request.payload?.userId,
          sellerId: request.payload?.sellerId,
          projectId: request.payload?.projectId || null,
          generationId,
          creativeDirection:
            request.payload?.creativeDirection ||
            request.payload?.creatorStudioProject?.creative_direction_json ||
            null,
          avatarIdentity: {
            gender: aiAvatarGender,
            ethnicity: aiAvatarEthnicity,
            ageRange: aiAvatarAgeRange,
            persona: characterType || 'expert_creator',
            strictIdentity: true,
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.imageUrl) {
        throw new Error(json?.error || 'No se pudo generar el avatar')
      }

      setImage(json.imageUrl)
      setGeneratedAvatarIdentity(json.avatarIdentity || null)
      setGeneratedAvatarPrompt(String(json.prompt || ''))
    } catch (err) {
      console.error(err)
      alert('Error generando avatar')
    } finally {
      setLoading(false)
    }
  }

  async function saveCharacter() {
    if (!image) return

    await continueWithAvatar({
      mode: 'ai_generated',
      imageUrl: image,
      promptBase:
        generatedAvatarPrompt ||
        'Avatar generado con IA desde el brief del proyecto',
      metadata: {
        avatarIdentity: generatedAvatarIdentity,
        generatedAvatarPrompt,
        planBrief: plan?.brief || null,
        subjectBible: plan?.subject_bible || null,
      },
    })
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-2 rounded-2xl border border-border bg-muted/30 p-1 md:grid-cols-3">
        <button
          type="button"
          onClick={() => setMode('existing')}
          className={[
            'rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
            mode === 'existing'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          Avatares existentes
        </button>

        <button
          type="button"
          onClick={() => setMode('clone')}
          className={[
            'rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
            mode === 'clone'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          Clon Digital
        </button>

        <button
          type="button"
          onClick={() => setMode('ai')}
          className={[
            'rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
            mode === 'ai'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          Generar con IA
        </button>
      </div>

      {mode === 'existing' && (
        <div className="space-y-4">
          {portraitsLoading ? (
            <div className="rounded-[28px] border border-border/70 bg-card/70 p-6 text-sm text-muted-foreground">
              Cargando avatares...
            </div>
          ) : portraits.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-border bg-card/50 p-6">
              <h4 className="font-semibold text-foreground">
                Aún no tienes avatares creados
              </h4>

              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Puedes crear tu clon digital con una foto propia o generar un avatar
                nuevo con IA desde el brief del proyecto.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setMode('clone')}
                  className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                >
                  Crear Clon Digital
                </button>

                <button
                  type="button"
                  onClick={() => setMode('ai')}
                  className="rounded-2xl border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground"
                >
                  Generar con IA
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {portraits.map((item) => {
                const active = selectedPortraitId === Number(item.portrait_id)

                return (
                  <button
                    key={item.portrait_id}
                    type="button"
                    onClick={() => void useExistingAvatar(item)}
                    disabled={saving}
                    className={[
                      'overflow-hidden rounded-[28px] border bg-card/80 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60',
                      active
                        ? 'border-primary ring-1 ring-primary/30'
                        : 'border-border/70',
                    ].join(' ')}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-muted">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={`Avatar ${item.portrait_id}`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                          Sin imagen
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-foreground">
                          Avatar #{item.portrait_id}
                        </p>

                        {Number(item.is_primary || 0) === 1 ? (
                          <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                            Principal
                          </span>
                        ) : null}
                      </div>

                      <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">
                        {item.prompt_base || 'Avatar personal listo para usar'}
                      </p>

                      <span className="inline-flex rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-500">
                        {saving && active ? 'Preparando...' : 'Usar este avatar'}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {mode === 'clone' && (
        <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
          <div className="rounded-[28px] border border-border/70 bg-card/80 p-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">
              Clon Digital Express
            </div>

            <h4 className="mt-3 text-xl font-semibold text-foreground">
              Crea un avatar basado en ti
            </h4>

            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Sube una foto clara de tu rostro. Crearemos un avatar profesional para
              usarlo como talento principal en este video.
            </p>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => cloneInputRef.current?.click()}
                disabled={cloneGenerating || saving}
                className="w-full rounded-2xl border border-dashed border-border bg-background/60 px-5 py-6 text-center text-sm text-muted-foreground transition hover:border-primary/40 hover:text-foreground disabled:opacity-50"
              >
                {cloneSourceImage ? 'Cambiar foto' : 'Subir foto para Clon Digital'}
              </button>

              <input
                ref={cloneInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleCloneFileUpload(file)
                  e.target.value = ''
                }}
                className="hidden"
              />
            </div>

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Estilo
              </p>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'professional', label: 'Pro' },
                  { id: 'corporate', label: 'Corporate' },
                  { id: 'premium', label: 'Premium' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCloneStyle(item.id as any)}
                    className={[
                      'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                      cloneStyle === item.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    ].join(' ')}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Pose
              </p>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'neutral_standing', label: 'Neutro' },
                  { id: 'arms_crossed', label: 'Autoridad' },
                  { id: 'relaxed_confident', label: 'Relajado' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setClonePosePreset(item.id as any)}
                    className={[
                      'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                      clonePosePreset === item.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    ].join(' ')}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Vestuario
              </p>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'smart_casual', label: 'Smart casual' },
                  { id: 'business', label: 'Business' },
                  { id: 'premium_black', label: 'Premium black' },
                  { id: 'creator_modern', label: 'Creator' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCloneClothingPreset(item.id as any)}
                    className={[
                      'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                      cloneClothingPreset === item.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    ].join(' ')}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void generateDigitalClone()}
                disabled={!cloneSourceImage || cloneGenerating || saving}
                className="rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40"
              >
                {cloneGenerating ? 'Creando clon...' : 'Crear Clon Digital'}
              </button>

              <button
                type="button"
                onClick={() => void useDigitalClone()}
                disabled={!cloneResult?.image_url || cloneGenerating || saving}
                className="rounded-2xl border border-primary/20 bg-primary/10 px-5 py-3 text-sm font-semibold text-primary disabled:opacity-40"
              >
                {saving ? 'Preparando...' : 'Usar Clon'}
              </button>
            </div>
          </div>

          <div className="relative min-h-[520px] overflow-hidden rounded-[32px] border border-border/70 bg-card">
            {cloneGenerating && (
              <div className="absolute inset-0 animate-pulse bg-primary/10" />
            )}

            {!cloneSourceImage && !cloneResult?.image_url && !cloneGenerating && (
              <div className="flex h-full items-center justify-center p-8 text-center text-sm leading-6 text-muted-foreground">
                Aquí aparecerá tu foto y el Clon Digital generado.
              </div>
            )}

            {cloneResult?.image_url ? (
              <img
                src={cloneResult.image_url}
                alt="Clon Digital"
                className="h-full w-full object-cover"
              />
            ) : cloneSourceImage ? (
              <img
                src={cloneSourceImage}
                alt="Foto fuente"
                className="h-full w-full object-contain p-6"
              />
            ) : null}
          </div>
        </div>
      )}

      {mode === 'ai' && (
        <div className="grid gap-8 md:grid-cols-[360px_1fr]">
          <div className="rounded-[28px] border border-border/70 bg-card/80 p-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-primary">
              Generar con IA
            </div>

            <div className="mt-3 text-2xl font-semibold text-foreground">
              Generemos un avatar para este video
            </div>

            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Crearemos un avatar nuevo usando el brief, audiencia, objetivo y estilo
              visual del proyecto.
            </p>

            <div className="mt-5 space-y-4 rounded-2xl border border-border bg-background/60 p-4">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Género
                </p>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'male', label: 'Hombre' },
                    { id: 'female', label: 'Mujer' },
                    { id: 'neutral', label: 'Neutral' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setAiAvatarGender(item.id as any)}
                      className={[
                        'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                        aiAvatarGender === item.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      ].join(' ')}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Apariencia base
                </p>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'latino', label: 'Latino / Mexicano' },
                    { id: 'caucasian', label: 'Caucásico' },
                    { id: 'black', label: 'Afrodescendiente' },
                    { id: 'asian', label: 'Asiático' },
                    { id: 'free', label: 'Libre' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setAiAvatarEthnicity(item.id as any)}
                      className={[
                        'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                        aiAvatarEthnicity === item.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      ].join(' ')}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Edad visual
                </p>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: '25-35', label: '25-35' },
                    { id: '35-45', label: '35-45' },
                    { id: '45-55', label: '45-55' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setAiAvatarAgeRange(item.id as any)}
                      className={[
                        'rounded-2xl border px-3 py-2 text-sm font-medium transition-colors',
                        aiAvatarAgeRange === item.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      ].join(' ')}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              <Info label="Brief" value={subject?.appearance || '—'} />
              <Info label="Estilo" value={subject?.casting_style || '—'} />
              <Info label="Vestuario" value={subject?.wardrobe || '—'} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={generateCharacter}
                disabled={loading || saving}
                className="rounded-2xl w-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40"
              >
                {loading ? 'Generando…' : image ? 'Regenerar avatar' : 'Generar avatar'}
              </button>

              <button
                type="button"
                onClick={saveCharacter}
                disabled={!image || loading || saving}
                className="rounded-2xl w-full border border-primary/20 bg-primary/10 px-5 py-3 text-sm font-semibold text-primary disabled:opacity-40"
              >
                {saving ? 'Guardando…' : 'Confirmar avatar'}
              </button>
            </div>
          </div>

          <div className="relative min-h-[520px] overflow-hidden rounded-[32px] border border-border/70 bg-card">
            {loading && (
              <div className="absolute inset-0 animate-pulse bg-primary/10" />
            )}

            {!image && !loading && (
              <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
                Aquí aparecerá tu avatar generado con IA
              </div>
            )}

            {image && (
              <img
                src={image}
                alt="Avatar preview"
                className="h-full w-full object-cover"
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
